/**
 * The message catalogue (IMPLEMENTATION_PLAN.md §2.9, decision 9): every UI string lives
 * here in both languages. English is the reference; a test checks Finnish has every key.
 * Placeholders are `{name}`.
 */
export const en = {
  "products.ai.organizationChanged":
    "The active organization changed. Reload this page and try again. Nothing was saved.",
  "app.name": "Scalephant",
  "app.tagline": "Marketing autopilot for self-operating companies.",
  "nav.home": "Home",
  "nav.dashboard": "Dashboard",
  "nav.campaigns": "Campaigns",
  "nav.adAccounts": "Ad accounts",
  "nav.organizations": "Organizations",
  "nav.settings": "Settings",
  "nav.signIn": "Sign in",
  "nav.register": "Create account",
  "nav.signOut": "Sign out",
  "nav.language": "Language",
  "common.email": "E-mail",
  "common.password": "Password",
  "common.name": "Name",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.continue": "Continue",
  "common.loading": "Loading…",
  "common.saved": "Saved.",
  "common.error.generic": "Something went wrong. Please try again.",
  "common.error.network": "Could not reach the server.",
  "common.error.unavailable":
    "This is not switched on for this server yet, so there is nothing to retry. Tell us and we will turn it on.",
  "common.error.rateLimited": "Too many attempts. Try again in {seconds} seconds.",
  "validation.required": "Required.",
  "validation.email_invalid": "Enter a valid e-mail address.",
  "validation.password_too_short": "Use at least {min} characters.",
  "validation.password_too_long": "Use at most {max} characters.",
  "validation.passwords_differ": "The passwords do not match.",
  "validation.code_invalid": "Enter the 6-digit code.",
  "auth.login.title": "Sign in",
  "auth.login.rememberMe": "Keep me signed in",
  "auth.login.submit": "Sign in",
  "auth.login.forgot": "Forgot your password?",
  "auth.login.noAccount": "No account yet?",
  "auth.login.google": "Continue with Google",
  "auth.login.error.invalid": "Wrong e-mail or password.",
  "auth.login.error.unverified": "Confirm your e-mail address first. We sent a new link.",
  "auth.register.title": "Create your account",
  "auth.register.submit": "Create account",
  "auth.register.haveAccount": "Already have an account?",
  "auth.register.locale": "Language",
  "auth.register.company": "Company",
  "auth.register.terms.before": "By creating an account you accept the ",
  "auth.register.terms.terms": "terms of service",
  "auth.register.terms.between": " and the ",
  "auth.register.terms.privacy": "privacy policy",
  "auth.register.terms.after": ".",
  "auth.register.error.exists": "That e-mail address is already registered.",
  "auth.register.error.existsLink": "Send a new confirmation link",
  "auth.verify.title": "Check your inbox",
  "auth.verify.body":
    "We sent a confirmation link to {email}. Open it to finish creating your account.",
  "auth.verify.bodyUnknown":
    "Enter the address you signed up with and we will send a new confirmation link.",
  "auth.verify.resend": "Send the link again",
  "auth.verify.resent": "A new link is on its way.",
  "auth.verify.done": "Your e-mail address is confirmed.",
  "auth.twoFactor.title": "Two-factor authentication",
  "auth.twoFactor.body": "Enter the 6-digit code from your authenticator app.",
  "auth.twoFactor.code": "Code",
  "auth.twoFactor.trustDevice": "Trust this device for 30 days",
  "auth.twoFactor.submit": "Verify",
  "auth.twoFactor.useBackup": "Use a backup code instead",
  "auth.twoFactor.useTotp": "Use the authenticator app instead",
  "auth.twoFactor.backupCode": "Backup code",
  "auth.twoFactor.error.invalid": "That code is not valid.",
  "auth.twoFactor.error.locked": "Too many wrong codes. Try again later.",
  "auth.twoFactor.email.body": "We sent a 6-digit code to your e-mail. It is valid for 5 minutes.",
  "auth.twoFactor.email.resend": "Send a new code",
  "auth.twoFactor.email.resent": "A new code is on its way.",
  "auth.twoFactor.useEmail": "E-mail me a code instead",
  "auth.forgot.title": "Reset your password",
  "auth.forgot.body":
    "Enter your e-mail address and we will send you a link to choose a new password.",
  "auth.forgot.submit": "Send the link",
  "auth.forgot.sent": "If that address has an account, a link is on its way.",
  "auth.forgot.back": "Back to sign in",
  "auth.reset.title": "Choose a new password",
  "auth.reset.newPassword": "New password",
  "auth.reset.confirm": "Repeat the new password",
  "auth.reset.submit": "Change password",
  "auth.reset.done": "Your password was changed. Sign in with the new one.",
  "auth.reset.error.invalidToken": "This link is no longer valid. Request a new one.",
  "auth.error.title": "Sign-in problem",
  "auth.error.body": "The sign-in could not be completed ({code}). Try again.",
  "platform.meta": "Meta",
  "platform.google": "Google",
  "adAccounts.title": "Advertising accounts",
  "adAccounts.intro":
    "These are the accounts Scalephant is allowed to act on. You stay the owner. Connecting one does not advertise on it: every campaign picks its own accounts, and nothing spends until you launch it.",
  "adAccounts.empty": "No advertising account is connected yet.",
  "adAccounts.noneConfigured": "No advertising platform is set up on this server yet.",
  "adAccounts.connect": "Connect {platform}",
  "adAccounts.add": "Add {platform} ad account",
  "adAccounts.reconnect": "Reconnect",
  "adAccounts.disconnect": "Disconnect",
  "adAccounts.column.account": "Account",
  "adAccounts.column.status": "Status",
  "adAccounts.column.checked": "Checked",
  "adAccounts.column.actions": "Actions",
  "adAccounts.status.connected": "Connected",
  "adAccounts.status.pending": "Connecting",
  "adAccounts.status.disconnected": "Disconnected",
  "adAccounts.status.error": "Needs attention",
  "adAccounts.reason.tokenUnreadable":
    "We can no longer read this account's stored access. Reconnect it to continue.",
  "adAccounts.reason.reauthorize": "The platform no longer accepts our access. Reconnect it.",
  "adAccounts.reason.noPaymentMethod":
    "The platform says this account cannot spend. Check its payment method.",
  "adAccounts.reason.permission":
    "The platform refused access to this account. Reconnect it with a login that can manage the account.",
  "adAccounts.reason.disconnected": "You disconnected this account.",
  "adAccounts.reason.unknown": "The platform reported a problem. Reconnecting usually fixes it.",
  "adAccounts.disconnectTitle": "Disconnect this account?",
  "adAccounts.disconnectWarning":
    "Campaigns already running stay running at the platform, and Scalephant stops managing them. Their history is kept.",
  "adAccounts.error.notConfigured": "{platform} is not set up on this server.",
  "campaigns.title": "Campaigns",
  "campaigns.new": "New campaign",
  "campaigns.empty": "No campaigns yet.",
  "campaigns.connectFirst": "Connect an advertising account to get started.",
  "campaigns.unknownAccount": "Unknown account",
  "campaigns.column.campaign": "Campaign",
  "campaigns.column.account": "Account",
  "campaigns.column.status": "Status",
  "campaigns.status.draft": "Draft",
  "campaigns.status.pendingApproval": "Launching",
  "campaigns.status.active": "Running",
  "campaigns.status.paused": "Paused",
  "campaigns.status.error": "Needs attention",
  "campaigns.status.completed": "Finished",
  "campaigns.status.unmanaged": "No longer managed",
  "campaigns.status.unknown": "Unknown",
  "campaigns.pausedBy.user": "You paused this.",
  "campaigns.pausedBy.cpaGuardrail": "Paused: it was costing too much per customer.",
  "campaigns.pausedBy.zeroConversions": "Paused: it spent without converting anyone.",
  "campaigns.pausedBy.monthlyCap": "Paused: this group has spent its month.",
  "campaigns.pausedBy.anomaly": "Paused: its numbers changed sharply.",
  "campaigns.pausedBy.platformRejected": "Paused: the platform rejected it.",
  "campaigns.pausedBy.accountDisconnected": "Paused: its advertising account was disconnected.",
  "campaigns.pausedBy.noSales": "Paused: no conversions in 14 days. The click campaign runs again.",
  "campaigns.pausedBy.goalChanged":
    "Paused: you chose another goal. The click campaign runs again.",
  "campaigns.pausedBy.goalCampaignRunning":
    "Paused: a campaign that optimizes for your goal runs instead. This one comes back if that one does not do better within 28 days.",
  "campaigns.pausedBy.goalNotBetter":
    "Paused: it did not get conversions cheaper than the click campaign in 28 days. The click campaign runs again.",
  "campaigns.pausedBy.unknown": "Paused by an automation.",
  "campaigns.notFound": "That campaign does not exist, or is not yours.",
  "campaigns.launchQueued":
    "Launch queued. It takes a moment at the platform; its status here will say when it's running.",
  "campaigns.copyRetryQueued":
    "Copy publication retry queued. Check the status here for the outcome.",
  "campaigns.copyRetryHint":
    "The campaign is already bound at the platform, but publishing its ad copy failed. Retry the copy without creating another campaign.",
  "campaigns.review.title": "Review before launch",
  "campaigns.review.intro": "The campaign is not live and spends nothing until you launch it.",
  "campaigns.review.budget": "Budget",
  "campaigns.review.targeting": "Targeting",
  "campaigns.review.notSet": "Not set",
  "campaigns.review.placement": "Placement",
  "campaigns.review.creatives": "Review creatives",
  "campaigns.review.creativesMissing": "Review your ad texts and images first.",
  "campaigns.review.others": "Other placements in this group",
  "campaigns.review.othersHint": "Review and launch each placement separately.",
  "campaigns.review.creativesIntro":
    "Draft → creatives → review → launch. Prepare copy and images here, then check budget and targeting before choosing to launch. A story is optional inspiration, not a launch requirement.",
  "campaigns.review.budgetTargeting": "Review budget and targeting",
  "campaigns.review.imagesQueued": "Images are queued, not live; review them when they appear.",
  "campaigns.review.onboarding":
    "Draft → creatives → review → launch. Creating drafts does not start spending; review each placement's budget and targeting before launching it separately.",
  "campaigns.review.storyOptional": "A story is optional inspiration, not a required step.",
  "campaigns.section.budget": "Budget",
  "campaigns.section.platform": "At the platform",
  "campaigns.section.performance": "Last 30 days",
  "campaigns.section.history": "History",
  "campaigns.budget.none": "No budget is set.",
  "campaigns.budget.daily": "{amount} a day",
  "campaigns.budget.newDaily": "New daily budget",
  "campaigns.budget.save": "Change budget",
  "campaigns.budget.invalid": "Enter an amount greater than zero.",
  "campaigns.budget.refused": "The platform would not accept that change.",
  "campaigns.budget.refusedNearest": "That is too big a step. The nearest allowed is {amount}.",
  "campaigns.budget.platformDiffers":
    "The platform still has {amount} a day. An earlier change did not finish; the next run will retry it.",
  "campaigns.platform.notLaunched": "Not launched yet.",
  "campaigns.platform.pending": "Launching.",
  "campaigns.platform.live": "Live.",
  "campaigns.platform.paused": "Paused.",
  "campaigns.platform.failed": "The launch failed.",
  "campaigns.targeting": "Countries: {countries}.",
  "campaigns.pmax.running": "Google is also testing Performance Max ads with part of this budget.",
  "campaigns.pmax.kept":
    "Performance Max ads did at least as well as search ads and keep part of this budget.",
  "campaigns.pmax.stopped":
    "Performance Max ads were tested and stopped; their budget came back to this campaign.",
  "campaigns.countries.change": "Change",
  "campaigns.countries.save": "Save countries",
  "campaigns.countries.saved": "Saved. Google gets the new countries within a few minutes.",
  "campaigns.action.pause": "Pause",
  "campaigns.pause.title": "Pause this campaign?",
  "campaigns.pause.warning":
    "Pause {name} at the ad platform? It remains active if the platform cannot confirm the pause.",
  "campaigns.action.delete": "Delete campaign",
  "campaigns.delete.title": "Delete this campaign?",
  "campaigns.delete.warning":
    "Remove {name}? This removes only this campaign. If the ad platform cannot confirm the operation, it will remain here.",
  "campaigns.delete.done": "Campaign removed after the platform confirmed the operation.",
  "campaigns.delete.journalRequired":
    "Removal is temporarily unavailable. This campaign remains here until it can be removed safely.",
  "campaigns.delete.launching":
    "This campaign is still being launched. Try again in a few minutes.",
  "campaigns.delete.busy": "Scalephant is updating this campaign right now. Try again in a minute.",
  "campaigns.delete.reconnect":
    "Reconnect the ad account in Settings first, then delete the campaign.",
  "campaigns.delete.pilot":
    "This campaign is part of a running test and cannot be deleted until the test ends.",
  "campaigns.launch.notEligible":
    "The ad platform does not let this campaign run. Check your ad account (for example billing or an account hold) and press Launch again, or delete the campaign.",
  "campaigns.action.resume": "Resume",
  "campaigns.action.launch": "Launch",
  "campaigns.measurement.title": "Optimization and measurement",
  "campaigns.measurement.visits": "Optimizing for page visits",
  "campaigns.measurement.googleVisits": "Driving website visits with click bidding",
  "campaigns.measurement.noGoal": "No conversion goal selected",
  "campaigns.measurement.check": "Check tracking",
  "campaigns.measurement.details": "Measurement details",
  "campaigns.launch.blocked.support": "Support needs to check an earlier launch first.",
  "campaigns.launch.blocked.tracking": "Check your tracking settings first.",
  "campaigns.launch.blocked.acknowledge": "Tick the box above to launch with traffic optimization.",
  "campaigns.launch.blocked.chooseCountries": "Choose countries",
  "campaigns.launch.blocked.setBudget": "Set budget",
  "campaigns.launch.uncertain":
    "A previous launch could not be confirmed. Contact support to check it before launching again.",
  "campaigns.launch.configuration":
    "Check the selected conversion goal and platform permissions. You do not need a previous conversion to launch.",
  "campaigns.launch.metaSettings":
    "Meta did not accept the campaign settings. Support can check the selected event, pixel permissions and objective compatibility. Do not reconnect a confirmed pixel just because no conversions have arrived yet.",
  "campaigns.launch.googleSettings":
    "Google did not accept the campaign settings. Check that an enabled primary conversion action matches your selected event. No previous conversions are required.",
  "campaigns.measurement.goal": "Goal: {goal}",
  "campaigns.measurement.traffic": "Effective optimization: link clicks (Traffic).",
  "campaigns.measurement.conversions": "Conversion optimization selected",
  "campaigns.measurement.businessGoal":
    "Effective optimization follows the campaign's business goal.",
  "campaigns.measurement.unmeasured":
    "Conversion measurement is incomplete. Delivery and click data do not prove purchases, sign-ups or ROAS.",
  "campaigns.measurement.warning":
    "Conversion tracking is not configured. You can launch now using traffic optimization. Scalephant can monitor delivery and clicks, but cannot reliably measure purchases/sign-ups or optimize for them until tracking is configured.",
  "campaigns.measurement.learning":
    "Click winners are not conversion winners. Conversion/ROAS learning is limited; setting up tracking will not automatically change this campaign’s optimization.",
  "campaigns.measurement.setup": "Set up tracking",
  "campaigns.measurement.serverWaiting":
    "Server-side tracking is set up. Waiting for the first event from your server.",
  "campaigns.measurement.acknowledge":
    "I understand the traffic optimization and measurement limits and authorize this launch.",
  "campaigns.measurement.launchTraffic": "Launch with traffic optimization",
  "campaigns.measurement.missing.pixel": "Missing: selected Meta pixel/dataset.",
  "campaigns.measurement.missing.pixel_health":
    "Missing: recently verified, healthy Meta pixel/dataset.",
  "campaigns.measurement.missing.primary_event":
    "Missing: verified primary purchase, lead or sign-up event.",
  "campaigns.measurement.missing.primary_event_ambiguous":
    "Primary event is ambiguous: resolve lead versus sign-up tracking before conversion optimization.",
  "campaigns.measurement.missing.event_signal":
    "Missing: recent confirmed browser-rule or authenticated server event signal.",
  "campaigns.measurement.missing.provider_acceptance":
    "Accepted delivery of the primary event has not yet been confirmed by the ad platform. Local ingestion alone is not acceptance.",
  "campaigns.action.retryCopy": "Retry copy publication",
  "campaigns.metrics.none": "No numbers yet.",
  "campaigns.metrics.summary": "{spend} spent · {clicks} clicks · {conversions} conversions",
  "campaigns.history.none": "Nothing has happened yet.",
  "creatives.copy.title": "Ad copy",
  "creatives.copy.edit": "Edit copy",
  "creatives.copy.headline": "Headline",
  "creatives.copy.body": "Body",
  "creatives.copy.cta": "CTA",
  "creatives.copy.editHint":
    "Saving invalidates the prior review. Review the saved text again before launch. Editing does not use your generation allowance.",
  "campaigns.review.countriesMissing": "Choose at least one country first.",
  "campaigns.review.budgetMissing": "Set a daily budget first.",
  "campaigns.review.creativesError": "We couldn't check your ads. Reload the page to try again.",
  "campaigns.draft.localOnly":
    "Draft settings are saved locally. Saving does not publish or change anything at the ad platform.",
  "campaigns.draft.countries": "Target countries (e.g. FI, SE)",
  "campaigns.draft.minAge": "Minimum age (optional)",
  "campaigns.draft.maxAge": "Maximum age (optional)",
  "campaigns.draft.saveTargeting": "Save targeting",
  "creatives.delete.copy": "Delete copy",
  "creatives.delete.image": "Delete image",
  "creatives.delete.confirm": "Confirm deletion",
  "creatives.delete.copyWarning":
    "Delete only this copy? This cannot be undone and does not use your AI allowance.",
  "creatives.delete.imageWarning":
    "Delete only this image? Copies linked to it lose approval and are retired from launch. This cannot be undone and does not use your AI allowance.",
  "campaigns.review.copyEmpty": "Create your ad texts first.",
  "campaigns.review.copyUnreviewed": "Review your ad texts first.",
  "campaigns.review.imageMissing": "Review at least one ad image first.",
  "creatives.regenerate.copy": "Regenerate copy",
  "creatives.regenerate.image": "Regenerate image",
  "creatives.regenerate.warning":
    "Manual regeneration is subject to your plan's fair-use limits. The existing creative stays until a replacement succeeds. Replacement clears human approval; review it again before launch.",
  "creatives.regenerate.confirm": "Confirm regeneration",
  "creatives.regenerate.queued":
    "Replacement queued. Existing image remains on failure. Refresh after generation; review the replacement and linked copy before launch.",
  "creatives.copy.none": "No ad copy yet.",
  "creatives.copy.generate": "Write ad copy",
  "creatives.copy.writing": "Writing… this can take up to a minute",
  "creatives.copy.live": "Live",
  "creatives.copy.unreviewed": "Not reviewed",
  "creatives.copy.why": "Why this draft?",
  "creatives.copy.strategy.hypothesis": "Writer's hypothesis",
  "creatives.copy.strategy.buyer": "Likely buyer",
  "creatives.copy.strategy.trigger": "Why now",
  "creatives.copy.strategy.outcome": "Desired outcome",
  "creatives.copy.strategy.differentiator": "Proposed difference",
  "creatives.copy.strategy.objection": "Possible hesitation",
  "creatives.copy.strategy.awareness": "Assumed awareness",
  "creatives.copy.sourceBasis":
    "Based on your saved product facts and website excerpts available when this draft was written.",
  "creatives.copy.factsBasis":
    "Based on your saved product facts; no matching website source was available for this draft.",
  "creatives.copy.evidenceLimits":
    "This angle is a messaging hypothesis. Customer problems and competitor positioning are not independently validated; review product claims before launch.",
  "creatives.copy.outcomeBasis": "Private business observations",
  "creatives.copy.industryBasis": "Tentative industry draft hypothesis",
  "creatives.copy.industryForm.question": "Question-form headline",
  "creatives.copy.industryForm.statistic": "Number-form headline",
  "creatives.copy.industryForm.urgency": "Urgency-form headline",
  "creatives.copy.industryForm.curiosity": "Curiosity-form headline",
  "creatives.copy.industryForm.pain_point": "Problem-form headline",
  "creatives.copy.industryForm.social_proof": "Social-proof-form headline",
  "creatives.copy.industryForm.direct": "Direct-form headline",
  "creatives.copy.industryLimits":
    "Based on customer-reported quality, not verified sales or a winning angle. Capture completeness is unknown and URL association is partial. Your own relevant results take precedence.",
  "creatives.copy.qualifiedBasis": "Customer-qualified reports from earlier ads",
  "creatives.copy.qualified.paid_order": "Backend-reported paid orders",
  "creatives.copy.qualified.useful_lead": "Backend-reported useful leads",
  "creatives.copy.qualifiedLimits":
    "Customer-reported quality, not independently verified. Closed UTC days do not prove complete capture; URL association is partial. No winner or budget change is inferred.",
  "creatives.copy.creativeOutcomeBasis": "Reports associated with earlier ads",
  "creatives.copy.creativeOutcomeLimits":
    "These reports match the campaign's conversion definition frozen at launch. URL tags are reported associations, not verified attribution or lead/payment quality. Use them as follow-up ideas, not winners; no automatic serving change is based on these associations.",
  "creatives.copy.outcomeLimits":
    "Your backend reported these campaign-attributed events. Capture completeness is unknown; these are not independently qualified sales or proof of a winning creative. No customer data is shared with other advertisers.",
  "creatives.copy.outcome.purchase": "Purchases",
  "creatives.copy.outcome.lead": "Leads",
  "creatives.copy.outcome.signup": "Signups",
  "creatives.copy.marketBasis": "Saved market research",
  "creatives.copy.marketHypotheses": "Possible messaging gaps — hypotheses",
  "creatives.copy.marketLimits":
    "This saved model summary describes competitor messaging, not proven customer pain or effectiveness. Links are a source inventory, not citations for each claim; the date is the report date, not each page's retrieval date.",
  "creatives.copy.reviewed": "Reviewed",
  "creatives.copy.review": "Approve copy",
  "creatives.copy.approveAll": "Approve all",
  "creatives.extras.title": "Ad extras",
  "creatives.extras.hint":
    "Links and short facts shown under your Google ad. Approved together with the ad copy.",
  "creatives.extras.pending": "Not reviewed",
  "creatives.extras.live": "On Google",
  "creatives.extras.remove": "Remove",
  "creatives.extras.approve": "Approve ad extras",
  "creatives.copy.unapprovedCount": "{count} of {total} not reviewed",
  "creatives.action.approve": "Approve",
  "creatives.action.edit": "Edit",
  "creatives.action.regenerate": "Regenerate",
  "creatives.action.delete": "Delete",
  "creatives.copy.error.productIncomplete":
    "Ads are written from what the product is and who it is for, and this product has no description yet. Describe it first, or let us read it from your website.",
  "creatives.copy.error.describeProduct": "Describe the product",
  "creatives.copy.error.noCredits":
    "Your plan's current AI usage limit has been reached. Try after renewal or contact support.",
  "creatives.copy.error.unpublishable":
    "We could not write copy that stays within what you have told us about the product. Adding your real offers and customer testimonials gives the ads more to work with.",
  "creatives.copy.error.modelFailed":
    "The writing model did not answer this time. Try again in a minute.",
  "creatives.copy.error.recoveryStorage":
    "Browser storage is unavailable. Enable tab storage before generating copy; no request was sent.",
  "creatives.copy.error.recoveryDamaged":
    "The saved generation identity is damaged. Contact support before retrying; no new generation was started.",
  "creatives.copy.error.sharingApproval":
    "AI data sharing approval is missing or does not allow this provider. Contact support before generating new copy. No AI call was made.",
  "creatives.copy.error.organizationChanged":
    "The active organization changed. Reload this campaign in the intended organization before continuing.",
  "creatives.copy.error.operationUnresolved":
    "The previous generation needs verification before continuing. Contact support; do not start another paid generation to recover it.",
  "creatives.copy.error.operationMismatch":
    "The saved generation no longer matches this request or its copy. Check the existing copy and contact support before retrying.",
  "creatives.brand.title": "We found on {site}",
  "creatives.brand.none": "No brand colours found on the site. Images use your logo's colours.",
  "creatives.brand.fromSite": "Your images use these colours.",
  "creatives.brand.yours": "Your own colours. Images use these.",
  "creatives.brand.main": "Main colour",
  "creatives.brand.accent": "Accent colour",
  "creatives.brand.change": "Change",
  "creatives.brand.useSite": "Use the site's colours",
  "creatives.brand.reread": "Read the site again",
  "creatives.brand.reading": "Reading…",
  "creatives.brand.save": "Save",
  "creatives.brand.cancel": "Cancel",
  "creatives.brand.style": "Look: {words}",
  "creatives.brand.stylePending": "Looking at the site's style…",
  "creatives.brand.imagery.photography": "photographs",
  "creatives.brand.imagery.illustration": "illustration",
  "creatives.brand.imagery.graphic": "graphic",
  "creatives.brand.imagery.product": "product pictures",
  "creatives.brand.imagery.minimal": "minimal",
  "creatives.brand.mood.calm": "calm",
  "creatives.brand.mood.energetic": "energetic",
  "creatives.brand.mood.premium": "premium",
  "creatives.brand.mood.playful": "playful",
  "creatives.brand.mood.technical": "technical",
  "creatives.brand.mood.warm": "warm",
  "creatives.brand.mood.bold": "bold",
  "creatives.brand.shapes.rounded": "rounded shapes",
  "creatives.brand.shapes.sharp": "straight edges",
  "creatives.brand.shapes.organic": "flowing shapes",
  "creatives.brand.shapes.geometric": "geometric shapes",
  "creatives.images.title": "Images",
  "creatives.images.none": "No images yet.",
  "creatives.images.generate": "Generate images",
  "creatives.images.queued":
    "{count} images queued. They take a while at the provider and appear here as they are made.",
  "creatives.images.error.notRunning":
    "Image generation is not running right now, so nothing was queued. Try again in a few minutes.",
  "creatives.images.error.planExcludes": "Your plan does not include generated images.",
  "creatives.images.error.seePlans": "See plans",
  "creatives.images.uploaded": "Uploaded",
  "creatives.images.preview": "Campaign image preview",
  "creatives.images.generatedBy": "Generated by {model}",
  "creatives.images.reviewed": "Reviewed",
  "creatives.images.unreviewed": "Not reviewed",
  "creatives.images.review": "Approve image",
  "wizard.title": "New campaign group",
  "wizard.needsAccount":
    "Connect an advertising account first — there is nowhere to run a campaign yet.",
  "wizard.step.product": "What are you advertising?",
  "wizard.step.where": "Where should it run?",
  "wizard.countries": "Target countries: {countries}",
  "wizard.countries.change": "Change",
  "wizard.countries.label": "Target countries",
  "wizard.countries.missing": "Choose at least one country.",
  "wizard.step.budget": "How much a month?",
  "wizard.existingProfile": "Product",
  "wizard.newProfile": "A new product",
  "wizard.productName": "Product name",
  "wizard.websiteUrl": "Website",
  "wizard.mixedCurrency":
    "These accounts bill in different currencies: {accounts}. The ad platform fixes an account's currency when the account is created. For now one budget covers one currency, so pick accounts that share it, or open an ad account in the same currency on the other platform.",
  "wizard.monthlyBudget": "Monthly budget ({currency})",
  "wizard.monthlyBudgetHint":
    "If the ad platform does not confirm the pause, account spending may exceed this budget.",
  "wizard.split": "{amount} a day on each of {count} accounts.",
  "wizard.dropped":
    "{count} of the accounts you picked will not get a campaign: the month cannot fund them all at the {minimum} a day platforms require.",
  "wizard.create": "Create drafts",
  "wizard.draftsOnly": "This creates drafts. Nothing spends until you launch it.",
  "wizard.error.noRoom":
    "Your plan has no room for these campaigns. Pause another campaign or choose a larger plan.",
  "wizard.planAllows": "Your {plan} plan runs {count} campaign(s) at a time.",
  "wizard.tooManyForPlan":
    "You have picked {picked}, more than your plan runs at once. Pick fewer accounts, or move to a larger plan.",
  "wizard.changePlan": "See the plans",
  "wizard.landingUrl": "Landing page (optional)",
  "wizard.landingUrl.hint": "Leave empty to send people to the product's website.",
  "wizard.editProduct": "Add a description, audience and offers",
  "wizard.productDetailsHint":
    "The ad copy is written from the product's details: its description, audience and offers. Fill them in under Settings → Company & products.",
  "wizard.error.url": "That does not look like a web address.",
  "wizard.error.duplicate":
    "You already have a product with that name. Pick it from the list above.",
  "dashboard.title": "Dashboard",
  "dashboard.welcome": "Welcome, {name}.",
  "settings.title": "Settings",
  "settings.profile.title": "Profile",
  "settings.security.title": "Security",
  "settings.profile.language": "Interface language",
  "settings.profile.saved": "Profile saved.",
  "settings.products.title": "Company & products",
  "products.intro":
    "What you sell, who it is for and what your ads may say. Every campaign is written from this.",
  "products.empty":
    "No products yet. Describe your first one below; the campaign wizard can then use it.",
  "products.picker": "Product",
  "products.new": "+ A new product",
  "products.create": "Add product",
  "products.saved": "Saved.",
  "products.intel.title": "Product research",
  "products.intel.pending": "Preparing saved product facts and website sources automatically.",
  "products.intel.limited":
    "Saved facts and website context are ready. Research is limited, not independent validation.",
  "products.intel.unavailable":
    "Research is unavailable or interrupted. Your saved product facts remain usable.",
  "products.intel.updated": "Updated:",
  "products.intel.facts": "Facts supplied by you",
  "products.intel.website": "Your website's claims — not independently verified",
  "products.intel.competitors": "Competitors have not been researched.",
  "products.intel.citations": "Provider-returned citations",
  "products.intel.mentions": "Mentioned competitor websites",
  "products.intel.unclassified": "Source inventory — citation classification unknown",
  "products.intel.market": "Saved market hypotheses",
  "products.intel.automaticResearch": "Automatic web research",
  "products.intel.hypotheses":
    "Competitor positioning and proposed angles are not proof of customer pain or effective advertising. Sources are an inventory, not claim-level citations.",
  "products.intel.offline":
    "Offline verification only — this is not live research or validated demand.",
  "products.intel.problems": "Customer problems have not been validated.",
  "products.intel.researchRetry":
    "Competitor research did not finish this time. It runs again automatically.",
  "products.intel.researchFailed":
    "Competitor research could not be finished for this version. Editing the product starts it again.",
  "products.intel.websiteUnavailable": "The website could not be read. Saved facts are retained.",
  "products.section.basics": "The product",
  "products.name": "Name",
  "products.website": "Website",
  "products.website.hint": "Where your ads send people, unless a campaign names another page.",
  "products.description": "What it is and what it does",
  "products.description.hint":
    "A few sentences in your own words. The ad copy is written from this.",
  "products.audience": "Who it is for",
  "products.audience.hint":
    "For example: owners of small online shops in Finland who run their own ads.",
  "products.language": "Language of the ads",
  "products.language.site": "Same as the website",
  "products.countries": "Countries",
  "products.countries.hint":
    "Two-letter codes separated by commas, e.g. FI, SE. EU covers the whole Union.",
  "products.goal": "What do you want from advertising?",
  "products.goal.clicks": "Visitors to my website",
  "products.goal.signup": "Sign-ups",
  "products.goal.lead": "Leads (contact requests)",
  "products.goal.purchase": "Sales",
  "products.goal.hint":
    "Campaigns start by bringing visitors. Once over 50 of your goal have come in within 30 days, Scalephant opens a campaign for it beside them.",
  "products.section.revenue": "Revenue",
  "products.revenue.intro":
    "What a customer is worth to you. The guardrails use this to tell a good cost per sale from a bad one.",
  "products.revenueModel": "How you earn",
  "products.revenueModel.unset": "Not set",
  "products.revenueModel.one_time": "One-off purchases",
  "products.revenueModel.subscription": "Subscriptions",
  "products.currency": "Currency",
  "products.orderValue": "Average order value",
  "products.subscriptionValue": "Monthly subscription price",
  "products.ltvMonths": "How many months a customer stays",
  "products.ltvMonths.hint": "An estimate is fine, from 1 to 120.",
  "products.adBudget": "Monthly ad budget",
  "products.adBudget.hint": "Across all campaigns. Optional.",
  "products.section.claims": "What the ads may say",
  "products.claims.intro":
    "Ads mention only the offers and quote only the customers listed here. Anything else a model writes is removed before it is published.",
  "products.offers": "Offers",
  "products.offers.hint":
    'One per line, for example "14-day free trial" or "Free delivery over 50 €".',
  "products.testimonials": "Customer testimonials",
  "products.testimonials.hint": "Word for word, and only with the customer's permission.",
  "products.testimonial.quote": "Quote",
  "products.testimonial.attribution": "Who said it",
  "products.testimonial.remove": "Remove",
  "products.testimonial.add": "Add a testimonial",
  "products.section.creative": "Creatives",
  "products.copyEnabled": "Write ad copy with AI",
  "products.imagesEnabled": "Make images with AI",
  "products.videoEnabled": "Make videos",
  "products.error.name": "Give the product a name.",
  "products.error.url": "That does not look like a web address.",
  "products.error.amount": "Write the amount as a number, for example 49.90.",
  "products.error.months": "A whole number of months, from 1 to 120.",
  "products.error.currency": "Choose a currency.",
  "products.error.countries": "Use two-letter country codes (US for the United States), or EU.",
  "products.error.testimonial": "Each testimonial needs both the quote and who said it.",
  "products.error.duplicate": "You already have a product with that name.",
  "onboarding.title": "Set up Scalephant",
  "onboarding.planActive":
    "Your plan is active. Next, tell us about your business and Scalephant builds your first campaign.",
  "onboarding.progress": "Progress",
  "onboarding.step.product": "Your business",
  "onboarding.step.revenue": "Revenue",
  "onboarding.step.claims": "What ads may say",
  "onboarding.step.tracking": "Tracking",
  "onboarding.step.accounts": "Ad accounts",
  "onboarding.step.campaign": "Campaign",
  "onboarding.tracking.title": "Conversion tracking",
  "onboarding.tracking.intro":
    "Set up the tracking snippet and define what counts as a conversion. You can continue and finish this later, but link tags alone cannot measure sales.",
  "onboarding.tracking.continue": "Continue to campaign",
  "onboarding.accounts.title": "Connect your ad accounts",
  "onboarding.accounts.intro":
    "Connect Meta or Google Ads to create a campaign draft. Connecting an account does not launch anything.",
  "onboarding.accounts.continue": "Continue to tracking",
  "onboarding.resumeCreatives": "Continue to creative review",
  "campaigns.review.continueLaunch": "Continue to launch review",
  "campaigns.review.launchNextHint":
    "Next, check your budget and targeting. Launch stays blocked until the required creatives are reviewed; only your explicit launch approval can start advertising.",
  "onboarding.campaign.title": "Your first campaign",
  "onboarding.campaign.intro":
    "Choose your product, ad accounts and monthly budget. This creates drafts; you review the creatives and launch separately.",
  "onboarding.step.next": "Next steps",
  "onboarding.product.title": "Tell us about your business",
  "onboarding.product.intro":
    "Give your website's address and press Read my website: Scalephant fills in what it can, and you check it before saving.",
  "onboarding.revenue.title": "What is a customer worth to you?",
  "onboarding.revenue.intro":
    "The guardrails use this to tell a good cost per sale from a bad one. You can skip this and fill it in later.",
  "onboarding.claims.title": "What may your ads say?",
  "onboarding.claims.intro":
    "Ads mention only the offers and quote only the customers you list here. Leave them empty if you have none.",
  "onboarding.saveAndContinue": "Save and continue",
  "onboarding.back": "Back",
  "onboarding.skip": "Skip for now",
  "onboarding.next.title": "Your business is described",
  "onboarding.next.intro": "What is left before the first campaign runs:",
  "onboarding.next.accounts": "Connect an advertising account",
  "onboarding.next.accountsDone": "An advertising account is connected.",
  "onboarding.next.tracking": "Set up conversion tracking",
  "onboarding.next.campaign": "Create your first campaign",
  "onboarding.next.done": "Go to the dashboard",
  "dashboard.onboarding.title": "Tell Scalephant about your business",
  "dashboard.onboarding.text":
    "Your ads are written from your product's details. It takes a few minutes, and Scalephant can read most of it from your website.",
  "dashboard.onboarding.start": "Start",
  "dashboard.onboarding.resumeTitle": "Continue your campaign setup",
  "dashboard.onboarding.resumeText":
    "Your product is saved. Continue with tracking, ad accounts and your first campaign.",
  "dashboard.onboarding.resume": "Continue onboarding",
  "dashboard.productIncomplete": "{name} has no description yet, and the ads are written from it.",
  "dashboard.productIncomplete.link": "Add one",
  "wizard.startOnboarding": "New here? Describe your business first",
  "products.analysis.run": "Read my website",
  "products.analysis.hint":
    "Fills in the empty fields from your site within your plan's usage limits.",
  "products.analysis.filled": "Filled in from {host}: {fields}. Check them before saving.",
  "products.analysis.nothingEmpty": "Every field already had a value, so nothing was changed.",
  "products.analysis.unavailable": "Reading websites is not switched on for this server yet.",
  "products.analysis.modelFailed":
    "Reading your website failed on our side, and nothing was charged. Try again in a few minutes, or fill in the details yourself.",
  "products.analysis.noCredits":
    "Your plan's current AI usage limit has been reached. Try after renewal or contact support.",
  "products.analysis.recoveryStorage":
    "Your browser cannot keep the recovery request in session storage. No analysis was started. Allow session storage and retry, or fill the details yourself.",
  "products.analysis.recoveryDamaged":
    "The saved recovery request is damaged. No new analysis was started. Contact support or fill the details yourself; do not clear the request to retry paid work.",
  "products.analysis.sharingApproval":
    "AI data sharing is not approved for this analysis or provider. Contact support before retrying, or fill the details yourself. No AI call was made.",
  "products.analysis.organizationChanged":
    "The active organization changed. Reload the page before reading its website. No analysis was started for this request.",
  "products.analysis.operationUnresolved":
    "The previous analysis has not safely completed. Wait and retry this same request; if it remains blocked, contact support or fill the details yourself. Retrying will not start another paid attempt.",
  "products.analysis.operationMismatch":
    "This analysis request belongs to a different address. Check the address and reopen the form if needed; contact support if the conflict remains.",
  "products.analysis.unreadable":
    "We could not read that page. Check the address, or fill the details in yourself.",
  "settings.security.password.title": "Change password",
  "settings.security.password.current": "Current password",
  "settings.security.password.new": "New password",
  "settings.security.password.confirm": "Repeat the new password",
  "settings.security.password.signOutOthers": "Sign out my other devices",
  "settings.security.password.submit": "Change password",
  "settings.security.password.done": "Password changed.",
  "settings.security.password.error.wrong": "The current password is wrong.",
  "settings.security.twoFactor.title": "Two-factor authentication",
  "settings.security.twoFactor.on": "Two-factor authentication is on.",
  "settings.security.twoFactor.off":
    "Two-factor authentication is off. Turn it on to protect your ad accounts.",
  "settings.security.twoFactor.enable": "Turn on",
  "settings.security.twoFactor.enableEmail": "Turn on with an e-mailed code",
  "settings.security.twoFactor.enableApp": "Use an authenticator app instead",
  "settings.security.twoFactor.email.done":
    "Two-factor authentication is on. When you sign in, we e-mail you a code.",
  "settings.security.twoFactor.disable": "Turn off",
  "settings.security.twoFactor.passwordPrompt": "Confirm with your password",
  "settings.security.twoFactor.setup.title": "Add Scalephant to your authenticator app",
  "settings.security.twoFactor.setup.body":
    "Scan the QR code or enter this key by hand, then type the code the app shows.",
  "settings.security.twoFactor.setup.key": "Key",
  "settings.security.twoFactor.setup.confirm": "Confirm code",
  "settings.security.twoFactor.setup.done":
    "Two-factor authentication is on. Store the backup codes somewhere safe.",
  "settings.security.twoFactor.backupCodes": "Backup codes",
  "settings.security.twoFactor.backupCodes.body":
    "Each code works once if you lose your authenticator.",
  "settings.security.twoFactor.disabled": "Two-factor authentication is off.",
  "settings.security.sessions.title": "Signed-in devices",
  "settings.security.sessions.current": "This device",
  "settings.security.sessions.signOutAll": "Sign out everywhere",
  "settings.security.sessions.signOutOthers": "Sign out other devices",
  "settings.security.sessions.done": "Other devices were signed out.",
  "settings.security.sessions.unknownDevice": "Unknown device",
  "settings.security.stepUp.title": "Confirm it is you",
  "settings.security.stepUp.body": "Enter your password, or get a code by e-mail, to continue.",
  "settings.security.stepUp.bodyPassword": "Enter your password to continue.",
  "settings.security.stepUp.codeSent": "We sent a code to your e-mail.",
  "settings.security.stepUp.resend": "Send a new code",
  "settings.security.stepUp.submit": "Confirm",
  "settings.security.stepUp.method": "Confirm with",
  "settings.security.stepUp.method.password": "Password",
  "settings.security.stepUp.method.email": "E-mail me a code",
  "settings.security.stepUp.error": "That did not match. Try again.",
  "settings.billing.title": "Billing",
  "billing.intro": "Your subscription, fair use and invoices.",
  "billing.notConfigured":
    "Payments are not enabled on this server yet. The plans below are for information only.",
  "billing.checkout.success":
    "Thank you! Stripe is confirming your subscription; this page updates in a moment.",
  "billing.checkout.canceled": "Checkout was cancelled. Nothing was charged.",
  "billing.current.title": "Current plan",
  "billing.current.none": "No subscription yet. Choose a plan to get started.",
  "billing.current.trialActive":
    "Full autopilot runs until {date}. Choose a plan to keep it running after that.",
  "billing.current.trialEnded":
    "The trial ended on {date}. Autopilot and AI refreshes pause until you choose a plan.",
  "billing.current.pending": "Waiting for Stripe to confirm the subscription…",
  "billing.status.trialing": "Trial",
  "billing.status.active": "Active",
  "billing.status.past_due": "Payment overdue",
  "billing.status.canceled": "Cancelled",
  "billing.status.unpaid": "Unpaid",
  "billing.status.incomplete": "Incomplete",
  "billing.status.incomplete_expired": "Expired",
  "billing.status.paused": "Paused",
  "billing.period": "Current period {start} – {end}",
  "billing.cancelAtPeriodEnd": "The subscription ends at the end of this period.",
  "billing.trialEnds": "The trial ends on {date}.",
  "billing.usage.title": "Usage this period",
  "billing.usage.consumed": "Manual fair-use allowance used: {percent}%",
  "billing.usage.overage":
    "No automatic overage charges. Contact support if you need more manual use.",
  "billing.usage.balance":
    "Automatic optimization does not consume this allowance. Different manual AI tasks have different usage weights.",
  "billing.plans.title": "Plans",
  "billing.plan.free.price": "Free",
  "billing.plan.perMonth": "{price} / month",
  "billing.plan.campaigns.one": "1 active campaign",
  "billing.plan.campaigns.many": "Up to {count} active campaigns",
  "billing.plan.credits": "AI tools under subscription fair use",
  "billing.plan.credits.once": "AI tools included for trying the service",
  "billing.plan.creatives":
    "{concepts} ad concepts, {images} images and {videos} videos per campaign",
  "billing.plan.creatives.noVideo": "{concepts} ad concepts and {images} images per campaign",
  "billing.plan.freeTrial": "{days}-day full-autopilot trial",
  "billing.plan.overage": "Contact support for additional usage",
  "billing.plan.noOverage": "No overage charges; manual use may pause at the fair-use limit",
  "billing.plan.trial": "{days}-day free trial",
  "billing.plan.choose": "Choose {plan}",
  "billing.plan.current": "Your plan",
  "billing.manage": "Manage billing",
  "billing.manage.hint":
    "Change the plan, update the card or download invoices in the Stripe customer portal.",
  "billing.payments.title": "Payments",
  "billing.payments.none": "No payments yet.",
  "billing.payment.status.paid": "Paid",
  "billing.payment.status.failed": "Failed",
  "billing.payment.status.pending": "Pending",
  "billing.payment.status.refunded": "Refunded",
  "billing.error.role": "Only owners and admins can manage billing.",
  "billing.error.alreadySubscribed":
    "The organization already has a subscription. Use Manage billing to change the plan.",
  "billing.error.noCustomer": "There is no billing account yet. Choose a plan first.",
  "billing.error.unavailable": "Payments are not available right now. Please try again later.",
  "nav.admin": "Admin",
  "admin.title": "Platform administration",
  "admin.intro": "Every organization on the platform. Actions here are audited under your name.",
  "admin.twoFactorRequired":
    "Staff actions require two-factor authentication. Turn it on in Security settings first.",
  "admin.organizations.title": "Organizations",
  "admin.organizations.none": "No organizations yet.",
  "admin.organizations.members": "{count} members",
  "admin.organizations.noPlan": "No plan",
  "admin.organizations.balance": "{available} credits available",
  "admin.credits.title": "Adjust credits for {organization}",
  "admin.credits.amount": "Credits (negative removes)",
  "admin.credits.reason": "Reason",
  "admin.credits.submit": "Apply",
  "admin.credits.done": "Balance is now {available} credits.",
  "admin.credits.error.insufficient": "The organization does not have that many credits.",
  "admin.credits.open": "Adjust credits",
  "admin.audit.title": "Recent audit entries",
  "admin.audit.none": "Nothing recorded yet.",
  "admin.audit.filter": "Organization",
  "admin.audit.all": "All organizations",
  "admin.audit.platform": "platform",
  "admin.error.forbidden": "This page is for platform staff.",
  "validation.nonZero": "Enter a non-zero amount.",
  "validation.tooShort": "Too short.",
  "validation.tooLong": "Too long.",
  "settings.privacy.title": "Privacy",
  "privacy.intro": "Your data: take a copy of it, or delete your account.",
  "privacy.export.title": "Your data",
  "privacy.export.body":
    "Get a copy of the personal data we hold about you as a JSON file. It is prepared in the background and we e-mail you when it is ready; the download stays available for seven days.",
  "privacy.export.request": "Request a copy of my data",
  "privacy.export.requested":
    "We are preparing your copy. You will get an e-mail when it is ready.",
  "privacy.export.download": "Download",
  "privacy.export.none": "No exports yet.",
  "privacy.export.status.requested": "Preparing",
  "privacy.export.status.ready": "Ready",
  "privacy.export.status.failed": "Failed",
  "privacy.export.status.expired": "Expired",
  "privacy.delete.title": "Delete account",
  "privacy.delete.body":
    "Deleting your account erases your profile, sign-in methods and sessions. Organizations where you are the only member are deleted only after their advertising campaigns and subscription are safely shut down and verified. In shared organizations you are removed as a member. Deletion starts immediately and stays on hold if external shutdown cannot be verified.",
  "privacy.delete.confirm": "Type your e-mail address to confirm",
  "privacy.delete.submit": "Delete my account",
  "privacy.delete.scheduled":
    "Deletion is starting. You can cancel before external shutdown begins; otherwise we will keep checking until removal can be verified.",
  "privacy.delete.blocked": "The deletion is on hold: {reason}. Fix this or cancel the request.",
  "privacy.delete.blockedCampaigns":
    "Deletion is on hold: Scalephant keeps your account and campaigns until removal on the ad platforms can be safely verified. This request has not yet removed anything on the platforms.",
  "privacy.delete.blockedVerification":
    "The deletion is on hold while external changes are being verified. Your account and campaign connections remain available.",
  "privacy.delete.removingCampaigns":
    "We're removing your campaigns from the ad platforms. Your account is deleted right after, usually within a few minutes. You can still cancel the request.",
  "privacy.delete.blockedBilling":
    "Account deletion is on hold because subscription cancellation cannot yet be safely verified. No cancellation was attempted by this request. Contact support or cancel the request.",
  "privacy.delete.reconnect":
    "To finish, reconnect your {platforms} ad account under Ad accounts. Scalephant's sign-in to it has expired, so we can't stop your campaigns there yet. Once it is reconnected, we remove the campaigns and delete your account automatically.",
  "privacy.delete.processing":
    "Account deletion is in progress. External campaign and subscription shutdown is being verified. This request can no longer be canceled.",
  "privacy.delete.receiptPending":
    "Your deletion request is being checked. Your account has not been confirmed deleted yet.",
  "privacy.delete.completed":
    "Your account has been deleted. A separate confirmation e-mail is sent when delivery is available.",
  "privacy.delete.receiptExpired":
    "This deletion status is no longer available in this browser. Check your e-mail for the separate confirmation.",
  "privacy.delete.stopChecking": "Stop checking on this browser",
  "privacy.delete.follow": "Keep checking after sign-out",
  "privacy.delete.cancel": "Cancel deletion",
  "privacy.delete.canceled": "The deletion was canceled.",
  "privacy.delete.soleOwner":
    "You are the only owner of {organizations}. Transfer ownership or remove the other members before deleting your account.",
  "privacy.delete.error.mismatch": "That is not your e-mail address.",
  "organizations.title": "Organizations",
  "organizations.body": "You act on one organization at a time. Switch here.",
  "organizations.active": "Active",
  "organizations.switched": "Now working in {organization}.",
  "organizations.switch": "Switch",
  "organizations.role.owner": "Owner",
  "organizations.role.admin": "Admin",
  "organizations.role.member": "Member",
  "organizations.create.title": "Create an organization",
  "organizations.create.name": "Organization name",
  "organizations.create.submit": "Create",
  "organizations.create.done": "Organization created.",
  "invitations.title": "Invitation",
  "invitations.body": "You were invited to join an organization.",
  "invitations.accept": "Accept invitation",
  "invitations.accepted": "You joined the organization.",
  "invitations.error": "This invitation is no longer valid.",
  "home.signedInAs": "Signed in as {email}",
  "nav.pricing": "Pricing",
  "nav.status": "Status",
  "nav.privacy": "Privacy",
  "nav.subprocessors": "Sub-processors",
  "nav.terms": "Terms",
  "marketing.nav.label": "Site",
  "marketing.cta.start": "Get started",
  "marketing.footer.product": "Product",
  "marketing.footer.legal": "Legal",
  "marketing.footer.contact": "Contact",
  "marketing.footer.copyright": "© {year} Scalephant. All rights reserved.",
  "marketing.footer.googleData":
    "When you connect Google Ads, Scalephant uses that access only to run the campaigns you ask for and to send your website's conversions to your own Google Ads account.",
  "marketing.footer.googleDataLink": "How we handle Google user data",
  "marketing.hero.badge": "Your 24/7 marketing team",
  "marketing.hero.title1": "Your product is automated.",
  "marketing.hero.title2": "Your marketing should be too.",
  "marketing.hero.lead":
    "Scalephant is the marketing team working around the clock across Google and Meta: creating fresh ads, protecting your budget, and turning campaign data into better next moves.",
  "marketing.hero.cta": "Put Scalephant to work",
  "marketing.hero.ctaPricing": "View pricing",
  "marketing.hero.trust.noCard": "No credit card required",
  "marketing.hero.trust.trial": "{days}-day full-autopilot trial",
  "marketing.hero.trust.platforms": "Google and Meta ready",
  "marketing.hero.fineprint": "You control the budget. Ad spend goes directly to Google and Meta.",
  "marketing.beyond.badge": "Built for more than ad delivery",
  "marketing.beyond.title":
    "Google and Meta deliver ads. Scalephant runs the marketing behind them.",
  "marketing.beyond.body1":
    "Your team keeps campaigns supplied with fresh creative, watches every spending rule, and shares the lessons worth carrying from one platform to the other.",
  "marketing.beyond.body2":
    "Scalephant brings creative, performance and budget decisions into one continuous system, so Google and Meta are working from the same picture of your business.",
  "marketing.beyond.body3":
    "Set the goal and guardrails once. Your marketing team carries the work forward.",
  "marketing.beyond.point.creative": "Fresh ads keep giving the platforms new options",
  "marketing.beyond.point.rules": "Your spending rules are watched around the clock",
  "marketing.beyond.point.lessons": "Useful lessons are shared between Google and Meta",
  "marketing.beyond.point.oneplace": "One place to understand what is happening",
  "marketing.flow.google": "Search and Performance Max",
  "marketing.flow.meta": "Facebook and Instagram",
  "marketing.flow.portfolio": "One budget portfolio",
  "marketing.flow.rebalanced": "Rebalanced daily",
  "marketing.pillars.title": "Three jobs your marketing team handles",
  "marketing.pillars.lead":
    "Fresh creative, smarter spending and connected learning: the work that keeps campaigns moving forward on Google and Meta.",
  "marketing.pillars.creative.title": "Creative factory",
  "marketing.pillars.creative.body":
    "Fresh ad copy, images and videos give Google and Meta more options to work with, without you having to keep a creative calendar.",
  "marketing.pillars.money.title": "Money supervisor",
  "marketing.pillars.money.body":
    "Your budget, spending limits and unusual activity are watched continuously, with guardrails the platforms do not build for you.",
  "marketing.pillars.brain.title": "Cross-platform brain",
  "marketing.pillars.brain.body":
    "Lessons from Google and Meta stay connected, so each platform can inform the next move instead of living in its own silo.",
  "marketing.pillars.closing":
    "Creative factory, money supervisor and cross-platform brain: one team keeping every campaign moving.",
  "marketing.steps.title": "A marketing team that starts with your brief and never clocks out.",
  "marketing.steps.lead":
    "Connect Google and Meta, set the direction, and Scalephant takes care of the day-to-day momentum.",
  "marketing.steps.brief.title": "Give your team the brief",
  "marketing.steps.brief.body":
    "Link Google and Meta, explain what you sell, and set the budget and guardrails that define great work.",
  "marketing.steps.supply.title": "Keep every campaign supplied",
  "marketing.steps.supply.body":
    "Scalephant creates fresh ads and videos, tests new angles, and refreshes creative when performance calls for it.",
  "marketing.steps.watch.title": "Stay on top of every signal",
  "marketing.steps.watch.body":
    "Budgets, unusual activity and performance are watched continuously across both platforms, day and night.",
  "marketing.video.badge": "The creative factory",
  "marketing.video.title": "Fresh creative takes more than one good idea",
  "marketing.video.body1":
    "Modern ad delivery has more options to choose from when you give it a steady supply of fresh creative. Scalephant can create finished video ads so that work does not turn into another item on your calendar.",
  "marketing.video.body2":
    "Each video includes on-screen text, a scene breakdown, a voiceover script and captions if you want to refine it. Recent videos are left alone, and refreshes follow your plan's usage limits.",
  "marketing.video.point.finished": "Finished videos, ready to use as they are",
  "marketing.video.point.captions": "On-screen text and captions included",
  "marketing.video.point.script": "A voiceover script comes with every video",
  "marketing.video.point.refresh": "Fresh creative when an active ad needs it",
  "marketing.safety.badge": "The money supervisor",
  "marketing.safety.title": "A money supervisor that knows your limits",
  "marketing.safety.lead":
    "Google and Meta can deliver the ads you give them. They do not know your comfort level with risk. Scalephant follows the limits you set and checks for waste before it keeps adding up.",
  "marketing.safety.cap.title": "Keep your spending cap in view",
  "marketing.safety.cap.body":
    "Actual spend is checked against the limit you set. If a campaign crosses it, Scalephant can pause it.",
  "marketing.safety.cpa.title": "Stop expensive campaigns from running unchecked",
  "marketing.safety.cpa.body":
    "If a campaign starts costing more per customer than you are comfortable with, it can be slowed or paused. You stay in control of turning it back on.",
  "marketing.safety.policy.title": "Avoid preventable launch surprises",
  "marketing.safety.policy.body":
    "Draft ads are checked against platform rules before launch, so you can address issues before they cause a delay.",
  "marketing.safety.closing":
    "You set the limits. Scalephant keeps the routine watch that is easy to put off when you are busy.",
  "marketing.features.title": "The ongoing ad work, handled for you",
  "marketing.features.lead":
    "The job is not only getting a campaign live. It is keeping ads fresh, spending sensible and lessons connected as the account changes.",
  "marketing.features.brain.title": "The cross-platform brain",
  "marketing.features.brain.forecast.title": "See your options before you spend",
  "marketing.features.brain.forecast.body":
    "Run the numbers first. Compare likely spend and cost per customer across a few budget options before a euro goes out.",
  "marketing.features.brain.portfolio.title": "Let both platforms inform the plan",
  "marketing.features.brain.portfolio.body":
    "Google and Meta are managed together, so Scalephant can move budget toward the channel bringing in customers for less.",
  "marketing.features.brain.market.title": "Keep an eye on your market",
  "marketing.features.brain.market.body":
    "Scalephant brings together market research, search trends and your own results to spot useful opportunities.",
  "marketing.features.brain.actions.title": "Turn what it learns into practical changes",
  "marketing.features.brain.actions.body":
    "Useful search terms, budget choices and message ideas turn into clear next steps based on what the account is seeing.",
  "marketing.features.brain.digest.title": "Know what happened without digging",
  "marketing.features.brain.digest.body":
    "A plain weekly summary shows what changed, what worked and what Scalephant is watching next.",
  "marketing.features.brain.memory.title": "Build on patterns from your own account",
  "marketing.features.brain.memory.body":
    "Each campaign leaves behind useful context for the next one, rather than making you start from zero every time.",
  "marketing.features.creative.title": "The creative factory",
  "marketing.features.creative.copy.title": "On-brand ads, ready when you need them",
  "marketing.features.creative.copy.body":
    "Headlines, ad copy and campaign ideas are written around your product and goal, so there is always something fresh to test.",
  "marketing.features.creative.voice.title": "Keep the voice recognisably yours",
  "marketing.features.creative.voice.body":
    "Scalephant uses your tone, calls to action and product strengths instead of handing you generic AI copy.",
  "marketing.features.creative.retargeting.title": "Bring people back with a clear story",
  "marketing.features.creative.retargeting.body":
    "When people have shown interest, Scalephant can prepare a sequence of ads that gives them a reason to come back.",
  "marketing.features.creative.video.title": "Have video ads ready to use",
  "marketing.features.creative.video.body":
    "Finished videos can arrive in your account with the script, captions and scene plan included when you want to refine them.",
  "marketing.features.creative.testing.title": "Give good ideas room to prove themselves",
  "marketing.features.creative.testing.body":
    "Scalephant can run several versions, watch their results and keep the stronger option in the mix.",
  "marketing.features.creative.fatigue.title": "Replace ads before they become wallpaper",
  "marketing.features.creative.fatigue.body":
    "Scalephant watches for ads that people have seen too often and makes room for something new.",
  "marketing.features.creative.offers.title": "Learn which offer people respond to",
  "marketing.features.creative.offers.body":
    "Test offers such as a trial, discount or guarantee, not just different words, and use what the account learns.",
  "marketing.features.money.title": "The money supervisor",
  "marketing.features.money.dayparting.title": "Put more budget behind useful times",
  "marketing.features.money.dayparting.body":
    "Scalephant looks at when customers tend to act and can put more of your budget behind those windows.",
  "marketing.features.money.negatives.title": "Stop paying for irrelevant searches",
  "marketing.features.money.negatives.body":
    "Searches that are clearly unrelated to what you sell are found and blocked, so they do not keep using your budget.",
  "marketing.features.money.guardrails.title": "Set spending guardrails that stay on",
  "marketing.features.money.guardrails.body":
    "If a campaign starts costing more than the limit you set, Scalephant can slow it down or pause it.",
  "marketing.features.money.anomaly.title": "Notice unusual spending sooner",
  "marketing.features.money.anomaly.body":
    "Sudden spending changes or a drop in results are checked promptly instead of waiting for the next report.",
  "marketing.features.money.pacing.title": "Spread your budget across the month",
  "marketing.features.money.pacing.body":
    "Your monthly budget is watched day by day, so it is less likely to disappear at the start of the month.",
  "marketing.features.money.frequency.title": "Avoid showing the same ad too often",
  "marketing.features.money.frequency.body":
    "Scalephant watches how often people see your ads and can pull back when the same audience is seeing too much.",
  "marketing.features.money.policy.title": "Catch policy problems before launch",
  "marketing.features.money.policy.body":
    "Draft ads are checked against platform rules, so issues can be fixed before they get in the way of a launch.",
  "marketing.features.money.housekeeping.title": "Keep doing the important housekeeping",
  "marketing.features.money.housekeeping.body":
    "Budget changes, ad refreshes and campaign adjustments are lined up and handled without making you live in an ad dashboard.",
  "marketing.features.picture.title": "One clear picture",
  "marketing.features.picture.attribution.title": "See where customers came from",
  "marketing.features.picture.attribution.body":
    "Campaigns and customer actions are connected, giving you a clearer view of what each channel is contributing.",
  "marketing.features.picture.journey.title": "See the customer journey in one place",
  "marketing.features.picture.journey.body":
    "Plan and review the ads that introduce, explain and sell your product without hopping between platforms.",
  "marketing.features.picture.types.title": "Use Google's campaign options with context",
  "marketing.features.picture.types.body":
    "When it makes sense to look at another Google campaign type, Scalephant helps you weigh the choice before you start.",
  "marketing.features.picture.lessons.title": "Carry lessons into the next campaign",
  "marketing.features.picture.lessons.body":
    "The useful things your account learns, from messages to audiences and formats, are available when the next campaign is prepared.",
  "marketing.features.picture.oneplace.title": "One place for Google and Meta",
  "marketing.features.picture.oneplace.body":
    "Start from one campaign brief and review both platforms from one dashboard, without stitching reports together.",
  "marketing.features.picture.value.title": "Remember what a customer is worth",
  "marketing.features.picture.value.body":
    "Add your average order value or subscription price so decisions can consider more than a raw count of clicks.",
  "marketing.cross.badge": "The cross-platform brain",
  "marketing.cross.title": "What Google teaches you can help Meta. And the other way around.",
  "marketing.cross.body1":
    "A message, audience or creative format that proves useful on one platform should not stay trapped there. Scalephant carries useful lessons across your campaigns and between Google and Meta when planning what comes next.",
  "marketing.cross.body2":
    "You get one connected picture instead of two separate accounts asking for your attention.",
  "marketing.final.title": "Give your marketing an always-on team.",
  "marketing.final.body":
    "Start your {days}-day full-autopilot trial and put Scalephant to work across Google and Meta.",
  "marketing.final.cta": "Start free",
  "marketing.final.fineprint":
    "No card needed. Free accounts include limited use of AI-generated ads and videos.",
  "pricing.title": "Simple, predictable pricing.",
  "pricing.lead":
    "Pay for the campaigns you run. Autopilot optimisation is included, and each plan shows its full creative bundle.",
  "pricing.perMonth": "/ month",
  "pricing.forever": "forever",
  "pricing.popular": "Most popular",
  "pricing.plan.free.description": "Perfect to see how the autopilot works.",
  "pricing.plan.starter.description": "For side projects and small experiments.",
  "pricing.plan.growth.description": "For serious solo operators looking to scale.",
  "pricing.plan.scale.description": "High-volume growth for ambitious advertisers.",
  "pricing.plan.scale_50.description": "Maximum volume for fast-growing products.",
  "pricing.plan.concepts": "Up to {concepts} headline and message angles per campaign",
  "pricing.plan.media.one": "{images} ad images and one video per campaign",
  "pricing.plan.media.many": "{images} ad images and {videos} videos per campaign",
  "pricing.plan.media.imagesOnly": "{images} ad images per campaign",
  "pricing.plan.free.noCard": "No credit card required, ever",
  "pricing.cta.choose": "Get {plan}",
  "pricing.custom.question": "Running more than 50 campaigns?",
  "pricing.custom.contact": "Contact us",
  "pricing.custom.rest": "for a custom high-volume plan tailored to your needs.",
  "pricing.included.title": "What every paid plan includes",
  "pricing.included.lead": "No per-action pricing. One subscription covers it all.",
  "pricing.included.planning": "Product analysis and campaign planning",
  "pricing.included.creatives": "Ad copy and creative generation",
  "pricing.included.forecasts": "Forecasts and retargeting analysis",
  "pricing.included.video": "Video ad scripts and rendering",
  "pricing.included.autopilot":
    "Autopilot scheduler jobs: budget shifts, anomaly detection, dayparting and fatigue checks",
  "pricing.fairUse":
    "Fair use: manual AI generation, such as extra creatives, forecasts and retargeting analysis, is covered by a generous monthly allowance on every paid plan, and normal use never reaches it. Launch work and work you ask for take priority. Autopilot optimisation does not count toward fair use.",
  "pricing.fairUse.link": "Read the fair-use terms",
  "status.page.title": "Service status",
  "status.page.lead":
    "A live check against the Scalephant API. Quote the details below if you contact support.",
  "status.title": "API status",
  "status.ok": "The API is answering normally.",
  "status.checking": "Checking the API…",
  "status.unavailable": "The API did not answer: {detail}",
  "status.noCorrelationId": "no correlation id",
  "status.service": "Service",
  "status.environment": "Environment",
  "status.version": "Version",
  "status.versionDev": "development build",
  "status.serverTime": "Server time",
  "settings.tracking.title": "Conversion tracking",
  "settings.tracking.backToCampaign": "Back to your campaign",
  "tracking.copied": "Copied",
  "tracking.paths.title": "Choose how to report conversions",
  "tracking.paths.intro":
    "Use a thank-you page in the browser, or send confirmed purchases and leads from your server. You do not need both.",
  "tracking.paths.browser": "Use a thank-you page",
  "tracking.paths.browserInstead": "Use a thank-you page instead",
  "tracking.paths.server": "Use server-side tracking",
  "tracking.paths.detectedServer":
    "Server-side tracking is active. The browser snippet and thank-you rule are optional; no extra setup is required to count your server conversions.",
  "tracking.checklist.serverTitle": "Server-side tracking",
  "tracking.checklist.serverEvent": "Send a confirmed conversion from your server",
  "tracking.checklist.serverEvent.todo":
    "Create a tracking-only key below and call the endpoint from your backend after a real purchase or lead.",
  "tracking.checklist.serverEvent.done":
    "A server event has arrived. No browser snippet or thank-you rule is needed for this path.",
  "tracking.checklist.serverEvent.waiting":
    "Your key is ready. Waiting for the first event from your server. Nothing else is needed.",
  "tracking.checklist.serverWaiting":
    "A server event has arrived, but no confirmed conversion is counted yet. Check the recent conversions below.",
  "tracking.checklist.browserWaiting":
    "Server conversions are counted, but a browser conversion has not been confirmed for this thank-you-page path yet.",
  "tracking.checklist.title": "Set up in three steps",
  "tracking.checklist.done": "Done",
  "tracking.checklist.pending": "To do",
  "tracking.checklist.step1": "Install the snippet on your site",
  "tracking.checklist.step1.todo":
    "Paste the two lines below into your site's <head>. Nothing has arrived yet.",
  "tracking.checklist.step1.done": "Your site is reporting. Last page view {when}.",
  "tracking.checklist.step2": "Say what a conversion is",
  "tracking.checklist.step2.todo":
    "Name the page visitors reach after converting, pick a detected signal, or report from your server.",
  "tracking.checklist.step2.done": "A conversion rule is in place.",
  "tracking.checklist.step2.viaServer":
    "Conversions are arriving from your server — no rule needed.",
  "tracking.checklist.step3": "See the first conversion arrive",
  "tracking.status.nothing": "No page view has arrived yet.",
  "tracking.status.pageviews_only":
    "Your site is reporting page views, but no conversion has been confirmed.",
  "tracking.status.signals_only":
    "Activity is arriving, but nothing is confirmed as a conversion yet — confirm a rule or report from your server.",
  "tracking.status.rule_waiting": "Your rule is confirmed. Waiting for the first conversion.",
  "tracking.status.counting": "Conversions are being counted. Nothing else to set up.",
  "tracking.install.title": "Install the snippet",
  "tracking.install.intro":
    "Copy this public snippet into the <head> of every page, including the thank-you page. It records page views; it is not a secret API key. Visit your site afterward to check the last-seen time below.",
  "tracking.install.copy": "Copy snippet",
  "tracking.install.lastSeen": "Last seen {when}",
  "tracking.install.siteHost": "Your site's domain",
  "tracking.install.siteHost.hint": "Auto-detection only trusts signals from this domain.",
  "tracking.originHint.mismatch":
    "Signals are arriving from {observedHost}, but the registered site is {registeredHost}. Register the domain the snippet actually runs on.",
  "tracking.originHint.noSite":
    "Signals are arriving from {observedHost}. Register that domain so they can be trusted.",
  "tracking.originHint.missingHeader":
    "Signals are arriving without a browser origin, which usually means something other than a browser is sending them.",
  "tracking.serverAuthority.quiet":
    "Your server has not reported a conversion for two days. If it should have, check the integration.",
  "tracking.rules.title": "What counts as a conversion",
  "tracking.rules.intro":
    "After installing the public snippet, add the page visitors reach only after a successful purchase, signup or lead, such as /thank-you. Choose what it counts as. Test a completed conversion and check Recent conversions below. A guessed rule must be confirmed; form submissions can count failed attempts.",
  "tracking.rules.empty": "No rule yet.",
  "tracking.rules.confirm": "Confirm",
  "tracking.rules.delete": "Remove",
  "tracking.rule.urlSentence": "Visits to {match} count as {type}s",
  "tracking.rule.formSentence": "Submissions of the form on {match} count as {type}s",
  "tracking.provenance.manual": "Yours",
  "tracking.provenance.confirmed": "Confirmed",
  "tracking.provenance.auto_promoted": "Guessed",
  "tracking.thankYou.label": "Page visitors see after converting",
  "tracking.thankYou.labelAnother": "Add another page visitors see after converting",
  "tracking.thankYou.hint": "A path such as /thank-you, or the full address.",
  "tracking.thankYou.add": "Add rule",
  "tracking.thankYou.error.empty": "Enter the page visitors see after converting, e.g. /thank-you.",
  "tracking.thankYou.error.invalid_url":
    "That does not look like a valid address — enter just the path, e.g. /thank-you.",
  "tracking.thankYou.error.homepage":
    "Use a specific page — the homepage would count every visitor as a conversion.",
  "tracking.eventType.label": "Counts as",
  "tracking.eventType.choose": "Choose…",
  "tracking.eventType.missing": "Choose what the page counts as.",
  "tracking.eventType.purchase": "Purchase",
  "tracking.eventType.lead": "Lead",
  "tracking.eventType.signup": "Signup",
  "tracking.candidates.title": "Detected on your site",
  "tracking.candidates.intro":
    "While no rule exists, the snippet notes pages reached, forms submitted and buttons clicked — paths and button text only, never what was typed.",
  "tracking.candidates.empty":
    "Nothing detected yet. Signals appear here as visitors use your site.",
  "tracking.candidates.seen": "seen {count} times",
  "tracking.candidates.suggestedHigh": "Probably this",
  "tracking.candidates.suggestedMedium": "Possibly this",
  "tracking.candidates.use": "Use this",
  "tracking.candidates.clickNoRule": "clicks cannot become rules yet",
  "tracking.candidate.url": "Visitors reach {match}",
  "tracking.candidate.form": "Someone submits the form on {match}",
  "tracking.candidate.click": 'Clicks on "{label}"',
  "tracking.stuck.title": "Auto-detection cannot finish on this site",
  "tracking.stuck.no_success_page":
    "For {days} days and {sightings} signals we looked for a page visitors only reach after converting — a thank-you or confirmation page. Your site does not seem to have one, so nothing qualified.",
  "tracking.stuck.success_page_unverified":
    "A page that looks like a confirmation page was seen, but not often enough from your registered domain to be picked without asking.",
  "tracking.stuck.why.click": "button clicks cannot become rules",
  "tracking.stuck.why.ordinary_page":
    "looks like an ordinary page, not one reached after converting",
  "tracking.stuck.why.unverified": "not seen often enough from your registered domain",
  "tracking.stuck.why.form_attempts": "a form submission counts attempts, not completions",
  "tracking.stuck.why.not_a_success_page": "does not look like a confirmation page",
  "tracking.stuck.serverSide":
    "The reliable way out: report conversions from your server with a key from the section below.",
  "tracking.stuck.formRule": "Or count every submission of the form on {match}:",
  "tracking.stuck.formRule.accept": "Count form submissions",
  "tracking.stuck.formRule.caveat": "Counts attempts that fail or are abandoned afterwards too.",
  "tracking.keys.title": "Report from your server",
  "tracking.keys.intro":
    "Your backend or payment webhook can report confirmed conversions directly. This path needs no browser snippet or thank-you rule. A server event alone is not proof that a conversion was counted; check Recent conversions below.",
  "tracking.keys.safety":
    "The tracking API key is secret. Never put this key in your browser, site HTML, snippet, or client-side code. Keep it in your server's environment or secret store.",
  "tracking.keys.scopeOnly":
    "This integration key has only the tracking:write scope; it cannot access agent features.",
  "tracking.keys.eventIdCaveat":
    "Use a stable unique eventId from the real order or lead. Reuse the same ID for retries of that event to avoid duplicates; a different ID for the same conversion can count twice. Replace the example ID and amount before sending.",
  "tracking.keys.visitorHint":
    "Send visitorId as well, so the conversion is credited to the ad that brought the visitor. Scalephant adds it to your site's forms as the hidden field scalephant_visitor_id.",
  "tracking.keys.shownOnce": "Your new key. It is shown this once — copy it now.",
  "tracking.keys.copy": "Copy key",
  "tracking.keys.empty": "No server-side key yet.",
  "tracking.keys.waiting": "Waiting for the first event from your server…",
  "tracking.keys.lastReceived": "Last conversion received {when}",
  "tracking.keys.column.name": "Name",
  "tracking.keys.column.prefix": "Key",
  "tracking.keys.column.lastUsed": "Last used",
  "tracking.keys.column.actions": "Actions",
  "tracking.keys.revoke": "Revoke",
  "tracking.keys.name": "Name for the key",
  "tracking.keys.defaultName": "Server integration",
  "tracking.keys.create": "Create key",
  "tracking.keys.roleNeeded": "Only an owner or admin can create or revoke keys.",
  "tracking.keys.sampleIntro": "Then, whenever your server confirms a conversion:",
  "tracking.pixel.title": "Meta pixel",
  "tracking.pixel.intro":
    "The snippet loads this pixel on your site and sends it page views and confirmed conversions, so Meta's optimiser learns from real visits and results. Checked daily.",
  "tracking.pixel.none":
    "No pixel selected. Confirmed conversions are still counted here; Meta's browser pixel does not fire.",
  "tracking.pixel.lastEvent": "Last fired {when}",
  "tracking.pixel.clear": "Stop using",
  "tracking.pixel.connectAccount": "Connect Meta account",
  "tracking.pixel.return": "Return to pixel selection",
  "tracking.pixel.accountConnected":
    "Meta account connected. Pixel selection and event verification are separate.",
  "tracking.pixel.accountMissing": "No connected Meta account.",
  "tracking.pixel.conflict":
    "Your site already runs this Meta pixel, so Scalephant sends conversions to Meta from its server only.",
  "tracking.consent.bannerMissing":
    "Your site has no cookie consent banner. Visitors from Europe are not tracked and their conversions do not reach your ad platforms until you add one. Scalephant reads Google Consent Mode, IAB TCF, Cookiebot and CookieYes.",
  "tracking.pixel.empty":
    "No pixels were found on your connected Meta accounts. Check pixel access in Meta Business settings.",
  "tracking.pixel.accounts": "Advertising accounts",
  "tracking.pixel.retry": "Retry pixel list",
  "tracking.pixel.choose": "Choose a pixel",
  "tracking.pixel.select": "Pixel",
  "tracking.pixel.selectPlaceholder": "Select…",
  "tracking.pixel.connectFirst": "Connect a Meta advertising account first:",
  "tracking.pixel.status.ok": "Reporting",
  "tracking.pixel.status.never_fired": "Never fired",
  "tracking.pixel.status.stale": "Gone quiet",
  "tracking.pixel.status.waiting": "Waiting for a conversion",
  "tracking.pixel.status.missing": "Not found",
  "tracking.pixel.status.unavailable": "Unavailable",
  "tracking.pixel.advice.never_fired":
    "This pixel has not fired yet. Open your site once and the pixel wakes up.",
  "tracking.pixel.advice.stale":
    "Conversions have come in since this pixel last fired, but Meta did not see them. Check that the snippet is still installed.",
  "tracking.pixel.advice.waiting":
    "No conversions since this pixel last fired, so it has had nothing to send. If a conversion does not reach Meta, it shows here.",
  "tracking.pixel.advice.missing":
    "This pixel is no longer on any connected Meta account. Choose another.",
  "tracking.pixel.advice.unavailable":
    "Meta reports this pixel as unavailable — check Events Manager.",
  "tracking.conversions.title": "Recent conversions",
  "tracking.conversions.summary":
    "{conversions} in the last 30 days, {confirmed} confirmed, {unattributed} not yet tied to a campaign.",
  "tracking.conversions.empty": "No conversions in the last 30 days.",
  "tracking.conversions.count": "{count} conversions",
  "tracking.conversions.column.when": "When",
  "tracking.conversions.column.type": "Type",
  "tracking.conversions.column.source": "Source",
  "tracking.conversions.column.value": "Value",
  "tracking.conversions.column.status": "Status",
  "tracking.conversions.suppressed": "Server reported",
  "tracking.conversions.confirmed": "Confirmed",
  "tracking.conversions.signal": "Signal",
  "tracking.source.snippet": "Site",
  "tracking.source.server": "Server",
  "nav.insights": "Insights",
  "nav.audiences": "Audiences",
  "dashboard.autopilot.on":
    "The autopilot is on. It tests copy, moves budget within your limits and reports every action here.",
  "dashboard.autopilot.off": "The autopilot is switched off. Campaigns run as they are.",
  "dashboard.autopilot.settings": "Autopilot settings",
  "dashboard.feed.title": "This week",
  "dashboard.feed.summary": "{actions} actions by the autopilot, {alerts} things to look at.",
  "dashboard.insight.title": "Weekly report",
  "dashboard.insight.none":
    "The first report arrives after the first complete week with a running campaign.",
  "dashboard.insight.more": "All reports",
  "nav.main": "Main navigation",
  "brand.next": "Next",
  "dashboard.band.autopilotOn": "Autopilot is running",
  "dashboard.band.autopilotOff": "Autopilot is off",
  "dashboard.band.review": "See what needs you",
  "dashboard.autopilot.enabled": "On",
  "dashboard.autopilot.disabled": "Off",
  "dashboard.autopilot.waiting": "Waiting for a running campaign.",
  "dashboard.connect.title": "Connect an ad account",
  "dashboard.connect.text":
    "Choose where your campaigns run. Nothing spends until you launch a campaign.",
  "dashboard.brandVoice.tone": "Tone: {tone}",
  "dashboard.retargeting.title": "Retargeting",
  "dashboard.retargeting.progress": "{count} of {total} conversions",
  "dashboard.settings.title": "Workspace settings",
  "activity.title": "Activity",
  "activity.window": "Last {days} days",
  "activity.alertsOnly": "Only things to look at",
  "activity.empty": "Nothing has happened in this window.",
  "activity.by.system": "Autopilot",
  "activity.by.job": "Autopilot",
  "activity.by.user": "You",
  "activity.by.admin": "Staff",
  "activity.alert": "Look at this",
  "activity.kind.created": "Campaign created",
  "activity.kind.launched": "Launched",
  "activity.kind.launch_failed": "Launch failed",
  "activity.kind.paused": "Paused",
  "activity.kind.resumed": "Resumed",
  "activity.kind.budget_changed": "Budget changed",
  "activity.kind.targeting_changed": "Targeting changed",
  "activity.kind.copy_generated": "Ad copy written",
  "activity.kind.images_generated": "Images generated",
  "activity.kind.copy_pushed": "Copy published to the platform",
  "activity.kind.copy_push_failed": "Copy could not be published to the platform",
  "activity.kind.asset_attached": "Image attached",
  "activity.kind.metrics_synced": "Numbers synced",
  "activity.kind.guardrail_paused": "Paused by the cost guardrail",
  "activity.kind.guardrail_reinstated": "Resumed after the cost guardrail",
  "activity.kind.anomaly_detected": "Sharp change in the numbers",
  "activity.kind.cap_reached": "Monthly limit reached",
  "activity.kind.platform_rejected": "Rejected by the platform",
  "activity.kind.archived": "Archived",
  "activity.kind.unmanaged": "No longer managed here",
  "activity.kind.automation_paused": "Autopilot paused for this campaign",
  "activity.kind.automation_resumed": "Autopilot resumed for this campaign",
  "activity.kind.budget_rebalanced": "Budget rebalanced between platforms",
  "activity.kind.budget_shifted": "Budget moved to the better platform",
  "activity.kind.frequency_reduced": "Budget lowered: people saw the ad too often",
  "activity.kind.anomaly_corrected": "Budget corrected after a sharp change",
  "activity.kind.spend_change_expected": "Spend will change: budget was moved",
  "activity.kind.variants_generated": "New ad variants written",
  "activity.kind.ab_round_started": "A/B test round started",
  "activity.kind.ab_winner_selected": "A/B winner chosen",
  "activity.kind.ab_losers_paused": "Losing variants paused",
  "activity.kind.fatigue_detected": "The ad is wearing out",
  "activity.kind.creative_refreshed": "Fresh creatives queued",
  "activity.kind.claim_audit_flagged": "A claim needs checking",
  "activity.kind.claim_audit_resolved": "Claim check resolved",
  "activity.kind.search_terms_negated": "Wasteful search terms excluded",
  "activity.kind.keywords_added": "Searches that sold added as keywords",
  "activity.kind.keywords_paused": "Weak keywords paused",
  "activity.kind.schedule_applied": "Ad schedule applied",
  "activity.kind.schedule_reverted": "Ad schedule removed",
  "activity.kind.schedule_skipped": "Ad schedule skipped: not enough data",
  "activity.kind.goal_lead_promoted": "Goal moved up to leads",
  "activity.kind.goal_graduated": "Goal moved up to sales",
  "activity.kind.goal_graduation_volume_low": "Too few sales for the sales goal yet",
  "activity.kind.goal_graduation_reverted": "Goal moved back a step",
  "activity.kind.targeting_adjusted": "Targeting adjusted from the other platform's results",
  "activity.kind.intel_refreshed": "Competitive report refreshed",
  "activity.kind.intel_refresh_failed": "Competitive report failed",
  "activity.kind.suggestion_created": "New suggestions",
  "activity.kind.suggestion_applied": "Suggestion applied",
  "activity.kind.suggestion_dismissed": "Suggestion dismissed",
  "activity.kind.pmax_ready": "Ready for Performance Max",
  "activity.kind.video_script_generated": "Video script written",
  "activity.kind.video_render_started": "Video render started",
  "activity.kind.video_rendered": "Video rendered",
  "activity.kind.video_render_failed": "Video render failed",
  "activity.kind.video_scene_repaired": "A video scene was re-rendered",
  "activity.kind.video_attached": "Video attached to the campaign",
  "activity.kind.video_attach_failed": "Video could not be attached",
  "activity.kind.video_attach_skipped": "Video attachment skipped",
  "activity.kind.story_sequence_generated": "Story sequence written",
  "activity.kind.pmax_assets_attached": "Performance Max image slots filled",
  "activity.kind.pmax_assets_attach_failed": "Performance Max image slots could not be filled",
  "activity.kind.audience_created": "Retargeting audience created",
  "activity.kind.audience_synced": "Retargeting audience synced",
  "activity.kind.audience_sync_failed": "Retargeting audience could not be synced",
  "activity.kind.retargeting_layer_added": "Retargeting layer added",
  "activity.kind.retargeting_layer_skipped": "Retargeting layer skipped",
  "activity.kind.retargeting_layer_failed": "Retargeting layer could not be added",
  "activity.kind.retargeting_layer_confirmed": "Retargeting layer confirmed at the platform",
  "activity.kind.creative_draft_started": "Started writing the first ads",
  "activity.kind.creative_draft_finished": "The first ads are ready to review",
  "activity.kind.creative_draft_failed": "The first ads could not be written",
  "activity.kind.own_creatives_added": "Your own ads or images were added",
  "activity.kind.creative_source_changed": "Scalephant now keeps filling automatically",
  "activity.kind.sales_campaign_opened":
    "The autopilot opened a campaign for your goal; it runs instead of the click campaign",
  "activity.kind.sales_campaign_paused":
    "The goal campaign was paused, and the click campaign runs again.",
  "activity.kind.goal_campaign_kept":
    "The goal campaign did better than the click campaign in 28 days and stays.",
  "activity.kind.bidding_switched":
    "The autopilot switched this Google campaign to bid for your goal instead of clicks.",
  "activity.kind.bidding_returned":
    "This Google campaign bids for clicks again, because bidding for your goal did not do better.",
  "activity.kind.bidding_kept":
    "Bidding for your goal did better than bidding for clicks in 28 days and stays.",
  "activity.kind.pmax_launched":
    "Google started testing Performance Max ads with part of this campaign's budget. Your total budget did not change.",
  "activity.kind.pmax_kept":
    "Performance Max ads brought conversions at least as cheaply as search ads alone and stay.",
  "activity.kind.pmax_stopped":
    "Performance Max ads were stopped, and their budget came back to this campaign.",
  "activity.kind.keywords_expanded":
    "We added new search keywords because your ads were shown rarely.",
  "intel.sales.googleWaiting":
    "This campaign starts bidding for sales automatically once {threshold} sales have come in on your site in 30 days (now {count}).",
  "intel.sales.bidding":
    "This campaign bids for sales. It goes back to bidding for clicks if that does not do better within 28 days.",
  "intel.goal.signup.googleWaiting":
    "This campaign starts bidding for sign-ups automatically once {threshold} sign-ups have come in on your site in 30 days (now {count}).",
  "intel.goal.signup.bidding":
    "This campaign bids for sign-ups. It goes back to bidding for clicks if that does not do better within 28 days.",
  "intel.goal.lead.googleWaiting":
    "This campaign starts bidding for leads automatically once {threshold} leads have come in on your site in 30 days (now {count}).",
  "intel.goal.lead.bidding":
    "This campaign bids for leads. It goes back to bidding for clicks if that does not do better within 28 days.",
  "settings.autopilot.title": "Autopilot",
  "settings.autopilot.intro":
    "What the autopilot may do on its own. Its actions appear in the activity feed; spending limits guide its budget decisions.",
  "settings.autopilot.enabled": "Autopilot on",
  "settings.autopilot.enabledHint":
    "Off means every campaign runs exactly as it is; nothing is tested or moved.",
  "settings.autopilot.caps.title": "Spending ceilings",
  "settings.autopilot.caps.intro":
    "Limits for budget decisions above each campaign group's monthly target. Leave empty for no ceiling.",
  "settings.autopilot.currency": "Currency of the ceilings",
  "settings.autopilot.maxMonthly": "Monthly ceiling",
  "settings.autopilot.maxMonthlyHint":
    "If the ad platform does not confirm the pause, account spending may exceed this ceiling.",
  "settings.autopilot.maxDaily": "Daily ceiling",
  "settings.autopilot.requireApproval": "Budget increases need my approval",
  "settings.autopilot.requireApprovalHint":
    "Automatic increases become suggestions you accept; decreases still happen on their own.",
  "settings.autopilot.systems.title": "Systems",
  "settings.autopilot.systems.intro":
    "Each one can be switched off on its own. The master switch above overrides them all.",
  "settings.autopilot.system.rebalancing": "Rebalance budget between platforms",
  "settings.autopilot.system.arbitrage": "Move budget to the platform that converts cheaper",
  "settings.autopilot.system.frequency": "Lower budget when people see the ad too often",
  "settings.autopilot.system.abTesting": "Test ad copy variants (A/B)",
  "settings.autopilot.system.fatigueRefresh": "Refresh creatives that are wearing out",
  "settings.autopilot.system.dayparting": "Schedule ads to the hours that convert",
  "settings.autopilot.system.searchTerms": "Exclude wasteful search terms (Google)",
  "settings.autopilot.system.goalLadder": "Move the goal up as results come in",
  "settings.autopilot.system.competitiveIntel": "Competitive intelligence reports",
  "settings.autopilot.system.weeklyInsights": "Weekly reports",
  "settings.autopilot.system.retargeting": "Retargeting",
  "settings.autopilot.save": "Save autopilot settings",
  "settings.autopilot.roleNeeded": "Only an owner or admin can change these.",
  "settings.autopilot.error.currencyRequired": "Choose a currency for the ceilings.",
  "settings.autopilot.error.invalidAmount":
    "Enter a whole amount greater than zero, or leave it empty.",
  "campaigns.tab.overview": "Overview",
  "campaigns.tab.creatives": "Creatives",
  "campaigns.tab.intel": "Intel",
  "campaigns.tab.forecast": "Forecast",
  "campaigns.tab.policy": "Autopilot",
  "campaigns.tab.activity": "Activity",
  "campaigns.tab.video": "Video",
  "campaigns.tab.story": "Story",

  "videos.title": "Video creatives",
  "videos.intro":
    "A script is written while you watch. Rendering happens in the media service and takes minutes; the row says which attempt it is on.",
  "videos.none": "No video scripts yet.",
  "videos.generate": "Write a video script",
  "videos.render": "Render",
  "videos.queued": "Queued. The render takes a few minutes; this page updates when it finishes.",
  "videos.format": "{aspect}, about {seconds}s, {scenes} scenes",
  "videos.marked": "Marked as AI-generated in the file itself",
  "videos.attemptsLeft": "{left} render attempts left",
  "videos.status.pending": "Not rendered",
  "videos.status.rendering": "Rendering",
  "videos.status.done": "Rendered",
  "videos.status.failed": "Render failed",
  "videos.status.superseded": "Replaced",
  "videos.hosted.link": "Link a YouTube video",
  "videos.hosted.field": "YouTube video id",
  "videos.hosted.linked": "Linked to YouTube video {id}",
  "videos.hosted.invalid":
    "That is not a YouTube video id. Paste the eleven characters after v=, not the whole address.",

  "stories.title": "Story sequences",
  "stories.intro":
    "Three ads that tell one story: the problem to a cold audience, the solution to the people who watched, the close to the people who visited.",
  "stories.none": "No story sequences yet.",
  "stories.generate": "Write a story sequence",
  "stories.act": "Act {number}",
  "stories.stage.cold": "Cold audience",
  "stories.stage.video_viewers": "Watched act 1",
  "stories.stage.site_visitors": "Visited the site",
  "stories.targetingNotes": "How the targeting works",
  "stories.setupSteps": "How this audience is built",

  "audiences.title": "Retargeting audiences",
  "audiences.intro":
    "Scalephant adds retargeting to a campaign that is already running; it never creates a separate one.",
  "audiences.none":
    "No audiences yet. One is created when there are enough conversions to retarget on.",
  "audiences.source.website_visitors": "Website visitors",
  "audiences.source.customer_list": "Customer list",
  "audiences.source.lookalike": "Lookalike",
  "audiences.retention": "Looks back {days} days",
  "audiences.size": "About {size} people",
  "audiences.sizeUnknown": "Size not reported yet",
  "audiences.state.pending": "Waiting",
  "audiences.state.syncing": "Syncing",
  "audiences.state.synced": "Synced",
  "audiences.state.failed": "Failed",
  "audiences.ready": "Ready to retarget",
  "audiences.notReady.not_enough_conversions":
    "Not enough conversions yet. Retargeting starts once ten have been recorded.",
  "audiences.notReady.pixel_stale":
    "The Meta pixel has not been seen firing recently. Check the snippet on your site.",
  "audiences.conversions": "{count} conversions recorded",
  "audiences.layers": "Campaigns with a retargeting layer",
  "audiences.layer.pending": "Being confirmed at the platform",
  "audiences.layer.confirmed": "Live",

  "apiKeys.scopes": "What this key may do",
  "apiKeys.scope.tracking:write": "Send conversions",
  "apiKeys.scope.agent:read": "Read campaigns and numbers",
  "apiKeys.scope.agent:write": "Write drafts and copy (counts toward manual fair use)",
  "policy.title": "What the autopilot may do to this campaign",
  "policy.active":
    "The autopilot may optimize this campaign: test copy, move budget within the limits below, adjust targeting from evidence.",
  "policy.paused":
    "The autopilot leaves this campaign as it is. It keeps running; nothing is tested or moved.",
  "policy.pausedSince": "Paused since {when}.",
  "policy.pause": "Pause the autopilot for this campaign",
  "policy.resume": "Let the autopilot optimize again",
  "policy.unmanaged":
    "This campaign runs on an account that was disconnected. Nothing here can act on it.",
  "policy.group.title": "This campaign group",
  "policy.group.target": "Monthly target: {amount}",
  "policy.group.noTarget": "No monthly target is set for the group.",
  "policy.org.title": "Your organization's limits",
  "policy.org.disabled": "The autopilot is switched off for the whole organization.",
  "policy.org.monthlyCap": "Monthly ceiling: {amount}",
  "policy.org.dailyCap": "Daily ceiling: {amount}",
  "policy.org.noCaps": "No ceilings are set; each group's own target is the limit.",
  "policy.org.approval": "Budget increases wait for your approval.",
  "policy.org.settings": "Change the organization's autopilot settings",
  "policy.roleNeeded": "Only an owner or admin can change this.",
  "intel.report.title": "Competitive report",
  "intel.report.none":
    "No report yet. One is made after launch, then monthly, or when results dip.",
  "intel.report.generated": "Generated {when} ({reason}), industry: {industry}",
  "intel.report.reason.no_report": "first report",
  "intel.report.reason.monthly": "monthly",
  "intel.report.reason.post_launch": "after launch",
  "intel.report.reason.performance_dip": "results dipped",
  "intel.report.reason.manual": "on request",
  "intel.report.competitors": "Competitors",
  "intel.report.themes": "Messaging themes",
  "intel.report.formats": "Creative formats",
  "intel.report.opportunities": "Opportunities",
  "intel.report.basedOn": "Based on: {sources}",
  "intel.source.webResearch": "web research",
  "intel.source.platformSignals": "platform signals",
  "intel.source.performanceData": "this campaign's results",
  "intel.source.none": "no data yet",
  "intel.refresh": "Refresh the report now",
  "intel.refreshQueued":
    "Queued. The new report appears here in a few minutes. Requested refreshes count toward manual fair use; scheduled optimization does not.",
  "intel.rules.title": "Directions and advice",
  "intel.rules.none": "No rules from the report yet.",
  "intel.rules.dismiss": "Dismiss",
  "intel.rule.type.creative_direction": "Creative direction",
  "intel.rule.type.messaging_angle": "Messaging angle",
  "intel.rule.type.add_keywords": "Keywords to add",
  "intel.rule.type.add_negative_keywords": "Keywords to exclude",
  "intel.rule.type.budget_shift": "Budget shift",
  "intel.rule.directive": "Steers the next copy",
  "intel.rule.advisory": "Advice only",
  "intel.rule.tainted": "Built from customer search phrases; not used automatically",
  "intel.adjustments.title": "Targeting adjustments",
  "intel.adjustments.none": "No adjustments from the other platform's results yet.",
  "intel.adjustment.kind.google_demographic_focus": "Google: focus on the segment Meta converts",
  "intel.adjustment.kind.meta_geo_focus": "Meta: focus on the region Google converts",
  "intel.adjustment.kind.pmax_graduation": "Performance Max is worth trying",
  "intel.adjustment.status.proposed": "Proposed",
  "intel.adjustment.status.applying": "Applying",
  "intel.adjustment.status.applied": "Applied",
  "intel.adjustment.status.failed": "Failed",
  "intel.adjustment.status.dismissed": "Dismissed",
  "intel.adjustment.status.expired": "Expired",
  "intel.adjustment.dismiss": "Decline",
  "intel.adjustment.pmaxNote":
    "This is not a launch. No automatic transition is available until a verified publisher and monitoring flow is connected. Keep Search running; review the requirements before trying PMax in Google Ads.",
  "intel.adjustment.pmaxRecorded": "Decision recorded",
  "intel.adjustment.pmaxRecordedNote":
    "Launch not verified. This recorded decision does not prove a PMax campaign exists in Google Ads.",
  "intel.adjustment.pmaxConversions":
    "{count} of 30 qualified conversions needed in the last 30 days (unknown if not verified).",
  "intel.adjustment.pmaxDays": "{count} of 25 complete metric days needed in the last 30 days.",
  "intel.adjustment.pmaxTracking":
    "Conversion tracking is not verified here; confirm qualified conversions before transition.",
  "intel.adjustment.pmaxAssets":
    "PMax image assets are not confirmed ready; check approved images and landing page.",
  "intel.adjustment.pmaxBudgetUnknown":
    "Daily pilot budget not confirmed. Preserve a Search minimum and stay under the group monthly cap.",
  "intel.adjustment.pmaxBudget":
    "At least {amount} daily pilot budget estimated; preserve the Search minimum and group monthly cap.",
  "intel.suggestions.title": "Suggestions",
  "intel.suggestions.intro":
    "Nothing here happens until you accept it. Accepting runs the same checked change you could make yourself.",
  "intel.suggestions.none": "No open suggestions.",
  "intel.suggestion.kind.budget_increase": "Raise the daily budget",
  "intel.suggestion.kind.budget_decrease": "Lower the daily budget",
  "intel.suggestion.kind.creative_refresh": "Refresh the creatives",
  "intel.suggestion.kind.targeting_change": "Change the targeting",
  "intel.suggestion.kind.bid_adjustment": "Adjust the bidding",
  "intel.suggestion.status.new": "Open",
  "intel.suggestion.status.accepted": "Accepted",
  "intel.suggestion.status.applied": "Applied",
  "intel.suggestion.status.dismissed": "Dismissed",
  "intel.suggestion.status.expired": "Expired",
  "intel.suggestion.value": "Proposed daily budget: {amount}",
  "intel.suggestion.accept": "Accept and apply",
  "intel.suggestion.dismiss": "Dismiss",
  "intel.suggestion.adviceOnly": "Advice: nothing can apply this automatically.",
  "intel.suggestion.applied": "Applied. The change is in the activity feed.",
  "intel.suggestion.refused": "The change was not allowed: {reason}",
  "intel.suggestion.expires": "Open until {when}",
  "intel.ladder.title": "Goal ladder",
  "intel.ladder.rung.clicks": "Clicks",
  "intel.ladder.rung.leads": "Leads",
  "intel.ladder.rung.sales": "Sales",
  "intel.ladder.current": "Optimizing for: {rung}",
  "intel.ladder.explicit": "You chose this goal; the ladder leaves it alone.",
  "intel.ladder.auto": "The autopilot moves the goal up as real leads and sales come in.",
  "intel.ladder.promoted": "Moved up to leads {when}",
  "intel.ladder.graduated": "Moved up to sales {when}",
  "intel.ladder.reverted": "Moved back {when}",
  "intel.ladder.volumeLow": "Sales volume flagged low {when}",
  "intel.sales.waiting":
    "A sales campaign starts automatically once {threshold} sales have come in on your site in 30 days (now {count}).",
  "intel.sales.budgetTooSmall":
    "The daily budget is under what a campaign needs to learn, so no sales campaign is opened yet.",
  "intel.sales.running":
    "A sales campaign runs instead of this one. This one comes back if that one does not do better within 28 days.",
  "intel.sales.exhausted":
    "Two sales campaigns brought no sales, so the autopilot no longer opens one.",
  "intel.sales.isSales":
    "This sales campaign optimizes for purchases. The autopilot opened it once enough sales had come in.",
  "intel.goal.signup.waiting":
    "A sign-up campaign starts automatically once {threshold} sign-ups have come in on your site in 30 days (now {count}).",
  "intel.goal.signup.budgetTooSmall":
    "The daily budget is under what a campaign needs to learn, so no sign-up campaign is opened yet.",
  "intel.goal.signup.running":
    "A sign-up campaign runs instead of this one. This one comes back if that one does not do better within 28 days.",
  "intel.goal.signup.exhausted":
    "Two sign-up campaigns brought no sign-ups, so the autopilot no longer opens one.",
  "intel.goal.signup.isSales":
    "This campaign optimizes for sign-ups. The autopilot opened it once enough sign-ups had come in.",
  "intel.goal.lead.waiting":
    "A lead campaign starts automatically once {threshold} leads have come in on your site in 30 days (now {count}).",
  "intel.goal.lead.budgetTooSmall":
    "The daily budget is under what a campaign needs to learn, so no lead campaign is opened yet.",
  "intel.goal.lead.running":
    "A lead campaign runs instead of this one. This one comes back if that one does not do better within 28 days.",
  "intel.goal.lead.exhausted":
    "Two lead campaigns brought no leads, so the autopilot no longer opens one.",
  "intel.goal.lead.isSales":
    "This campaign optimizes for leads. The autopilot opened it once enough leads had come in.",
  "intel.goalSwitch.platformWaiting.meta":
    "Your site has enough conversions. The switch waits until Meta has received at least half of them (now {count} of {needed}).",
  "intel.goalSwitch.platformWaiting.google":
    "Your site has enough conversions. The switch waits until {needed} of them have come from your Google ads (now {count}).",
  "intel.goal.purchase.unmeasured": "Your goal is sales, but no page counts them yet.",
  "intel.goal.signup.unmeasured": "Your goal is sign-ups, but no page counts them yet.",
  "intel.goal.lead.unmeasured": "Your goal is leads, but no page counts them yet.",
  "intel.insights.title": "What the group's other platform learned",
  "intel.insights.none": "Nothing to share between platforms yet.",
  "intel.insights.kind.winning_angle": "Winning angle",
  "intel.insights.kind.customer_language": "Customers' own words",
  "intel.insights.kind.avoid_language": "Words to avoid",
  "intel.insights.kind.audience_segment": "Segment that converts",
  "intel.insights.kind.comparative_note": "Platform comparison",
  "intel.insights.kind.fatigue_theme": "Theme that wore out",
  "forecast.title": "If I change my budget, what should I expect?",
  "forecast.intro":
    "Grounded first on this campaign's own results, then on the platform's estimates, and only then on industry benchmarks. Anything not measured is a range, not a fact.",
  "forecast.budgets": "Monthly budgets to forecast, separated by commas",
  "forecast.budgetsHint": "In {currency}, whole amounts. Up to six.",
  "forecast.submit": "Forecast",
  "forecast.invalid": "Enter one to six whole amounts greater than zero.",
  "forecast.noCredits": "Manual usage limit reached. Try after renewal or contact support.",
  "forecast.unavailable": "Forecasting is not available on this server.",
  "forecast.latest": "Forecast from {when}",
  "forecast.none": "No forecast yet.",
  "forecast.grounding.measured": "Based on this campaign's measured results",
  "forecast.grounding.platform": "Uses the platform's audience and keyword estimates",
  "forecast.grounding.benchmarks": "Directional only: industry benchmarks, no data of your own yet",
  "forecast.column.budget": "Monthly budget",
  "forecast.column.impressions": "Impressions",
  "forecast.column.clicks": "Clicks",
  "forecast.column.conversions": "Conversions",
  "forecast.column.cpa": "Cost per conversion",
  "forecast.column.roas": "ROAS",
  "forecast.column.ctr": "CTR",
  "forecast.column.confidence": "Confidence",
  "forecast.confidence.high": "High",
  "forecast.confidence.medium": "Medium",
  "forecast.confidence.low": "Low",
  "forecast.methodology": "How it was made",
  "forecast.caveats": "What could change it",
  "forecast.assumptions": "Assumptions",
  "forecast.plan.title": "Getting to a positive return",
  "forecast.plan.minBudget": "Positive ROAS needs at least {amount} a month.",
  "forecast.plan.constraints": "Constraints",
  "forecast.plan.actions": "What Scalephant will do",
  "forecast.roleNeeded": "Only an owner or admin can ask for a forecast.",
  "insights.title": "Insights",
  "insights.intro":
    "Every week the autopilot writes down what happened, what it will do next, and what to watch.",
  "insights.weekly.title": "Weekly reports",
  "insights.weekly.none":
    "No reports yet. The first one covers the first complete week with a running campaign.",
  "insights.weekly.week": "Week {start} to {end}",
  "insights.weekly.quality.real_metrics": "From platform numbers",
  "insights.weekly.quality.tracked_only": "From your tracked conversions only",
  "insights.weekly.quality.no_data": "No numbers this week",
  "insights.weekly.highlights": "Highlights",
  "insights.weekly.warnings": "Watch out",
  "insights.weekly.planned": "Next",
  "insights.weekly.top": "Top campaign: {name}",
  "insights.brandVoice.title": "Brand voice",
  "insights.brandVoice.intro":
    "Extracted from your own ads, winners first, and used when new copy is written.",
  "insights.brandVoice.none": "Not analyzed yet.",
  "insights.brandVoice.analyze": "Analyze now",
  "insights.brandVoice.analyzed": "Analyzed {when} from {count} campaigns.",
  "insights.brandVoice.tone": "Tone: {tone}, {formality}, {humor}",
  "insights.brandVoice.valueProps": "Value propositions",
  "insights.brandVoice.ctas": "Calls to action",
  "insights.brandVoice.avoid": "Avoid",
  "insights.brandVoice.examples": "Example headlines",
  "insights.brandVoice.error.no_copy": "There is no ad copy to analyze yet.",
  "insights.brandVoice.error.autopilot_off": "The autopilot is switched off.",
  "insights.brandVoice.error.no_credits":
    "Manual usage limit reached. Try after renewal or contact support.",
  "insights.brandVoice.roleNeeded": "Only an owner or admin can run the analysis.",
  "admin.tab.organizations": "Organizations",
  "admin.tab.learning": "Learning brain",
  "admin.tab.aiUsage": "AI usage",
  "admin.tab.cost": "Cost supervision",
  "admin.tab.models": "Models",
  "admin.models.intro":
    "The text model each tier runs, the evaluations of candidates against it, and every plan's estimated margin. A candidate is approved only here, and only when no plan would fall below the margin floor.",
  "admin.models.tiers": "Tiers",
  "admin.models.tier.light": "Light",
  "admin.models.tier.content": "Content",
  "admin.models.tier.core": "Core (optimisation)",
  "admin.models.source.builtin": "Built-in",
  "admin.models.source.assigned": "Approved",
  "admin.models.previous": "previous: {model}",
  "admin.models.restore": "Restore previous",
  "admin.models.restored":
    "The tier is back on its previous model. Every service picks it up within a minute.",
  "admin.models.margins": "Margin per plan",
  "admin.models.margins.note":
    "Estimate on full use with list prices and fixed hosting and card fees; not bookkeeping. Floor {floor}.",
  "admin.models.belowFloor": "below the floor",
  "admin.models.evaluations": "Evaluations",
  "admin.models.evaluations.note":
    "Each run compares the candidate with the tier's model on fixed sample material; one run costs at most {cap}.",
  "admin.models.evaluations.none": "No evaluations yet.",
  "admin.models.automatic":
    "Candidates are proposed automatically. Every Monday the system reads OpenAI's model list and queues an evaluation when a cheaper or newer model fits a tier. When a model passes, you get an e-mail and only approve or reject it here.",
  "admin.models.against": "against {model} · {input} / {output} per million tokens ({source})",
  "admin.models.summary.quality":
    "Quality: the judge rated it as good as or better than the current model in {good} of {judged} examples ({need} needed).",
  "admin.models.summary.noAnswers":
    "Quality: it gave no usable answers, so nothing could be compared.",
  "admin.models.summary.rejections": "Unusable answers: {candidate} (current model {baseline}).",
  "admin.models.summary.cheaper": "Cost: {share} cheaper per task.",
  "admin.models.summary.dearer": "Cost: {share} more per task.",
  "admin.models.runCost": "Run cost {cost}",
  "admin.models.marginWith": "Margin with this model: {margins}",
  "admin.models.samples": "Examples side by side",
  "admin.models.approve": "Approve",
  "admin.models.approved": "Approved. Every service switches within a minute.",
  "admin.models.reject": "Reject",
  "admin.models.dismiss": "Clear from list",
  "admin.models.rejected": "Rejected.",
  "admin.models.blocked": "Blocked: the margin of {plans} would fall below 50 %.",
  "admin.models.status.queued": "Queued",
  "admin.models.status.running": "Running",
  "admin.models.status.passed": "Passed",
  "admin.models.status.failed": "Did not pass",
  "admin.models.status.error": "Error",
  "admin.models.status.approved": "Approved",
  "admin.models.status.rejected": "Rejected",
  "admin.models.status.superseded": "Superseded",
  "admin.models.column.tier": "Tier",
  "admin.models.column.model": "Model",
  "admin.models.column.price": "Input / output per million tokens",
  "admin.models.column.since": "In use since",
  "admin.models.column.plan": "Plan",
  "admin.models.column.priceMonth": "Price a month",
  "admin.models.column.cost": "Estimated cost",
  "admin.models.column.margin": "Margin",
  "admin.models.column.candidate": "Candidate",
  "admin.models.column.status": "Status",
  "admin.models.column.result": "Result",
  "admin.models.candidate.title": "Add a model by hand",
  "admin.models.candidate.intro":
    "Only needed for a model the weekly check has not proposed. Prices are from OpenAI's pricing page. The run starts within ten minutes.",
  "admin.models.candidate.model": "Model id",
  "admin.models.candidate.input":
    "Price of text sent to the model (input), dollars per million tokens",
  "admin.models.candidate.output":
    "Price of text the model writes (output), dollars per million tokens",
  "admin.models.candidate.context": "Context window, tokens",
  "admin.models.candidate.maxOutput": "Maximum output, tokens",
  "admin.models.candidate.priceSource": "Where the price is from",
  "admin.models.candidate.effort": "Takes a reasoning effort",
  "admin.models.candidate.submit": "Queue evaluation",
  "admin.models.candidate.queued": "Queued. The result appears here when the run is done.",
  "admin.models.candidate.open": "That model is already being evaluated for this tier.",
  "admin.tab.watchdog": "Watchdog",
  "admin.tab.launchFailures": "Launch failures",
  "admin.tab.queues": "Queues",
  "admin.stepUpRequired": "Recent authentication is required for this page.",
  "admin.learning.intro":
    "What the autopilot has learned across every organization: counts only. No learning's text ever crosses a tenant boundary.",
  "admin.learning.total": "{count} learnings",
  "admin.learning.highConfidence": "{count} high confidence",
  "admin.learning.organizations": "from {count} organizations",
  "admin.learning.byType": "By type",
  "admin.learning.byIndustry": "By industry",
  "admin.learning.byPlatform": "By platform",
  "admin.learning.none": "Nothing learned yet.",
  "admin.learning.type.creative_winner": "Creative winner",
  "admin.learning.type.fatigue_pattern": "Fatigue pattern",
  "admin.learning.type.anomaly_correction": "Anomaly correction",
  "admin.learning.type.budget_optimization": "Budget optimization",
  "admin.aiUsage.intro":
    "Credits settled per organization and feature. Model spend in dollars is a metric on /metrics, not stored here.",
  "admin.window": "Window",
  "admin.window.days": "Last {days} days",
  "admin.aiUsage.none": "No consumption in this window.",
  "admin.aiUsage.column.organization": "Organization",
  "admin.aiUsage.column.plan": "Plan",
  "admin.aiUsage.column.credits": "Credits",
  "admin.aiUsage.column.calls": "Calls",
  "admin.aiUsage.column.features": "By feature",
  "admin.aiUsage.feature": "{feature}: {credits} credits over {calls} calls",
  "admin.cost.intro":
    "Consumption against each plan's allowance and list price. Recognised revenue counts active subscriptions at list price.",
  "admin.cost.recognised": "Recognised monthly revenue: {amount}",
  "admin.cost.consumed": "Credits consumed: {count}",
  "admin.cost.column.organization": "Organization",
  "admin.cost.column.plan": "Plan",
  "admin.cost.column.price": "List price",
  "admin.cost.column.allowance": "Allowance",
  "admin.cost.column.consumed": "Consumed",
  "admin.cost.column.utilisation": "Utilisation",
  "admin.cost.none": "No organizations.",
  "admin.watchdog.intro":
    "The public model catalogue against the models in use, and a synthetic probe of every model every five minutes. Findings are findings; nothing changes a model on its own.",
  "admin.watchdog.findings": "Findings",
  "admin.watchdog.findings.none": "No findings.",
  "admin.watchdog.finding.deprecated_in_use": "A model in use is deprecated",
  "admin.watchdog.finding.newer_available": "A newer model is available",
  "admin.watchdog.finding.new_model": "A new model appeared",
  "admin.watchdog.finding.api_version_sunset": "A platform API version is sunsetting",
  "admin.watchdog.status.active": "Active",
  "admin.watchdog.status.resolved": "Resolved",
  "admin.watchdog.column.finding": "Finding",
  "admin.watchdog.column.model": "Model",
  "admin.watchdog.column.seen": "First seen / last seen",
  "admin.watchdog.column.alerted": "Alerted",
  "admin.watchdog.inUse": "in use: {model}",
  "admin.watchdog.health": "Model health",
  "admin.watchdog.health.none": "No models are being probed.",
  "admin.watchdog.state.healthy": "Healthy",
  "admin.watchdog.state.degraded": "Degraded",
  "admin.watchdog.state.unavailable": "Unavailable",
  "admin.watchdog.column.capability": "Capability",
  "admin.watchdog.column.state": "State",
  "admin.watchdog.column.failures": "Consecutive failures",
  "admin.watchdog.column.lastProbe": "Last probe / last success",
  "admin.watchdog.run": "Run the watchdog now",
  "admin.watchdog.probe": "Probe every model now",
  "admin.watchdog.queued": "Queued: {queue}. Results appear here when the worker is done.",
  "admin.launchFailures.intro":
    "Campaigns whose launch failed or stalled, across every organization, newest first.",
  "admin.launchFailures.none": "No failed launches.",
  "admin.launchFailures.column.campaign": "Campaign",
  "admin.launchFailures.column.organization": "Organization",
  "admin.launchFailures.column.status": "Status",
  "admin.launchFailures.column.attempts": "Attempts",
  "admin.launchFailures.column.error": "Error",
  "admin.launchFailures.column.when": "Last attempt",
  "admin.launchFailures.binding.pending": "binding pending",
  "admin.launchFailures.binding.live": "binding live",
  "admin.launchFailures.binding.failed": "binding failed",
  "admin.launchFailures.binding.none": "no binding",
  "admin.queues.intro":
    "Every queue with its policy, schedule and depth, from the job store. This stands in for the dashboard the plan named, which could not sit behind this gate.",
  "admin.queues.none": "No queues.",
  "admin.queues.noStore": "The api has no job store to ask; counts are unavailable.",
  "admin.queues.column.queue": "Queue",
  "admin.queues.column.schedule": "Schedule",
  "admin.queues.column.ready": "Ready",
  "admin.queues.column.active": "Active",
  "admin.queues.column.failed": "Failed",
  "admin.queues.column.deadLetter": "Dead letters",
  "admin.queues.column.oldest": "Oldest waiting",
  "admin.queues.column.lastFailure": "Last failure",
  "admin.queues.onDemand": "on demand",
  "admin.queues.retries": "{count} retries",
  // SP-022 — where the ads come from (docs/creative-source/implementation_plan.md).
  "source.title": "Where do your ads come from?",
  "source.intro":
    "Choose separately for the texts and the images. This decides what Scalephant writes, what you bring, and what the autopilot does later when ads wear out.",
  "source.copy": "Texts",
  "source.images": "Images",
  "source.option.ai": "Scalephant makes them",
  "source.option.upload": "I bring my own",
  "source.option.mixed": "Both",
  "source.option.ai.copy":
    "Scalephant writes the headlines, texts and calls to action from your product information, and writes new ones when the old ones wear out. You review them before anything is published.",
  "source.option.ai.images":
    "Scalephant draws the ad images from your product information, and new ones when the old ones wear out. You review them before anything is published.",
  "source.option.upload.copy":
    "You write your own ready-made ads: headline, text and call to action. Scalephant writes nothing on its own; it tells you when your ads wear out.",
  "source.option.upload.images":
    "You upload your own images and your logo. Scalephant draws nothing on its own; it tells you when your images wear out.",
  "source.option.mixed.copy":
    "You add your own ads and Scalephant writes the rest, using yours as a model of your tone.",
  "source.option.mixed.images":
    "You upload your own images and your logo, and Scalephant draws the rest.",
  "source.locked": "You cannot change this choice later.",
  "source.required": "Choose where both the texts and the images come from.",
  "source.value.ai": "Scalephant",
  "source.value.upload": "Your own",
  "source.value.mixed": "Your own and Scalephant",
  "source.summary": "Texts: {copy} · Images: {images}",
  "source.chosenAtSetup": "Chosen when the product was set up; it cannot be changed.",
  "onboarding.step.content": "Ad content",
  "onboarding.content.title": "What goes into your ads?",
  "onboarding.content.intro":
    "Decide whether Scalephant makes your ad texts and images, you bring your own, or both.",
  "onboarding.content.saveChoice": "Save choice",
  "onboarding.content.continue": "Continue",
  "onboarding.content.logoRequired":
    "Upload your logo before continuing: ads with your own images need it.",
  "onboarding.content.ownAdsHint":
    "You can add your own ads now, or later on each campaign's ads page.",
  "onboarding.content.ownImagesHint":
    "You can upload your own images now, or later on each campaign's ads page.",
  "logo.title": "Logo",
  "logo.hint":
    "A square image of at least 128 × 128 pixels. Google shows it with your Performance Max ads, and its colours guide your generated images.",
  "logo.found.text": "We found this logo on your website. Use it in your ads?",
  "logo.found.used": "Logo saved. Its colours now guide your generated images.",
  "logo.choose": "Choose a logo file",
  "logo.use": "Use this logo",
  "logo.change": "Change logo",
  "logo.current": "Your logo",
  "logo.preview": "Logo preview",
  "logo.uploading": "Uploading…",
  "upload.choose": "Choose images",
  "upload.hint":
    "JPEG, PNG, WebP or GIF, at most 25 MB and at least 300 pixels on the shorter side.",
  "upload.uploading": "Uploading {name}…",
  "upload.done": "{name} added.",
  "upload.error.type": "{name}: only JPEG, PNG, WebP and GIF images can be added.",
  "upload.error.size": "{name}: the file is larger than 25 MB.",
  "upload.error.file_signature_mismatch": "{name}: the file is not the image its name says it is.",
  "upload.error.unreadable_image": "{name}: the image could not be read.",
  "upload.error.image_too_small": "{name}: the image is too small to advertise with.",
  "upload.error.logo_not_square": "{name}: a logo must be square.",
  "upload.error.own_creative_limit": "{name}: there is no room for more of your own images here.",
  "upload.error.not_an_image": "{name}: only images can be added here.",
  "upload.error.generic": "{name}: the upload failed. Try again.",
  "upload.error.unavailable": "Uploading files is not available on this server right now.",
  "ownAds.title": "Your own ads",
  "ownAds.empty": "No own ads yet.",
  "ownAds.add": "Add ad",
  "ownAds.headline": "Headline",
  "ownAds.body": "Text",
  "ownAds.cta": "Call to action",
  "ownAds.counter": "{count}/{max}",
  "ownAds.counter.both": "{count} characters · Google allows {google}, Meta {meta}",
  "ownAds.tooLongFor": "Too long for {platform}; shorten it before it can be used there.",
  "ownAds.remove": "Remove",
  "ownAds.added": "Ad added.",
  "library.title": "Your own images",
  "library.empty": "No own images yet.",
  "library.remove": "Remove",
  "library.crop": "Adjust crop",
  "library.cropped": "Cropped",
  "library.preview": "Your own image",
  "crop.title": "Crop the image",
  "crop.intro":
    "Choose what each ad shape shows. Drag the frame, or use the buttons and the size slider.",
  "crop.ratio.1.91:1": "Landscape 1.91:1",
  "crop.ratio.1:1": "Square 1:1",
  "crop.ratio.4:5": "Portrait 4:5",
  "crop.size": "Frame size",
  "crop.left": "Move left",
  "crop.right": "Move right",
  "crop.up": "Move up",
  "crop.down": "Move down",
  "crop.auto": "Use automatic crop",
  "crop.save": "Save crop",
  "crop.saved": "Crop saved. Review the image again before launch.",
  "crop.loading": "Loading the image…",
  "imageConsent.title": "Your own images and AI",
  "imageConsent.disclosure":
    "Scalephant can use your own images as a model when it makes new ad images. You review the new images before anything is published.",
  "imageConsent.authorize": "Allow sending my own images to the image model",
  "imageConsent.ownerOnly": "Only an owner or admin can change this.",
  "imageConsent.saved": "Saved.",
  "creatives.origin.customer": "Your own",
  "creatives.origin.fromOwn": "AI, from your originals",
  "creatives.own.add": "Add your own ad",
  "creatives.own.cancel": "Cancel",
  "creatives.own.added": "Added. Your own ad counts as reviewed.",
  "creatives.own.queued": "Added. It goes live on the platform shortly.",
  "creatives.images.upload": "Upload your own images",
  "creatives.images.crop": "Adjust crop",
  "creatives.google.needs":
    "Google needs at least 3 different headlines and 2 different texts. Still missing: {headlines} headlines and {bodies} texts.",
  "creatives.google.ok": "There are enough headlines and texts for Google.",
  "creatives.fill.copy": "Fill texts with AI",
  "creatives.fill.images": "Fill images with AI",
  "creatives.fill.title": "Fill with AI",
  "creatives.fill.intro.copy":
    "Scalephant writes the texts that are missing, using your own ads as a model of your tone. You review them before anything is published. This uses one text batch.",
  "creatives.fill.intro.images":
    "Scalephant draws the images that are missing. With your permission it uses your own images as a reference. You review them before anything is published. The images count toward your plan's image allowance.",
  "creatives.fill.question": "Do you want Scalephant to keep filling automatically from now on?",
  "creatives.fill.yes": "Yes, keep filling automatically",
  "creatives.fill.yesHint":
    "From now on the autopilot makes new ads itself when the old ones wear out. We still tell you when your own ads wear out.",
  "creatives.fill.no": "No, just this once",
  "creatives.fill.noHint": "When your ads wear out, you get a notification and decide yourself.",
  "creatives.fill.confirm": "Fill now",
  "creatives.fill.cancel": "Cancel",
  "creatives.fill.chooseFirst": "Choose one of the options first.",
  "creatives.fill.done.copy": "{count} new texts are ready for you to review.",
  "creatives.fill.done.images": "{count} new images are being drawn and appear here shortly.",
  "creatives.fill.none": "Nothing was missing, so nothing new was made.",
  "creatives.fill.changed": "Scalephant now keeps filling automatically.",
  "creatives.draft.running": "Scalephant is writing your first ads. They appear here in a moment.",
  "creatives.draft.failed":
    "Scalephant could not write the first ads. You can write them with the buttons below.",
  "notifications.title": "Needs your attention",
  "notifications.fatigue.copy": "Your own texts in {campaign} are wearing out.",
  "notifications.fatigue.images": "Your own images in {campaign} are wearing out.",
  "notifications.fatigue.both": "Your own images and texts in {campaign} are wearing out.",
  "notifications.fatigue.ask":
    "Add new images, texts or calls to action, or let Scalephant write more from your originals. Nothing new is published before you review it.",
  "notifications.open": "Open the ads",
  "notifications.generate": "Write more from my originals",
  "notifications.dismiss": "Dismiss",
  "products.section.source": "Where the ads come from",
} as const;

export type MessageKey = keyof typeof en;

export const fi: Record<MessageKey, string> = {
  "products.ai.organizationChanged":
    "Aktiivinen organisaatio vaihtui. Lataa sivu uudelleen ja yritä uudestaan. Mitään ei tallennettu.",
  "app.name": "Scalephant",
  "app.tagline": "Markkinoinnin autopilotti itseohjautuville yrityksille.",
  "nav.home": "Etusivu",
  "nav.dashboard": "Kojelauta",
  "nav.campaigns": "Kampanjat",
  "nav.adAccounts": "Mainostilit",
  "nav.organizations": "Organisaatiot",
  "nav.settings": "Asetukset",
  "nav.signIn": "Kirjaudu",
  "nav.register": "Luo tili",
  "nav.signOut": "Kirjaudu ulos",
  "nav.language": "Kieli",
  "common.email": "Sähköposti",
  "common.password": "Salasana",
  "common.name": "Nimi",
  "common.save": "Tallenna",
  "common.cancel": "Peruuta",
  "common.continue": "Jatka",
  "common.loading": "Ladataan…",
  "common.saved": "Tallennettu.",
  "common.error.generic": "Jokin meni pieleen. Yritä uudelleen.",
  "common.error.network": "Palvelimeen ei saatu yhteyttä.",
  "common.error.unavailable":
    "Tätä ei ole vielä otettu käyttöön tällä palvelimella, joten uudelleenyritys ei auta. Kerro meille, niin laitamme sen päälle.",
  "common.error.rateLimited": "Liian monta yritystä. Yritä uudelleen {seconds} sekunnin kuluttua.",
  "validation.required": "Pakollinen.",
  "validation.email_invalid": "Anna kelvollinen sähköpostiosoite.",
  "validation.password_too_short": "Käytä vähintään {min} merkkiä.",
  "validation.password_too_long": "Käytä enintään {max} merkkiä.",
  "validation.passwords_differ": "Salasanat eivät täsmää.",
  "validation.code_invalid": "Anna 6-numeroinen koodi.",
  "auth.login.title": "Kirjaudu sisään",
  "auth.login.rememberMe": "Pidä minut kirjautuneena",
  "auth.login.submit": "Kirjaudu",
  "auth.login.forgot": "Unohtuiko salasana?",
  "auth.login.noAccount": "Eikö sinulla ole vielä tiliä?",
  "auth.login.google": "Jatka Googlella",
  "auth.login.error.invalid": "Väärä sähköposti tai salasana.",
  "auth.login.error.unverified": "Vahvista ensin sähköpostiosoitteesi. Lähetimme uuden linkin.",
  "auth.register.title": "Luo tili",
  "auth.register.submit": "Luo tili",
  "auth.register.haveAccount": "Onko sinulla jo tili?",
  "auth.register.locale": "Kieli",
  "auth.register.company": "Yritys",
  "auth.register.terms.before": "Luomalla tilin hyväksyt ",
  "auth.register.terms.terms": "käyttöehdot",
  "auth.register.terms.between": " ja ",
  "auth.register.terms.privacy": "tietosuojaselosteen",
  "auth.register.terms.after": ".",
  "auth.register.error.exists": "Sähköpostiosoite on jo rekisteröity.",
  "auth.register.error.existsLink": "Lähetä uusi vahvistuslinkki",
  "auth.verify.title": "Tarkista sähköpostisi",
  "auth.verify.body":
    "Lähetimme vahvistuslinkin osoitteeseen {email}. Avaa se, niin tilisi on valmis.",
  "auth.verify.bodyUnknown":
    "Anna osoite jolla rekisteröidyit, niin lähetämme uuden vahvistuslinkin.",
  "auth.verify.resend": "Lähetä linkki uudelleen",
  "auth.verify.resent": "Uusi linkki on matkalla.",
  "auth.verify.done": "Sähköpostiosoitteesi on vahvistettu.",
  "auth.twoFactor.title": "Kaksivaiheinen tunnistus",
  "auth.twoFactor.body": "Anna todennussovelluksen näyttämä 6-numeroinen koodi.",
  "auth.twoFactor.code": "Koodi",
  "auth.twoFactor.trustDevice": "Luota tähän laitteeseen 30 päivää",
  "auth.twoFactor.submit": "Vahvista",
  "auth.twoFactor.useBackup": "Käytä varakoodia",
  "auth.twoFactor.useTotp": "Käytä todennussovellusta",
  "auth.twoFactor.backupCode": "Varakoodi",
  "auth.twoFactor.error.invalid": "Koodi ei kelpaa.",
  "auth.twoFactor.error.locked": "Liian monta väärää koodia. Yritä myöhemmin uudelleen.",
  "auth.twoFactor.email.body":
    "Lähetimme 6-numeroisen koodin sähköpostiisi. Se on voimassa 5 minuuttia.",
  "auth.twoFactor.email.resend": "Lähetä uusi koodi",
  "auth.twoFactor.email.resent": "Uusi koodi on matkalla.",
  "auth.twoFactor.useEmail": "Lähetä koodi sähköpostiin",
  "auth.forgot.title": "Vaihda salasana",
  "auth.forgot.body": "Anna sähköpostiosoitteesi, niin lähetämme linkin uuden salasanan valintaan.",
  "auth.forgot.submit": "Lähetä linkki",
  "auth.forgot.sent": "Jos osoitteella on tili, linkki on matkalla.",
  "auth.forgot.back": "Takaisin kirjautumiseen",
  "auth.reset.title": "Valitse uusi salasana",
  "auth.reset.newPassword": "Uusi salasana",
  "auth.reset.confirm": "Toista uusi salasana",
  "auth.reset.submit": "Vaihda salasana",
  "auth.reset.done": "Salasana vaihdettiin. Kirjaudu sisään uudella salasanalla.",
  "auth.reset.error.invalidToken": "Linkki ei ole enää voimassa. Pyydä uusi.",
  "auth.error.title": "Kirjautumisongelma",
  "auth.error.body": "Kirjautumista ei voitu viedä loppuun ({code}). Yritä uudelleen.",
  "platform.meta": "Meta",
  "platform.google": "Google",
  "adAccounts.title": "Mainostilit",
  "adAccounts.intro":
    "Näillä tileillä Scalephantilla on oikeus toimia. Omistajuus pysyy sinulla. Tilin yhdistäminen ei vielä mainosta sillä: jokainen kampanja valitsee omat tilinsä, eikä mitään kuluteta ennen kuin julkaiset sen.",
  "adAccounts.empty": "Yhtään mainostiliä ei ole vielä yhdistetty.",
  "adAccounts.noneConfigured": "Tälle palvelimelle ei ole vielä määritetty yhtään mainosalustaa.",
  "adAccounts.connect": "Yhdistä {platform}",
  "adAccounts.add": "Lisää {platform}-mainostili",
  "adAccounts.reconnect": "Yhdistä uudelleen",
  "adAccounts.disconnect": "Katkaise yhteys",
  "adAccounts.column.account": "Tili",
  "adAccounts.column.status": "Tila",
  "adAccounts.column.checked": "Tarkistettu",
  "adAccounts.column.actions": "Toiminnot",
  "adAccounts.status.connected": "Yhdistetty",
  "adAccounts.status.pending": "Yhdistetään",
  "adAccounts.status.disconnected": "Yhteys katkaistu",
  "adAccounts.status.error": "Vaatii huomiota",
  "adAccounts.reason.tokenUnreadable":
    "Emme saa enää luettua tämän tilin tallennettua pääsyä. Yhdistä tili uudelleen.",
  "adAccounts.reason.reauthorize": "Alusta ei enää hyväksy pääsyämme. Yhdistä tili uudelleen.",
  "adAccounts.reason.noPaymentMethod":
    "Alustan mukaan tämä tili ei voi kuluttaa. Tarkista sen maksutapa.",
  "adAccounts.reason.permission":
    "Alusta esti pääsyn tähän tiliin. Yhdistä tili uudelleen tunnuksella, jolla on oikeus hallita sitä.",
  "adAccounts.reason.disconnected": "Katkaisit yhteyden tähän tiliin.",
  "adAccounts.reason.unknown":
    "Alusta ilmoitti ongelmasta. Uudelleenyhdistäminen yleensä korjaa sen.",
  "adAccounts.disconnectTitle": "Katkaistaanko yhteys tähän tiliin?",
  "adAccounts.disconnectWarning":
    "Jo käynnissä olevat kampanjat jäävät käyntiin alustalle, ja Scalephant lakkaa hallinnoimasta niitä. Historia säilyy.",
  "adAccounts.error.notConfigured": "{platform} ei ole määritetty tälle palvelimelle.",
  "campaigns.title": "Kampanjat",
  "campaigns.new": "Uusi kampanja",
  "campaigns.empty": "Ei vielä kampanjoita.",
  "campaigns.connectFirst": "Yhdistä mainostili päästäksesi alkuun.",
  "campaigns.unknownAccount": "Tuntematon tili",
  "campaigns.column.campaign": "Kampanja",
  "campaigns.column.account": "Tili",
  "campaigns.column.status": "Tila",
  "campaigns.status.draft": "Luonnos",
  "campaigns.status.pendingApproval": "Julkaistaan",
  "campaigns.status.active": "Käynnissä",
  "campaigns.status.paused": "Tauolla",
  "campaigns.status.error": "Vaatii huomiota",
  "campaigns.status.completed": "Päättynyt",
  "campaigns.status.unmanaged": "Ei enää hallinnassa",
  "campaigns.status.unknown": "Tuntematon",
  "campaigns.pausedBy.user": "Pysäytit tämän itse.",
  "campaigns.pausedBy.cpaGuardrail": "Tauolla: asiakashankinta maksoi liikaa.",
  "campaigns.pausedBy.zeroConversions": "Tauolla: kulutti tuottamatta yhtään konversiota.",
  "campaigns.pausedBy.monthlyCap": "Tauolla: tämä ryhmä on kuluttanut kuukautensa.",
  "campaigns.pausedBy.anomaly": "Tauolla: luvut muuttuivat jyrkästi.",
  "campaigns.pausedBy.platformRejected": "Tauolla: alusta hylkäsi sen.",
  "campaigns.pausedBy.accountDisconnected": "Tauolla: sen mainostilin yhteys katkaistiin.",
  "campaigns.pausedBy.noSales":
    "Tauolla: ei konversioita 14 päivässä. Klikkikampanja on taas käynnissä.",
  "campaigns.pausedBy.goalChanged":
    "Tauolla: valitsit toisen tavoitteen. Klikkikampanja on taas käynnissä.",
  "campaigns.pausedBy.goalCampaignRunning":
    "Tauolla: sen tilalla on kampanja, joka optimoi tavoitteellesi. Tämä palaa, jos se ei tuota paremmin 28 päivässä.",
  "campaigns.pausedBy.goalNotBetter":
    "Tauolla: konversiot eivät tulleet 28 päivässä halvemmalla kuin klikkikampanjalla. Klikkikampanja on taas käynnissä.",
  "campaigns.pausedBy.unknown": "Automaatio pysäytti tämän.",
  "campaigns.notFound": "Kampanjaa ei ole olemassa, tai se ei ole sinun.",
  "campaigns.launchQueued":
    "Julkaisu jonossa. Alustalla se kestää hetken; tila kertoo tässä kun se on käynnissä.",
  "campaigns.copyRetryQueued": "Mainostekstien julkaisu yritetään uudelleen. Seuraa tulosta tästä.",
  "campaigns.copyRetryHint":
    "Kampanja on jo yhdistetty mainosalustalle, mutta mainostekstien julkaisu epäonnistui. Yritä tekstejä uudelleen luomatta uutta kampanjaa.",
  "campaigns.review.title": "Tarkista ennen käynnistystä",
  "campaigns.review.intro":
    "Kampanja ei ole käynnissä eikä kuluta rahaa ennen kuin käynnistät sen.",
  "campaigns.review.budget": "Budjetti",
  "campaigns.review.targeting": "Kohdennus",
  "campaigns.review.notSet": "Ei asetettu",
  "campaigns.review.placement": "Mainospaikka",
  "campaigns.review.creatives": "Tarkista tekstit ja kuvat",
  "campaigns.review.creativesMissing": "Tarkista ensin mainostekstit ja kuvat.",
  "campaigns.review.others": "Muut tämän ryhmän mainospaikat",
  "campaigns.review.othersHint": "Tarkista ja käynnistä jokainen mainospaikka erikseen.",
  "campaigns.review.creativesIntro":
    "Luonnos → tekstit ja kuvat → tarkistus → käynnistys. Valmistele aineistot täällä ja tarkista sitten budjetti ja kohdennus. Tarina on valinnainen ideointiapu, ei käynnistyksen ehto.",
  "campaigns.review.budgetTargeting": "Tarkista budjetti ja kohdennus",
  "campaigns.review.imagesQueued":
    "Kuvat ovat jonossa eivätkä vielä mainoksissa; tarkista ne niiden valmistuttua.",
  "campaigns.review.onboarding":
    "Luonnos → tekstit ja kuvat → tarkistus → käynnistys. Luonnosten luonti ei aloita kulutusta; tarkista jokaisen mainospaikan budjetti ja kohdennus ennen erillistä käynnistystä.",
  "campaigns.review.storyOptional": "Tarina on valinnainen ideointiapu, ei pakollinen vaihe.",
  "campaigns.section.budget": "Budjetti",
  "campaigns.section.platform": "Alustalla",
  "campaigns.section.performance": "Viimeiset 30 päivää",
  "campaigns.section.history": "Historia",
  "campaigns.budget.none": "Budjettia ei ole asetettu.",
  "campaigns.budget.daily": "{amount} päivässä",
  "campaigns.budget.newDaily": "Uusi päiväbudjetti",
  "campaigns.budget.save": "Muuta budjettia",
  "campaigns.budget.invalid": "Syötä nollaa suurempi summa.",
  "campaigns.budget.refused": "Alusta ei hyväksynyt muutosta.",
  "campaigns.budget.refusedNearest": "Askel on liian suuri. Lähin sallittu on {amount}.",
  "campaigns.budget.platformDiffers":
    "Alustalla on yhä {amount} päivässä. Aiempi muutos ei mennyt perille; seuraava ajo yrittää uudelleen.",
  "campaigns.platform.notLaunched": "Ei vielä julkaistu.",
  "campaigns.platform.pending": "Julkaistaan.",
  "campaigns.platform.live": "Käynnissä.",
  "campaigns.platform.paused": "Tauolla.",
  "campaigns.platform.failed": "Julkaisu epäonnistui.",
  "campaigns.targeting": "Kohdemaat: {countries}.",
  "campaigns.pmax.running":
    "Google testaa osalla tästä budjetista myös Performance Max -mainoksia.",
  "campaigns.pmax.kept":
    "Performance Max -mainokset toimivat vähintään yhtä hyvin kuin hakumainokset ja saavat jatkossakin osan tästä budjetista.",
  "campaigns.pmax.stopped":
    "Performance Max -mainoksia testattiin ja ne pysäytettiin; niiden budjetti palasi tälle kampanjalle.",
  "campaigns.countries.change": "Muuta",
  "campaigns.countries.save": "Tallenna kohdemaat",
  "campaigns.countries.saved":
    "Tallennettu. Google saa uudet kohdemaat muutaman minuutin kuluessa.",
  "campaigns.action.pause": "Tauolle",
  "campaigns.pause.title": "Keskeytetäänkö kampanja?",
  "campaigns.pause.warning":
    "Keskeytetäänkö {name} mainosalustalla? Jos alusta ei vahvista keskeytystä, kampanja pysyy käynnissä.",
  "campaigns.action.delete": "Poista kampanja",
  "campaigns.delete.title": "Poistetaanko kampanja?",
  "campaigns.delete.warning":
    "Poistetaanko {name}? Vain tämä kampanja poistetaan. Jos mainosalusta ei vahvista toimenpidettä, kampanja jää näkyviin.",
  "campaigns.delete.done": "Kampanja poistettiin mainosalustan vahvistettua toimenpiteen.",
  "campaigns.delete.journalRequired":
    "Poisto ei ole tilapäisesti käytettävissä. Kampanja säilyy täällä, kunnes se voidaan poistaa turvallisesti.",
  "campaigns.delete.launching":
    "Kampanjaa julkaistaan vielä. Yritä uudelleen muutaman minuutin päästä.",
  "campaigns.delete.busy":
    "Scalephant päivittää kampanjaa juuri nyt. Yritä uudelleen minuutin päästä.",
  "campaigns.delete.reconnect":
    "Yhdistä mainostili ensin uudelleen asetuksissa ja poista sitten kampanja.",
  "campaigns.delete.pilot":
    "Kampanja on mukana käynnissä olevassa testissä, eikä sitä voi poistaa ennen testin loppua.",
  "campaigns.launch.notEligible":
    "Mainosalusta ei päästä kampanjaa käyntiin. Tarkista mainostilisi (esimerkiksi laskutus tai tilin esto) ja paina Julkaise uudelleen, tai poista kampanja.",
  "campaigns.action.resume": "Jatka",
  "campaigns.action.launch": "Julkaise",
  "campaigns.measurement.title": "Optimointi ja mittaus",
  "campaigns.measurement.visits": "Optimoidaan sivukäyntejä",
  "campaigns.measurement.googleVisits":
    "Ohjataan sivustokäyntejä klikkauspohjaisella hintatarjouksella",
  "campaigns.measurement.noGoal": "Konversiotavoitetta ei ole valittu",
  "campaigns.measurement.check": "Tarkista seuranta",
  "campaigns.measurement.details": "Mittauksen lisätiedot",
  "campaigns.launch.blocked.support": "Tuen täytyy ensin tarkistaa aiempi käynnistys.",
  "campaigns.launch.blocked.tracking": "Tarkista ensin seuranta-asetukset.",
  "campaigns.launch.blocked.acknowledge":
    "Rastita yllä oleva ruutu käynnistääksesi liikenneoptimoinnilla.",
  "campaigns.launch.blocked.chooseCountries": "Valitse maat",
  "campaigns.launch.blocked.setBudget": "Aseta budjetti",
  "campaigns.launch.uncertain":
    "Aiemman julkaisun tulosta ei voitu vahvistaa. Ota yhteyttä tukeen ennen uutta julkaisua.",
  "campaigns.launch.configuration":
    "Tarkista valittu konversiotavoite ja mainosalustan käyttöoikeudet. Julkaisu ei vaadi aiempaa konversiota.",
  "campaigns.launch.metaSettings":
    "Meta ei hyväksynyt kampanjan asetuksia. Tuki voi tarkistaa valitun tapahtuman, pikselin käyttöoikeudet ja tavoitteen yhteensopivuuden. Älä yhdistä vahvistettua pikseliä uudelleen vain siksi, ettei konversioita ole vielä saapunut.",
  "campaigns.launch.googleSettings":
    "Google ei hyväksynyt kampanjan asetuksia. Tarkista, että käytössä oleva ensisijainen konversiotoiminto vastaa valittua tapahtumaa. Aiempia konversioita ei tarvita.",
  "campaigns.measurement.goal": "Tavoite: {goal}",
  "campaigns.measurement.traffic": "Todellinen optimointi: linkin klikkaukset (Liikenne).",
  "campaigns.measurement.conversions": "Konversio-optimointi valittu",
  "campaigns.measurement.businessGoal":
    "Todellinen optimointi noudattaa kampanjan liiketoimintatavoitetta.",
  "campaigns.measurement.unmeasured":
    "Konversiomittaus on puutteellinen. Jakelu- ja klikkaustiedot eivät todista ostoksia, rekisteröitymisiä tai mainonnan tuottoa.",
  "campaigns.measurement.warning":
    "Konversioseurantaa ei ole määritetty. Voit julkaista nyt liikenneoptimoinnilla. Scalephant voi seurata mainosten jakelua ja klikkauksia, mutta ei voi luotettavasti mitata ostoksia tai rekisteröitymisiä eikä optimoida niitä ennen seurannan määrittämistä.",
  "campaigns.measurement.learning":
    "Klikkausten voittaja ei ole konversioiden voittaja. Konversio- ja ROAS-oppiminen on rajallista; seurannan määrittäminen ei muuta tämän kampanjan optimointia automaattisesti.",
  "campaigns.measurement.setup": "Määritä seuranta",
  "campaigns.measurement.serverWaiting":
    "Palvelinpuolen seuranta on valmis. Odotetaan ensimmäistä tapahtumaa palvelimeltasi.",
  "campaigns.measurement.acknowledge":
    "Ymmärrän liikenneoptimoinnin ja mittauksen rajoitukset ja hyväksyn tämän julkaisun.",
  "campaigns.measurement.launchTraffic": "Julkaise liikenneoptimoinnilla",
  "campaigns.measurement.missing.pixel": "Puuttuu: valittu Meta-pikseli tai tietojoukko.",
  "campaigns.measurement.missing.pixel_health":
    "Puuttuu: äskettäin varmennettu, toimiva Meta-pikseli tai tietojoukko.",
  "campaigns.measurement.missing.primary_event":
    "Puuttuu: varmennettu ensisijainen osto-, liidi- tai rekisteröitymistapahtuma.",
  "campaigns.measurement.missing.primary_event_ambiguous":
    "Ensisijainen tapahtuma on epäselvä: valitse liidi- tai rekisteröitymisseuranta ennen konversio-optimointia.",
  "campaigns.measurement.missing.event_signal":
    "Puuttuu: tuore vahvistetun selainsäännön tai tunnistetun palvelintapahtuman signaali.",
  "campaigns.measurement.missing.provider_acceptance":
    "Mainosalustan hyväksymää ensisijaisen tapahtuman toimitusta ei ole vielä vahvistettu. Paikallinen vastaanotto ei todista hyväksyntää.",
  "campaigns.action.retryCopy": "Yritä mainostekstien julkaisua uudelleen",
  "campaigns.metrics.none": "Ei vielä lukuja.",
  "campaigns.metrics.summary":
    "{spend} kulutettu · {clicks} klikkausta · {conversions} konversiota",
  "campaigns.history.none": "Mitään ei ole vielä tapahtunut.",
  "creatives.copy.title": "Mainostekstit",
  "creatives.copy.edit": "Muokkaa tekstiä",
  "creatives.copy.headline": "Otsikko",
  "creatives.copy.body": "Leipäteksti",
  "creatives.copy.cta": "Toimintakehote",
  "creatives.copy.editHint":
    "Tallennus mitätöi aiemman tarkistuksen. Tarkista tallennettu teksti uudelleen ennen käynnistystä. Muokkaus ei kuluta generointirajaasi.",
  "campaigns.review.countriesMissing": "Valitse ensin vähintään yksi maa.",
  "campaigns.review.budgetMissing": "Aseta ensin päiväbudjetti.",
  "campaigns.review.creativesError": "Mainoksia ei voitu tarkistaa. Lataa sivu uudelleen.",
  "campaigns.draft.localOnly":
    "Luonnoksen asetukset tallennetaan paikallisesti. Tallennus ei julkaise eikä muuta mitään mainosalustalla.",
  "campaigns.draft.countries": "Kohdemaat (esim. FI, SE)",
  "campaigns.draft.minAge": "Alaikäraja (valinnainen)",
  "campaigns.draft.maxAge": "Yläikäraja (valinnainen)",
  "campaigns.draft.saveTargeting": "Tallenna kohdennus",
  "creatives.delete.copy": "Poista teksti",
  "creatives.delete.image": "Poista kuva",
  "creatives.delete.confirm": "Vahvista poisto",
  "creatives.delete.copyWarning":
    "Poistetaanko vain tämä teksti? Poistoa ei voi perua eikä se kuluta tekoälyn käyttörajaasi.",
  "creatives.delete.imageWarning":
    "Poistetaanko vain tämä kuva? Siihen liitettyjen tekstien tarkistus mitätöidään ja ne poistuvat käynnistyksestä. Poistoa ei voi perua eikä se kuluta tekoälyn käyttörajaasi.",
  "campaigns.review.copyEmpty": "Luo ensin mainostekstit.",
  "campaigns.review.copyUnreviewed": "Tarkista ensin mainostekstit.",
  "campaigns.review.imageMissing": "Tarkista ensin vähintään yksi mainoskuva.",
  "creatives.regenerate.copy": "Luo teksti uudelleen",
  "creatives.regenerate.image": "Luo kuva uudelleen",
  "creatives.regenerate.warning":
    "Manuaalista uudelleenluontia koskevat pakettisi kohtuukäyttörajat. Nykyinen säilyy kunnes korvaava luonti onnistuu. Hyväksyntä poistuu; tarkista uusi sisältö ennen julkaisua.",
  "creatives.regenerate.confirm": "Vahvista uudelleenluonti",
  "creatives.regenerate.queued":
    "Korvaava kuva jonossa. Nykyinen säilyy epäonnistuessa. Päivitä luonnin jälkeen ja tarkista uusi kuva sekä siihen liitetyt tekstit ennen julkaisua.",
  "creatives.copy.none": "Ei vielä mainostekstejä.",
  "creatives.copy.generate": "Kirjoita mainostekstit",
  "creatives.copy.writing": "Kirjoitetaan… tämä voi kestää minuutin",
  "creatives.copy.live": "Käynnissä",
  "creatives.copy.unreviewed": "Tarkistamatta",
  "creatives.copy.why": "Miksi tämä luonnos?",
  "creatives.copy.strategy.hypothesis": "Kirjoittajan oletus",
  "creatives.copy.strategy.buyer": "Todennäköinen ostaja",
  "creatives.copy.strategy.trigger": "Miksi juuri nyt",
  "creatives.copy.strategy.outcome": "Tavoiteltu hyöty",
  "creatives.copy.strategy.differentiator": "Ehdotettu erottautumistekijä",
  "creatives.copy.strategy.objection": "Mahdollinen epäröinti",
  "creatives.copy.strategy.awareness": "Oletettu tietoisuus",
  "creatives.copy.sourceBasis":
    "Perustuu tallennettuihin tuotefaktoihin ja tämän luonnoksen kirjoitushetkellä käytettävissä olleisiin sivustokatkelmiin.",
  "creatives.copy.factsBasis":
    "Perustuu tallennettuihin tuotefaktoihin; vastaavaa sivustolähdettä ei ollut käytettävissä tätä luonnosta varten.",
  "creatives.copy.evidenceLimits":
    "Mainoskulma on viestintähypoteesi. Asiakasongelmia tai kilpailijoiden asemointia ei ole riippumattomasti vahvistettu; tarkista tuoteväitteet ennen käynnistystä.",
  "creatives.copy.outcomeBasis": "Yksityiset liiketoimintahavainnot",
  "creatives.copy.industryBasis": "Alustava toimialan luonnoshypoteesi",
  "creatives.copy.industryForm.question": "Kysymysmuotoinen otsikko",
  "creatives.copy.industryForm.statistic": "Numeromuotoinen otsikko",
  "creatives.copy.industryForm.urgency": "Kiiremuotoinen otsikko",
  "creatives.copy.industryForm.curiosity": "Uteliaisuusmuotoinen otsikko",
  "creatives.copy.industryForm.pain_point": "Ongelmamuotoinen otsikko",
  "creatives.copy.industryForm.social_proof": "Sosiaalisen näytön otsikkomuoto",
  "creatives.copy.industryForm.direct": "Suora otsikkomuoto",
  "creatives.copy.industryLimits":
    "Perustuu asiakkaiden ilmoittamaan laatuun, ei varmennettuihin myynteihin tai voittavaan kulmaan. Mittauksen kattavuus on tuntematon ja URL-yhteys osittainen. Omat olennaiset tuloksesi ovat ensisijaisia.",
  "creatives.copy.qualifiedBasis": "Asiakkaan kvalifioimat aiempien mainosten ilmoitukset",
  "creatives.copy.qualified.paid_order": "Backendin ilmoittamat maksetut tilaukset",
  "creatives.copy.qualified.useful_lead": "Backendin ilmoittamat hyödylliset liidit",
  "creatives.copy.qualifiedLimits":
    "Asiakkaan ilmoittama laatu, ei riippumatonta varmennusta. Päättyneet UTC-päivät eivät todista kattavaa mittausta; URL-yhteys on osittainen. Voittajaa tai budjettimuutosta ei päätellä.",
  "creatives.copy.creativeOutcomeBasis": "Aiempiin mainoksiin yhdistetyt ilmoitukset",
  "creatives.copy.creativeOutcomeLimits":
    "Ilmoitukset vastaavat käynnistyksessä jäädytettyä kampanjan konversiomääritelmää. URL-tunnisteet ovat ilmoitettuja yhteyksiä, eivät varmennettua attribuutiota tai liidin/maksun laatua. Niitä käytetään jatkoideoihin, ei voittajiin; nämä yhteydet eivät ohjaa automaattista mainosten näyttämisen muutosta.",
  "creatives.copy.outcomeLimits":
    "Taustajärjestelmäsi ilmoitti nämä kampanjaan kohdistetut tapahtumat. Mittauksen kattavuus on tuntematon; nämä eivät ole riippumattomasti vahvistettuja myyntejä tai näyttö voittavasta mainoksesta. Asiakastietoja ei jaeta muille mainostajille.",
  "creatives.copy.outcome.purchase": "Ostot",
  "creatives.copy.outcome.lead": "Liidit",
  "creatives.copy.outcome.signup": "Rekisteröitymiset",
  "creatives.copy.marketBasis": "Tallennettu markkinatutkimus",
  "creatives.copy.marketHypotheses": "Mahdolliset viestintäaukot — oletuksia",
  "creatives.copy.marketLimits":
    "Tallennettu malliyhteenveto kuvaa kilpailijoiden viestintää, ei todennettuja asiakasongelmia tai tehoa. Linkit ovat lähdeluettelo, eivät väitekohtaisia viitteitä; päivämäärä on raportin eikä kunkin sivun hakuhetki.",
  "creatives.copy.reviewed": "Tarkistettu",
  "creatives.copy.review": "Hyväksy teksti",
  "creatives.copy.approveAll": "Hyväksy kaikki",
  "creatives.extras.title": "Mainoksen lisärivit",
  "creatives.extras.hint":
    "Linkit ja lyhyet tiedot Google-mainoksesi alla. Hyväksytään yhdessä mainostekstien kanssa.",
  "creatives.extras.pending": "Tarkistamatta",
  "creatives.extras.live": "Googlessa",
  "creatives.extras.remove": "Poista",
  "creatives.extras.approve": "Hyväksy mainoksen lisärivit",
  "creatives.copy.unapprovedCount": "{count}/{total} tarkistamatta",
  "creatives.action.approve": "Hyväksy",
  "creatives.action.edit": "Muokkaa",
  "creatives.action.regenerate": "Luo uudelleen",
  "creatives.action.delete": "Poista",
  "creatives.copy.error.productIncomplete":
    "Mainokset kirjoitetaan sen pohjalta, mikä tuote on ja kenelle se on, eikä tällä tuotteella ole vielä kuvausta. Kuvaile se ensin tai anna meidän lukea se verkkosivuiltasi.",
  "creatives.copy.error.describeProduct": "Kuvaile tuote",
  "creatives.copy.error.noCredits":
    "Pakettisi nykyinen AI-käyttöraja on täyttynyt. Yritä tilausjakson vaihduttua tai ota yhteys tukeen.",
  "creatives.copy.error.unpublishable":
    "Emme saaneet kirjoitettua tekstiä, joka pysyy siinä, mitä olet kertonut tuotteesta. Todelliset tarjouksesi ja asiakkaiden suosittelut antavat mainoksille enemmän sanottavaa.",
  "creatives.copy.error.modelFailed":
    "Kirjoittava malli ei vastannut tällä kertaa. Yritä uudelleen hetken kuluttua.",
  "creatives.copy.error.recoveryStorage":
    "Selaimen tab-tallennus ei ole käytettävissä. Salli tallennus ennen tekstin generointia; pyyntöä ei lähetetty.",
  "creatives.copy.error.recoveryDamaged":
    "Tallennettu generoinnin tunniste on vaurioitunut. Ota yhteys tukeen ennen uusintaa; uutta generointia ei aloitettu.",
  "creatives.copy.error.sharingApproval":
    "AI-tietojen jakamisen hyväksyntä puuttuu tai ei salli tätä palveluntarjoajaa. Ota yhteys tukeen ennen uuden tekstin generointia. AI-kutsua ei tehty.",
  "creatives.copy.error.organizationChanged":
    "Aktiivinen organisaatio vaihtui. Lataa kampanja uudelleen oikeassa organisaatiossa ennen jatkamista.",
  "creatives.copy.error.operationUnresolved":
    "Kampanjan aiempi generointi tarvitsee selvityksen ennen jatkamista. Ota yhteys tukeen; älä aloita uutta maksullista generointia palautuksen vuoksi.",
  "creatives.copy.error.operationMismatch":
    "Tallennettu generointi ei enää vastaa pyyntöä tai sen tekstiä. Tarkista nykyinen teksti ja ota yhteys tukeen ennen uusintaa.",
  "creatives.brand.title": "Tunnistimme sivulta {site}",
  "creatives.brand.none": "Sivulta ei löytynyt brändivärejä. Kuvissa käytetään logosi värejä.",
  "creatives.brand.fromSite": "Kuvasi tehdään näillä väreillä.",
  "creatives.brand.yours": "Omat värisi. Kuvat tehdään näillä.",
  "creatives.brand.main": "Pääväri",
  "creatives.brand.accent": "Korostusväri",
  "creatives.brand.change": "Muuta",
  "creatives.brand.useSite": "Käytä sivun värejä",
  "creatives.brand.reread": "Lue sivu uudelleen",
  "creatives.brand.reading": "Luetaan…",
  "creatives.brand.save": "Tallenna",
  "creatives.brand.cancel": "Peruuta",
  "creatives.brand.style": "Tyyli: {words}",
  "creatives.brand.stylePending": "Katsotaan sivun tyyliä…",
  "creatives.brand.imagery.photography": "valokuvat",
  "creatives.brand.imagery.illustration": "kuvitus",
  "creatives.brand.imagery.graphic": "graafinen",
  "creatives.brand.imagery.product": "tuotekuvat",
  "creatives.brand.imagery.minimal": "pelkistetty",
  "creatives.brand.mood.calm": "rauhallinen",
  "creatives.brand.mood.energetic": "energinen",
  "creatives.brand.mood.premium": "laadukas",
  "creatives.brand.mood.playful": "leikkisä",
  "creatives.brand.mood.technical": "tekninen",
  "creatives.brand.mood.warm": "lämmin",
  "creatives.brand.mood.bold": "rohkea",
  "creatives.brand.shapes.rounded": "pyöreät muodot",
  "creatives.brand.shapes.sharp": "suorat linjat",
  "creatives.brand.shapes.organic": "virtaavat muodot",
  "creatives.brand.shapes.geometric": "geometriset muodot",
  "creatives.images.title": "Kuvat",
  "creatives.images.none": "Ei vielä kuvia.",
  "creatives.images.generate": "Luo kuvia",
  "creatives.images.queued":
    "{count} kuvaa jonossa. Ne kestävät hetken palveluntarjoajalla ja ilmestyvät tähän sitä mukaa.",
  "creatives.images.error.notRunning":
    "Kuvien luonti ei ole juuri nyt käynnissä, joten mitään ei laitettu jonoon. Yritä uudelleen muutaman minuutin kuluttua.",
  "creatives.images.error.planExcludes": "Pakettiisi ei sisälly tekoälyllä luotuja kuvia.",
  "creatives.images.error.seePlans": "Katso paketit",
  "creatives.images.uploaded": "Ladattu",
  "creatives.images.preview": "Kampanjakuvan esikatselu",
  "creatives.images.generatedBy": "Luonut {model}",
  "creatives.images.reviewed": "Tarkistettu",
  "creatives.images.unreviewed": "Ei tarkistettu",
  "creatives.images.review": "Hyväksy kuva",
  "wizard.title": "Uusi kampanjaryhmä",
  "wizard.needsAccount":
    "Yhdistä ensin mainostili — kampanjalle ei ole vielä paikkaa missä pyöriä.",
  "wizard.step.product": "Mitä mainostat?",
  "wizard.step.where": "Missä sen pitäisi pyöriä?",
  "wizard.countries": "Kohdemaat: {countries}",
  "wizard.countries.change": "Muuta",
  "wizard.countries.label": "Kohdemaat",
  "wizard.countries.missing": "Valitse vähintään yksi maa.",
  "wizard.step.budget": "Paljonko kuukaudessa?",
  "wizard.existingProfile": "Tuote",
  "wizard.newProfile": "Uusi tuote",
  "wizard.productName": "Tuotteen nimi",
  "wizard.websiteUrl": "Verkkosivu",
  "wizard.mixedCurrency":
    "Tilit laskuttavat eri valuutoissa: {accounts}. Mainosalusta lukitsee tilin valuutan, kun tili luodaan. Toistaiseksi yksi budjetti kattaa yhden valuutan, joten valitse samanvaluuttaiset tilit tai avaa toiselle alustalle mainostili samassa valuutassa.",
  "wizard.monthlyBudget": "Kuukausibudjetti ({currency})",
  "wizard.monthlyBudgetHint":
    "Jos mainosalusta ei vahvista pysäytystä, mainostilin kulutus voi ylittää tämän budjetin.",
  "wizard.split": "{amount} päivässä kullakin {count} tilillä.",
  "wizard.dropped":
    "{count} valitsemistasi tileistä ei saa kampanjaa: kuukausi ei riitä niille kaikille alustojen vaatimalla {minimum} päivässä.",
  "wizard.create": "Luo luonnokset",
  "wizard.draftsOnly": "Tämä luo luonnokset. Mikään ei kuluta ennen kuin julkaiset sen.",
  "wizard.error.noRoom":
    "Paketissasi ei ole tilaa näille kampanjoille. Keskeytä toinen kampanja tai valitse suurempi paketti.",
  "wizard.planAllows": "{plan}-pakettisi ajaa {count} kampanjaa kerrallaan.",
  "wizard.tooManyForPlan":
    "Valitsit {picked}, enemmän kuin pakettisi ajaa kerrallaan. Valitse vähemmän tilejä tai siirry isompaan pakettiin.",
  "wizard.changePlan": "Katso paketit",
  "wizard.landingUrl": "Laskeutumissivu (valinnainen)",
  "wizard.landingUrl.hint": "Jätä tyhjäksi, niin mainos vie tuotteen verkkosivulle.",
  "wizard.editProduct": "Lisää kuvaus, kohdeyleisö ja tarjoukset",
  "wizard.productDetailsHint":
    "Mainostekstit kirjoitetaan tuotteen tiedoista: kuvauksesta, kohdeyleisöstä ja tarjouksista. Täytä ne kohdassa Asetukset → Yritys ja tuotteet.",
  "wizard.error.url": "Tämä ei näytä verkko-osoitteelta.",
  "wizard.error.duplicate": "Sinulla on jo samanniminen tuote. Valitse se yllä olevasta listasta.",
  "dashboard.title": "Kojelauta",
  "dashboard.welcome": "Tervetuloa, {name}.",
  "settings.title": "Asetukset",
  "settings.profile.title": "Profiili",
  "settings.security.title": "Turvallisuus",
  "settings.profile.language": "Käyttöliittymän kieli",
  "settings.profile.saved": "Profiili tallennettu.",
  "settings.products.title": "Yritys ja tuotteet",
  "products.intro":
    "Mitä myyt, kenelle ja mitä mainoksissa saa sanoa. Jokainen kampanja kirjoitetaan näiden tietojen pohjalta.",
  "products.empty":
    "Tuotteita ei vielä ole. Kuvaa ensimmäinen alla, niin kampanjavelho voi käyttää sitä.",
  "products.picker": "Tuote",
  "products.new": "+ Uusi tuote",
  "products.create": "Lisää tuote",
  "products.saved": "Tallennettu.",
  "products.intel.title": "Tuotetutkimus",
  "products.intel.pending":
    "Tallennetut tuotefaktat ja sivustolähteet valmistellaan automaattisesti.",
  "products.intel.limited":
    "Tuotefaktat ja sivustokonteksti ovat valmiit. Tutkimus on rajattu, ei riippumaton vahvistus.",
  "products.intel.unavailable":
    "Tutkimus ei ole saatavilla tai keskeytyi. Tallennetut tuotefaktat ovat edelleen käytettävissä.",
  "products.intel.updated": "Päivitetty:",
  "products.intel.facts": "Antamasi tuotefaktat",
  "products.intel.website": "Oman sivustosi väitteet — ei riippumattomasti vahvistettu",
  "products.intel.competitors": "Kilpailijoita ei ole tutkittu.",
  "products.intel.citations": "Palveluntarjoajan palauttamat viitteet",
  "products.intel.mentions": "Mainitut kilpailijasivustot",
  "products.intel.unclassified": "Lähdeluettelo — viitteiden luokittelu tuntematon",
  "products.intel.market": "Tallennetut markkinahypoteesit",
  "products.intel.automaticResearch": "Automaattinen verkkotutkimus",
  "products.intel.hypotheses":
    "Kilpailijoiden asemointi ja ehdotetut kulmat eivät vahvista asiakasongelmaa tai mainonnan tehoa. Lähteet ovat luettelo, eivät väitekohtaisia viitteitä.",
  "products.intel.offline":
    "Vain offline-varmennus — ei live-tutkimusta tai vahvistettua kysyntää.",
  "products.intel.problems": "Asiakasongelmia ei ole vahvistettu.",
  "products.intel.researchRetry":
    "Kilpailijatutkimus ei valmistunut tällä kertaa. Se käynnistyy automaattisesti uudelleen.",
  "products.intel.researchFailed":
    "Kilpailijatutkimusta ei saatu valmiiksi tälle versiolle. Tuotteen muokkaus käynnistää sen uudelleen.",
  "products.intel.websiteUnavailable": "Sivustoa ei voitu lukea. Tuotefaktat säilyvät.",
  "products.section.basics": "Tuote",
  "products.name": "Nimi",
  "products.website": "Verkkosivu",
  "products.website.hint": "Minne mainokset vievät, ellei kampanjalle ole annettu muuta sivua.",
  "products.description": "Mikä se on ja mitä se tekee",
  "products.description.hint": "Muutama lause omin sanoin. Mainostekstit kirjoitetaan tästä.",
  "products.audience": "Kenelle se on tarkoitettu",
  "products.audience.hint":
    "Esimerkiksi: pienten suomalaisten verkkokauppojen omistajat, jotka hoitavat mainontansa itse.",
  "products.language": "Mainosten kieli",
  "products.language.site": "Sama kuin verkkosivulla",
  "products.countries": "Maat",
  "products.countries.hint":
    "Kaksikirjaimiset koodit pilkuilla erotettuina, esim. FI, SE. EU kattaa koko unionin.",
  "products.goal": "Mitä haluat mainonnalta?",
  "products.goal.clicks": "Kävijöitä verkkosivulleni",
  "products.goal.signup": "Rekisteröitymisiä",
  "products.goal.lead": "Liidejä (yhteydenottoja)",
  "products.goal.purchase": "Myyntiä",
  "products.goal.hint":
    "Kampanjat aloittavat tuomalla kävijöitä. Kun tavoitettasi on kertynyt yli 50 kertaa 30 päivässä, Scalephant avaa sille oman kampanjan niiden rinnalle.",
  "products.section.revenue": "Tulot",
  "products.revenue.intro":
    "Mitä asiakas on sinulle arvoinen. Suojakaiteet erottavat tämän avulla hyvän myyntikohtaisen hinnan huonosta.",
  "products.revenueModel": "Miten ansaitset",
  "products.revenueModel.unset": "Ei asetettu",
  "products.revenueModel.one_time": "Kertaostot",
  "products.revenueModel.subscription": "Tilaukset",
  "products.currency": "Valuutta",
  "products.orderValue": "Keskiostos",
  "products.subscriptionValue": "Tilauksen kuukausihinta",
  "products.ltvMonths": "Montako kuukautta asiakas pysyy",
  "products.ltvMonths.hint": "Arvio riittää, 1–120.",
  "products.adBudget": "Mainosbudjetti kuukaudessa",
  "products.adBudget.hint": "Kaikki kampanjat yhteensä. Valinnainen.",
  "products.section.claims": "Mitä mainoksissa saa sanoa",
  "products.claims.intro":
    "Mainokset mainitsevat vain tässä luetellut tarjoukset ja lainaavat vain tässä lueteltuja asiakkaita. Kaikki muu, mitä malli kirjoittaa, poistetaan ennen julkaisua.",
  "products.offers": "Tarjoukset",
  "products.offers.hint":
    'Yksi per rivi, esimerkiksi "14 päivän ilmainen kokeilu" tai "Ilmainen toimitus yli 50 € tilauksiin".',
  "products.testimonials": "Asiakkaiden suosittelut",
  "products.testimonials.hint": "Sanatarkasti ja vain asiakkaan luvalla.",
  "products.testimonial.quote": "Lainaus",
  "products.testimonial.attribution": "Kuka sanoi",
  "products.testimonial.remove": "Poista",
  "products.testimonial.add": "Lisää suosittelu",
  "products.section.creative": "Mainosmateriaalit",
  "products.copyEnabled": "Kirjoita mainostekstit tekoälyllä",
  "products.imagesEnabled": "Tee kuvat tekoälyllä",
  "products.videoEnabled": "Tee videot",
  "products.error.name": "Anna tuotteelle nimi.",
  "products.error.url": "Tämä ei näytä verkko-osoitteelta.",
  "products.error.amount": "Kirjoita summa numerona, esimerkiksi 49,90.",
  "products.error.months": "Kokonainen määrä kuukausia väliltä 1–120.",
  "products.error.currency": "Valitse valuutta.",
  "products.error.countries": "Käytä kaksikirjaimisia maatunnuksia (Yhdysvallat: US) tai EU.",
  "products.error.testimonial": "Jokaisessa suosittelussa tarvitaan sekä lainaus että sen sanoja.",
  "products.error.duplicate": "Sinulla on jo samanniminen tuote.",
  "onboarding.title": "Ota Scalephant käyttöön",
  "onboarding.planActive":
    "Pakettisi on käytössä. Kerro seuraavaksi yrityksestäsi, niin Scalephant rakentaa ensimmäisen kampanjasi.",
  "onboarding.progress": "Eteneminen",
  "onboarding.step.product": "Yrityksesi",
  "onboarding.step.revenue": "Tulot",
  "onboarding.step.claims": "Mitä mainoksissa saa sanoa",
  "onboarding.step.tracking": "Seuranta",
  "onboarding.step.accounts": "Mainostilit",
  "onboarding.step.campaign": "Kampanja",
  "onboarding.tracking.title": "Konversioseuranta",
  "onboarding.tracking.intro":
    "Asenna seurantakoodi ja määritä, mikä lasketaan konversioksi. Voit jatkaa ja tehdä tämän myöhemmin, mutta pelkät linkkitunnisteet eivät mittaa myyntiä.",
  "onboarding.tracking.continue": "Jatka kampanjaan",
  "onboarding.accounts.title": "Yhdistä mainostilisi",
  "onboarding.accounts.intro":
    "Yhdistä Meta- tai Google Ads -tili kampanjaluonnoksen luomista varten. Tilin yhdistäminen ei käynnistä kampanjaa.",
  "onboarding.accounts.continue": "Jatka seurantaan",
  "onboarding.resumeCreatives": "Jatka mainosten tarkistukseen",
  "campaigns.review.continueLaunch": "Jatka käynnistyksen tarkistukseen",
  "campaigns.review.launchNextHint":
    "Tarkista seuraavaksi budjetti ja kohdennus. Käynnistys pysyy estettynä, kunnes vaaditut mainosaineistot on tarkistettu. Vain erillinen käynnistyshyväksyntäsi voi aloittaa mainonnan.",
  "onboarding.campaign.title": "Ensimmäinen kampanjasi",
  "onboarding.campaign.intro":
    "Valitse tuote, mainostilit ja kuukausibudjetti. Tämä luo luonnokset; tarkistat mainosaineistot ja käynnistät kampanjan erikseen.",
  "onboarding.step.next": "Seuraavaksi",
  "onboarding.product.title": "Kerro yrityksestäsi",
  "onboarding.product.intro":
    "Anna verkkosivusi osoite ja paina Lue verkkosivuni: Scalephant täyttää sen, minkä sivuilta löytää, ja sinä tarkistat tiedot ennen tallennusta.",
  "onboarding.revenue.title": "Mitä asiakas on sinulle arvoinen?",
  "onboarding.revenue.intro":
    "Suojakaiteet erottavat tämän avulla hyvän myyntikohtaisen hinnan huonosta. Voit ohittaa tämän ja täyttää sen myöhemmin.",
  "onboarding.claims.title": "Mitä mainoksissasi saa sanoa?",
  "onboarding.claims.intro":
    "Mainokset mainitsevat vain tässä luetellut tarjoukset ja lainaavat vain tässä lueteltuja asiakkaita. Jätä tyhjäksi, jos niitä ei ole.",
  "onboarding.saveAndContinue": "Tallenna ja jatka",
  "onboarding.back": "Takaisin",
  "onboarding.skip": "Ohita toistaiseksi",
  "onboarding.next.title": "Yrityksesi on kuvattu",
  "onboarding.next.intro": "Ennen kuin ensimmäinen kampanja pyörii, jäljellä on:",
  "onboarding.next.accounts": "Yhdistä mainostili",
  "onboarding.next.accountsDone": "Mainostili on yhdistetty.",
  "onboarding.next.tracking": "Ota konversioseuranta käyttöön",
  "onboarding.next.campaign": "Luo ensimmäinen kampanja",
  "onboarding.next.done": "Siirry etusivulle",
  "dashboard.onboarding.title": "Kerro Scalephantille yrityksestäsi",
  "dashboard.onboarding.text":
    "Mainoksesi kirjoitetaan tuotteesi tiedoista. Se vie muutaman minuutin, ja Scalephant osaa lukea suurimman osan verkkosivuiltasi.",
  "dashboard.onboarding.start": "Aloita",
  "dashboard.onboarding.resumeTitle": "Jatka kampanjan käyttöönottoa",
  "dashboard.onboarding.resumeText":
    "Tuotteesi on tallennettu. Jatka seurantaan, mainostileihin ja ensimmäiseen kampanjaan.",
  "dashboard.onboarding.resume": "Jatka aloitusta",
  "dashboard.productIncomplete":
    "Tuotteella {name} ei ole vielä kuvausta, ja mainokset kirjoitetaan siitä.",
  "dashboard.productIncomplete.link": "Lisää kuvaus",
  "wizard.startOnboarding": "Uusi käyttäjä? Kuvaa ensin yrityksesi",
  "products.analysis.run": "Lue verkkosivuni",
  "products.analysis.hint": "Täyttää tyhjät kentät sivuiltasi pakettisi käyttörajojen puitteissa.",
  "products.analysis.filled": "Täytetty sivulta {host}: {fields}. Tarkista ennen tallennusta.",
  "products.analysis.nothingEmpty": "Kaikissa kentissä oli jo arvo, joten mitään ei muutettu.",
  "products.analysis.unavailable":
    "Verkkosivujen lukeminen ei ole vielä käytössä tällä palvelimella.",
  "products.analysis.modelFailed":
    "Verkkosivun lukeminen epäonnistui meidän päässämme, eikä mitään veloitettu. Yritä muutaman minuutin kuluttua uudelleen tai täytä tiedot itse.",
  "products.analysis.noCredits":
    "Pakettisi nykyinen AI-käyttöraja on täyttynyt. Yritä tilausjakson vaihduttua tai ota yhteys tukeen.",
  "products.analysis.recoveryStorage":
    "Selain ei pysty säilyttämään palautuspyyntöä istuntotallennuksessa. Analyysia ei aloitettu. Salli istuntotallennus ja yritä uudelleen tai täytä tiedot itse.",
  "products.analysis.recoveryDamaged":
    "Tallennettu palautuspyyntö on vaurioitunut. Uutta analyysia ei aloitettu. Ota yhteyttä tukeen tai täytä tiedot itse; älä poista pyyntöä maksullisen työn uusimiseksi.",
  "products.analysis.sharingApproval":
    "AI-tietojen jakamista ei ole hyväksytty tälle analyysille tai palveluntarjoajalle. Ota yhteys tukeen ennen uusintaa tai täytä tiedot itse. AI-kutsua ei tehty.",
  "products.analysis.organizationChanged":
    "Aktiivinen organisaatio vaihtui. Lataa sivu uudelleen ennen verkkosivun lukemista. Tälle pyynnölle ei aloitettu analyysia.",
  "products.analysis.operationUnresolved":
    "Edellinen analyysi ei ole valmistunut varmasti. Odota ja yritä samaa pyyntöä uudelleen; jos esto jatkuu, ota yhteyttä tukeen tai täytä tiedot itse. Uusinta ei aloita uutta maksullista yritystä.",
  "products.analysis.operationMismatch":
    "Tämä analyysipyyntö kuuluu eri osoitteelle. Tarkista osoite ja avaa lomake tarvittaessa uudelleen; ota yhteyttä tukeen, jos ristiriita jatkuu.",
  "products.analysis.unreadable": "Sivua ei voitu lukea. Tarkista osoite tai täytä tiedot itse.",
  "settings.security.password.title": "Vaihda salasana",
  "settings.security.password.current": "Nykyinen salasana",
  "settings.security.password.new": "Uusi salasana",
  "settings.security.password.confirm": "Toista uusi salasana",
  "settings.security.password.signOutOthers": "Kirjaa muut laitteeni ulos",
  "settings.security.password.submit": "Vaihda salasana",
  "settings.security.password.done": "Salasana vaihdettu.",
  "settings.security.password.error.wrong": "Nykyinen salasana on väärin.",
  "settings.security.twoFactor.title": "Kaksivaiheinen tunnistus",
  "settings.security.twoFactor.on": "Kaksivaiheinen tunnistus on päällä.",
  "settings.security.twoFactor.off":
    "Kaksivaiheinen tunnistus on pois päältä. Kytke se päälle suojataksesi mainostilisi.",
  "settings.security.twoFactor.enable": "Kytke päälle",
  "settings.security.twoFactor.enableEmail": "Kytke päälle sähköpostikoodilla",
  "settings.security.twoFactor.enableApp": "Käytä mieluummin todennussovellusta",
  "settings.security.twoFactor.email.done":
    "Kaksivaiheinen tunnistus on päällä. Kun kirjaudut, lähetämme koodin sähköpostiisi.",
  "settings.security.twoFactor.disable": "Kytke pois",
  "settings.security.twoFactor.passwordPrompt": "Vahvista salasanallasi",
  "settings.security.twoFactor.setup.title": "Lisää Scalephant todennussovellukseesi",
  "settings.security.twoFactor.setup.body":
    "Skannaa QR-koodi tai syötä avain käsin ja kirjoita sitten sovelluksen näyttämä koodi.",
  "settings.security.twoFactor.setup.key": "Avain",
  "settings.security.twoFactor.setup.confirm": "Vahvista koodi",
  "settings.security.twoFactor.setup.done":
    "Kaksivaiheinen tunnistus on päällä. Tallenna varakoodit turvalliseen paikkaan.",
  "settings.security.twoFactor.backupCodes": "Varakoodit",
  "settings.security.twoFactor.backupCodes.body":
    "Jokainen koodi toimii kerran, jos menetät todennussovelluksesi.",
  "settings.security.twoFactor.disabled": "Kaksivaiheinen tunnistus on pois päältä.",
  "settings.security.sessions.title": "Kirjautuneet laitteet",
  "settings.security.sessions.current": "Tämä laite",
  "settings.security.sessions.signOutAll": "Kirjaudu ulos kaikkialta",
  "settings.security.sessions.signOutOthers": "Kirjaa muut laitteet ulos",
  "settings.security.sessions.done": "Muut laitteet kirjattiin ulos.",
  "settings.security.sessions.unknownDevice": "Tuntematon laite",
  "settings.security.stepUp.title": "Vahvista, että olet sinä",
  "settings.security.stepUp.body": "Anna salasanasi tai pyydä koodi sähköpostiin jatkaaksesi.",
  "settings.security.stepUp.bodyPassword": "Anna salasanasi jatkaaksesi.",
  "settings.security.stepUp.codeSent": "Lähetimme koodin sähköpostiisi.",
  "settings.security.stepUp.resend": "Lähetä uusi koodi",
  "settings.security.stepUp.submit": "Vahvista",
  "settings.security.stepUp.method": "Vahvista käyttäen",
  "settings.security.stepUp.method.password": "Salasana",
  "settings.security.stepUp.method.email": "Lähetä koodi sähköpostiin",
  "settings.security.stepUp.error": "Tunnistus ei onnistunut. Yritä uudelleen.",
  "settings.billing.title": "Laskutus",
  "billing.intro": "Tilauksesi, kohtuukäyttösi ja laskusi.",
  "billing.notConfigured":
    "Maksuja ei ole vielä otettu käyttöön tällä palvelimella. Alla olevat paketit ovat vain tiedoksi.",
  "billing.checkout.success":
    "Kiitos! Stripe vahvistaa tilaustasi; tämä sivu päivittyy hetken kuluttua.",
  "billing.checkout.canceled": "Tilaus peruttiin. Mitään ei veloitettu.",
  "billing.current.title": "Nykyinen paketti",
  "billing.current.none": "Ei vielä tilausta. Valitse paketti aloittaaksesi.",
  "billing.current.trialActive":
    "Täysi autopilotti on käytössä {date} asti. Valitse paketti, jotta se jatkuu sen jälkeen.",
  "billing.current.trialEnded":
    "Kokeilu päättyi {date}. Autopilotti ja AI-päivitykset ovat tauolla, kunnes valitset paketin.",
  "billing.current.pending": "Odotetaan, että Stripe vahvistaa tilauksen…",
  "billing.status.trialing": "Kokeilu",
  "billing.status.active": "Aktiivinen",
  "billing.status.past_due": "Maksu myöhässä",
  "billing.status.canceled": "Peruttu",
  "billing.status.unpaid": "Maksamatta",
  "billing.status.incomplete": "Kesken",
  "billing.status.incomplete_expired": "Vanhentunut",
  "billing.status.paused": "Tauolla",
  "billing.period": "Nykyinen kausi {start} – {end}",
  "billing.cancelAtPeriodEnd": "Tilaus päättyy tämän kauden lopussa.",
  "billing.trialEnds": "Kokeilu päättyy {date}.",
  "billing.usage.title": "Käyttö tällä kaudella",
  "billing.usage.consumed": "Manuaalisen käytön kohtuukäyttörajasta käytetty {percent} %",
  "billing.usage.overage":
    "Ei automaattisia ylitysmaksuja. Ota yhteyttä tukeen, jos tarvitset enemmän manuaalista käyttöä.",
  "billing.usage.balance":
    "Automaattinen optimointi ei kuluta tätä rajaa. Eri manuaalisilla tekoälytehtävillä on eri käyttöpainot.",
  "billing.plans.title": "Paketit",
  "billing.plan.free.price": "Ilmainen",
  "billing.plan.perMonth": "{price} / kk",
  "billing.plan.campaigns.one": "1 aktiivinen kampanja",
  "billing.plan.campaigns.many": "Enintään {count} aktiivista kampanjaa",
  "billing.plan.credits": "Tekoälytyökalut tilauksen kohtuukäytöllä",
  "billing.plan.credits.once": "Tekoälytyökalut palvelun kokeiluun",
  "billing.plan.creatives":
    "{concepts} mainoskonseptia, {images} kuvaa ja {videos} videota per kampanja",
  "billing.plan.creatives.noVideo": "{concepts} mainoskonseptia ja {images} kuvaa per kampanja",
  "billing.plan.freeTrial": "{days} päivän täyden autopilotin kokeilu",
  "billing.plan.overage": "Kysy tuesta lisäkäyttöä",
  "billing.plan.noOverage": "Ei ylitysmaksuja; manuaalinen käyttö voi pysähtyä kohtuukäyttörajalla",
  "billing.plan.trial": "{days} päivän ilmainen kokeilu",
  "billing.plan.choose": "Valitse {plan}",
  "billing.plan.current": "Pakettisi",
  "billing.manage": "Hallitse laskutusta",
  "billing.manage.hint":
    "Vaihda pakettia, päivitä kortti tai lataa laskut Stripen asiakasportaalissa.",
  "billing.payments.title": "Maksut",
  "billing.payments.none": "Ei vielä maksuja.",
  "billing.payment.status.paid": "Maksettu",
  "billing.payment.status.failed": "Epäonnistui",
  "billing.payment.status.pending": "Odottaa",
  "billing.payment.status.refunded": "Hyvitetty",
  "billing.error.role": "Vain omistajat ja ylläpitäjät voivat hallita laskutusta.",
  "billing.error.alreadySubscribed":
    "Organisaatiolla on jo tilaus. Vaihda pakettia kohdasta Hallitse laskutusta.",
  "billing.error.noCustomer": "Laskutustiliä ei vielä ole. Valitse ensin paketti.",
  "billing.error.unavailable":
    "Maksut eivät ole juuri nyt käytettävissä. Yritä myöhemmin uudelleen.",
  "nav.admin": "Ylläpito",
  "admin.title": "Alustan ylläpito",
  "admin.intro": "Kaikki alustan organisaatiot. Täällä tehdyt toimet kirjataan lokiin nimelläsi.",
  "admin.twoFactorRequired":
    "Ylläpitotoimet vaativat kaksivaiheisen tunnistautumisen. Ota se ensin käyttöön turvallisuusasetuksissa.",
  "admin.organizations.title": "Organisaatiot",
  "admin.organizations.none": "Ei vielä organisaatioita.",
  "admin.organizations.members": "{count} jäsentä",
  "admin.organizations.noPlan": "Ei pakettia",
  "admin.organizations.balance": "{available} krediittiä käytettävissä",
  "admin.credits.title": "Muuta krediittejä: {organization}",
  "admin.credits.amount": "Krediittejä (negatiivinen vähentää)",
  "admin.credits.reason": "Perustelu",
  "admin.credits.submit": "Toteuta",
  "admin.credits.done": "Saldo on nyt {available} krediittiä.",
  "admin.credits.error.insufficient": "Organisaatiolla ei ole niin paljon krediittejä.",
  "admin.credits.open": "Muuta krediittejä",
  "admin.audit.title": "Viimeisimmät lokimerkinnät",
  "admin.audit.none": "Ei vielä merkintöjä.",
  "admin.audit.filter": "Organisaatio",
  "admin.audit.all": "Kaikki organisaatiot",
  "admin.audit.platform": "alusta",
  "admin.error.forbidden": "Tämä sivu on alustan henkilökunnalle.",
  "validation.nonZero": "Anna nollasta poikkeava määrä.",
  "validation.tooShort": "Liian lyhyt.",
  "validation.tooLong": "Liian pitkä.",
  "settings.privacy.title": "Tietosuoja",
  "privacy.intro": "Omat tietosi: ota niistä kopio tai poista tilisi.",
  "privacy.export.title": "Tietosi",
  "privacy.export.body":
    "Saat kopion sinusta tallennetuista henkilötiedoista JSON-tiedostona. Kopio valmistellaan taustalla ja lähetämme sähköpostin, kun se on valmis; lataus on saatavilla seitsemän päivää.",
  "privacy.export.request": "Pyydä kopio tiedoistani",
  "privacy.export.requested": "Valmistelemme kopiota. Saat sähköpostin, kun se on valmis.",
  "privacy.export.download": "Lataa",
  "privacy.export.none": "Ei vielä vientejä.",
  "privacy.export.status.requested": "Valmistellaan",
  "privacy.export.status.ready": "Valmis",
  "privacy.export.status.failed": "Epäonnistui",
  "privacy.export.status.expired": "Vanhentunut",
  "privacy.delete.title": "Poista tili",
  "privacy.delete.body":
    "Tilin poisto hävittää profiilisi, kirjautumistapasi ja istuntosi. Organisaatiot, joiden ainoa jäsen olet, poistetaan vasta, kun niiden mainoskampanjoiden ja tilauksen sulkeminen on turvallisesti vahvistettu. Jaetuista organisaatioista sinut poistetaan jäsenenä. Poisto alkaa heti ja jää odottamaan, jos alustojen sulkemista ei voida vahvistaa.",
  "privacy.delete.confirm": "Vahvista kirjoittamalla sähköpostiosoitteesi",
  "privacy.delete.submit": "Poista tilini",
  "privacy.delete.scheduled":
    "Poisto käynnistyy. Voit perua ennen alustatoimien alkamista; muuten jatkamme tarkistuksia, kunnes poisto voidaan vahvistaa.",
  "privacy.delete.blocked": "Poisto on pysähdyksissä: {reason}. Korjaa tilanne tai peru pyyntö.",
  "privacy.delete.blockedCampaigns":
    "Poistopyyntö odottaa: Scalephant säilyttää tilin ja kampanjat, kunnes mainosalustojen poisto voidaan turvallisesti vahvistaa. Tämä pyyntö ei ole vielä tehnyt alustapoistoa.",
  "privacy.delete.blockedVerification":
    "Poistopyyntö odottaa ulkoisten muutosten varmistusta. Tili ja kampanjoiden alustayhteydet säilyvät siihen asti.",
  "privacy.delete.removingCampaigns":
    "Poistamme kampanjasi mainosalustoilta. Tili poistetaan heti sen jälkeen, yleensä muutamassa minuutissa. Voit vielä perua pyynnön.",
  "privacy.delete.blockedBilling":
    "Tilin poisto on odotustilassa, koska tilauksen perumista ei voida vielä turvallisesti varmistaa. Tämä poistopyyntö ei yrittänyt perua tilausta. Ota yhteyttä tukeen tai peru poistopyyntö.",
  "privacy.delete.reconnect":
    "Viimeistele poisto yhdistämällä {platforms}-mainostilisi uudelleen Mainostilit-sivulla. Scalephantin kirjautuminen tiliin on vanhentunut, joten emme voi vielä pysäyttää kampanjoitasi siellä. Kun tili on yhdistetty, poistamme kampanjat ja tilisi automaattisesti.",
  "privacy.delete.processing":
    "Tilin poisto on käynnissä. Kampanjoiden ja tilauksen sulkemista vahvistetaan. Poistopyyntöä ei voi enää perua.",
  "privacy.delete.receiptPending":
    "Poistopyyntöä tarkistetaan. Tilisi poistumista ei ole vielä vahvistettu.",
  "privacy.delete.completed":
    "Tilisi on poistettu. Erillinen vahvistusviesti lähetetään, kun sen toimitus on mahdollista.",
  "privacy.delete.receiptExpired":
    "Poiston tila ei ole enää saatavilla tässä selaimessa. Tarkista erillinen vahvistus sähköpostistasi.",
  "privacy.delete.stopChecking": "Lopeta seuranta tässä selaimessa",
  "privacy.delete.follow": "Jatka seurantaa uloskirjautumisen jälkeen",
  "privacy.delete.cancel": "Peru poisto",
  "privacy.delete.canceled": "Poisto peruttiin.",
  "privacy.delete.soleOwner":
    "Olet organisaation {organizations} ainoa omistaja. Siirrä omistajuus tai poista muut jäsenet ennen tilin poistoa.",
  "privacy.delete.error.mismatch": "Tuo ei ole sähköpostiosoitteesi.",
  "organizations.title": "Organisaatiot",
  "organizations.body": "Toimit yhdessä organisaatiossa kerrallaan. Vaihda tässä.",
  "organizations.active": "Aktiivinen",
  "organizations.switched": "Työskentelet nyt organisaatiossa {organization}.",
  "organizations.switch": "Vaihda",
  "organizations.role.owner": "Omistaja",
  "organizations.role.admin": "Ylläpitäjä",
  "organizations.role.member": "Jäsen",
  "organizations.create.title": "Luo organisaatio",
  "organizations.create.name": "Organisaation nimi",
  "organizations.create.submit": "Luo",
  "organizations.create.done": "Organisaatio luotu.",
  "invitations.title": "Kutsu",
  "invitations.body": "Sinut on kutsuttu liittymään organisaatioon.",
  "invitations.accept": "Hyväksy kutsu",
  "invitations.accepted": "Liityit organisaatioon.",
  "invitations.error": "Kutsu ei ole enää voimassa.",
  "home.signedInAs": "Kirjautuneena: {email}",
  "nav.pricing": "Hinnat",
  "nav.status": "Tila",
  "nav.privacy": "Tietosuoja",
  "nav.subprocessors": "Alikäsittelijät",
  "nav.terms": "Käyttöehdot",
  "marketing.nav.label": "Sivusto",
  "marketing.cta.start": "Aloita",
  "marketing.footer.product": "Tuote",
  "marketing.footer.legal": "Ehdot",
  "marketing.footer.contact": "Yhteystiedot",
  "marketing.footer.copyright": "© {year} Scalephant. Kaikki oikeudet pidätetään.",
  "marketing.footer.googleData":
    "Kun yhdistät Google Adsin, Scalephant käyttää oikeutta vain pyytämiesi kampanjoiden ajamiseen ja verkkosivustosi konversioiden lähettämiseen omalle Google Ads -tilillesi.",
  "marketing.footer.googleDataLink": "Näin käsittelemme Googlelta saatuja tietoja",
  "marketing.hero.badge": "Markkinointitiimisi ympäri vuorokauden",
  "marketing.hero.title1": "Tuotteesi on automatisoitu.",
  "marketing.hero.title2": "Markkinoinnin pitäisi olla myös.",
  "marketing.hero.lead":
    "Scalephant on markkinointitiimi, joka työskentelee ympäri vuorokauden Googlessa ja Metassa: tekee uusia mainoksia, suojaa budjettiasi ja muuttaa kampanjadatan paremmiksi seuraaviksi siirroiksi.",
  "marketing.hero.cta": "Ota Scalephant töihin",
  "marketing.hero.ctaPricing": "Katso hinnat",
  "marketing.hero.trust.noCard": "Ei luottokorttia",
  "marketing.hero.trust.trial": "{days} päivän täyden autopilotin kokeilu",
  "marketing.hero.trust.platforms": "Valmis Googleen ja Metaan",
  "marketing.hero.fineprint":
    "Sinä päätät budjetin. Mainosraha menee suoraan Googlelle ja Metalle.",
  "marketing.beyond.badge": "Enemmän kuin mainosten jakelua",
  "marketing.beyond.title":
    "Google ja Meta jakavat mainokset. Scalephant hoitaa markkinoinnin niiden takana.",
  "marketing.beyond.body1":
    "Tiimisi pitää kampanjat uusissa mainoksissa, valvoo jokaista budjettisääntöä ja siirtää opit alustalta toiselle.",
  "marketing.beyond.body2":
    "Scalephant kokoaa mainokset, tulokset ja budjettipäätökset yhdeksi jatkuvaksi järjestelmäksi, joten Google ja Meta katsovat samaa kuvaa liiketoiminnastasi.",
  "marketing.beyond.body3":
    "Aseta tavoite ja rajat kerran. Markkinointitiimisi vie työn eteenpäin.",
  "marketing.beyond.point.creative": "Uudet mainokset antavat alustoille jatkuvasti vaihtoehtoja",
  "marketing.beyond.point.rules": "Budjettisääntöjäsi valvotaan ympäri vuorokauden",
  "marketing.beyond.point.lessons": "Hyödylliset opit kulkevat Googlen ja Metan välillä",
  "marketing.beyond.point.oneplace": "Yksi paikka, josta näet mitä tapahtuu",
  "marketing.flow.google": "Haku ja Performance Max",
  "marketing.flow.meta": "Facebook ja Instagram",
  "marketing.flow.portfolio": "Yksi budjettisalkku",
  "marketing.flow.rebalanced": "Tasapainotetaan päivittäin",
  "marketing.pillars.title": "Kolme tehtävää, jotka markkinointitiimisi hoitaa",
  "marketing.pillars.lead":
    "Uudet mainokset, viisaampi rahankäyttö ja yhdistetty oppiminen: työ, joka pitää kampanjat liikkeessä Googlessa ja Metassa.",
  "marketing.pillars.creative.title": "Mainostehdas",
  "marketing.pillars.creative.body":
    "Uudet mainostekstit, kuvat ja videot antavat Googlelle ja Metalle enemmän vaihtoehtoja, ilman että sinun tarvitsee ylläpitää sisältökalenteria.",
  "marketing.pillars.money.title": "Rahan vartija",
  "marketing.pillars.money.body":
    "Budjettiasi, kulutusrajojasi ja poikkeavaa toimintaa seurataan jatkuvasti rajoilla, joita alustat eivät sinulle rakenna.",
  "marketing.pillars.brain.title": "Alustojen yhteinen aivo",
  "marketing.pillars.brain.body":
    "Googlen ja Metan opit pysyvät yhteydessä, joten kumpikin alusta voi ohjata seuraavaa siirtoa sen sijaan että eläisi omassa siilossaan.",
  "marketing.pillars.closing":
    "Mainostehdas, rahan vartija ja alustojen yhteinen aivo: yksi tiimi, joka pitää jokaisen kampanjan liikkeessä.",
  "marketing.steps.title": "Markkinointitiimi, joka aloittaa briiffistäsi eikä lopeta työpäivää.",
  "marketing.steps.lead":
    "Yhdistä Google ja Meta, aseta suunta, ja Scalephant huolehtii arjen etenemisestä.",
  "marketing.steps.brief.title": "Anna tiimillesi briiffi",
  "marketing.steps.brief.body":
    "Yhdistä Google ja Meta, kerro mitä myyt ja aseta budjetti ja rajat, jotka määrittelevät hyvän työn.",
  "marketing.steps.supply.title": "Pidä jokainen kampanja materiaalissa",
  "marketing.steps.supply.body":
    "Scalephant tekee uusia mainoksia ja videoita, testaa uusia kulmia ja uudistaa mainokset, kun tulokset sitä vaativat.",
  "marketing.steps.watch.title": "Pysy kärryillä jokaisesta signaalista",
  "marketing.steps.watch.body":
    "Budjetteja, poikkeavaa toimintaa ja tuloksia seurataan jatkuvasti molemmilla alustoilla, yötä päivää.",
  "marketing.video.badge": "Mainostehdas",
  "marketing.video.title": "Tuoreisiin mainoksiin tarvitaan enemmän kuin yksi hyvä idea",
  "marketing.video.body1":
    "Nykyaikaisella mainonnan jakelulla on enemmän vaihtoehtoja, kun sille antaa tasaisen virran uusia mainoksia. Scalephant tekee valmiita videomainoksia, jotta työ ei muutu taas yhdeksi kalenterimerkinnäksi.",
  "marketing.video.body2":
    "Jokaisessa videossa on ruututekstit, kohtaussuunnitelma, spiikin käsikirjoitus ja tekstitykset, jos haluat hioa niitä. Tuoreisiin videoihin ei kosketa, ja uudistukset noudattavat pakettisi käyttörajoja.",
  "marketing.video.point.finished": "Valmiit videot, käyttövalmiina sellaisenaan",
  "marketing.video.point.captions": "Ruututekstit ja tekstitykset mukana",
  "marketing.video.point.script": "Jokaisen videon mukana tulee spiikin käsikirjoitus",
  "marketing.video.point.refresh": "Uusi mainos, kun aktiivinen mainos sitä tarvitsee",
  "marketing.safety.badge": "Rahan vartija",
  "marketing.safety.title": "Rahan vartija, joka tuntee rajasi",
  "marketing.safety.lead":
    "Google ja Meta jakavat ne mainokset, jotka niille annat. Ne eivät tiedä, kuinka paljon riskiä siedät. Scalephant noudattaa asettamiasi rajoja ja etsii hukkaa ennen kuin sitä kertyy lisää.",
  "marketing.safety.cap.title": "Pidä kulutuskatto näkyvissä",
  "marketing.safety.cap.body":
    "Toteutunutta kulutusta verrataan asettamaasi rajaan. Jos kampanja ylittää sen, Scalephant voi pysäyttää sen.",
  "marketing.safety.cpa.title": "Estä kalliita kampanjoita jatkamasta huomaamatta",
  "marketing.safety.cpa.body":
    "Jos kampanja alkaa maksaa asiakasta kohden enemmän kuin siedät, sitä voidaan hidastaa tai se voidaan pysäyttää. Sinä päätät, milloin se käynnistetään uudelleen.",
  "marketing.safety.policy.title": "Vältä turhat yllätykset julkaisussa",
  "marketing.safety.policy.body":
    "Mainosluonnokset tarkistetaan alustojen sääntöjä vasten ennen julkaisua, joten ongelmat voi korjata ennen kuin ne viivästyttävät.",
  "marketing.safety.closing":
    "Sinä asetat rajat. Scalephant hoitaa rutiinivalvonnan, joka kiireessä jää helposti tekemättä.",
  "marketing.features.title": "Jatkuva mainostyö, hoidettuna puolestasi",
  "marketing.features.lead":
    "Työ ei ole vain kampanjan saaminen käyntiin. Se on mainosten pitämistä tuoreina, rahankäytön järkevänä ja oppien yhteydessä toisiinsa, kun tili muuttuu.",
  "marketing.features.brain.title": "Alustojen yhteinen aivo",
  "marketing.features.brain.forecast.title": "Näe vaihtoehdot ennen kuin käytät rahaa",
  "marketing.features.brain.forecast.body":
    "Laske ensin. Vertaa todennäköistä kulutusta ja asiakashintaa muutamalla budjettivaihtoehdolla ennen kuin euroakaan lähtee.",
  "marketing.features.brain.portfolio.title": "Anna molempien alustojen ohjata suunnitelmaa",
  "marketing.features.brain.portfolio.body":
    "Googlea ja Metaa hallitaan yhdessä, joten Scalephant voi siirtää budjettia kanavalle, joka tuo asiakkaat halvemmalla.",
  "marketing.features.brain.market.title": "Pidä silmällä markkinaasi",
  "marketing.features.brain.market.body":
    "Scalephant yhdistää markkinatiedon, hakutrendit ja omat tuloksesi ja löytää hyödyllisiä mahdollisuuksia.",
  "marketing.features.brain.actions.title": "Muuta opit käytännön muutoksiksi",
  "marketing.features.brain.actions.body":
    "Hyödylliset hakusanat, budjettivalinnat ja viesti-ideat muuttuvat selkeiksi seuraaviksi askeliksi sen perusteella, mitä tilillä tapahtuu.",
  "marketing.features.brain.digest.title": "Tiedä mitä tapahtui ilman kaivelua",
  "marketing.features.brain.digest.body":
    "Selkeä viikkokooste kertoo, mikä muuttui, mikä toimi ja mitä Scalephant seuraa seuraavaksi.",
  "marketing.features.brain.memory.title": "Rakenna oman tilisi havaintojen varaan",
  "marketing.features.brain.memory.body":
    "Jokainen kampanja jättää jälkeensä hyödyllistä taustaa seuraavalle sen sijaan että aloittaisit joka kerta tyhjästä.",
  "marketing.features.creative.title": "Mainostehdas",
  "marketing.features.creative.copy.title": "Brändinmukaiset mainokset silloin kun tarvitset",
  "marketing.features.creative.copy.body":
    "Otsikot, mainostekstit ja kampanjaideat kirjoitetaan tuotteesi ja tavoitteesi ympärille, joten testattavaa on aina.",
  "marketing.features.creative.voice.title": "Pidä äänensävy tunnistettavasti omanasi",
  "marketing.features.creative.voice.body":
    "Scalephant käyttää sinun sävyäsi, kehotuksiasi ja tuotteesi vahvuuksia yleisen tekoälytekstin sijaan.",
  "marketing.features.creative.retargeting.title": "Tuo ihmiset takaisin selkeällä tarinalla",
  "marketing.features.creative.retargeting.body":
    "Kun ihmiset ovat osoittaneet kiinnostusta, Scalephant voi valmistella mainossarjan, joka antaa syyn palata.",
  "marketing.features.creative.video.title": "Pidä videomainokset valmiina",
  "marketing.features.creative.video.body":
    "Valmiit videot voivat ilmestyä tilillesi käsikirjoituksen, tekstitysten ja kohtaussuunnitelman kanssa, kun haluat hioa niitä.",
  "marketing.features.creative.testing.title": "Anna hyville ideoille tilaa näyttää kykynsä",
  "marketing.features.creative.testing.body":
    "Scalephant voi ajaa useaa versiota, seurata niiden tuloksia ja pitää vahvemman mukana.",
  "marketing.features.creative.fatigue.title": "Vaihda mainokset ennen kuin niistä tulee tapettia",
  "marketing.features.creative.fatigue.body":
    "Scalephant seuraa mainoksia, joita ihmiset ovat nähneet liian usein, ja tekee tilaa uudelle.",
  "marketing.features.creative.offers.title": "Opi, mihin tarjoukseen ihmiset tarttuvat",
  "marketing.features.creative.offers.body":
    "Testaa tarjouksia, kuten kokeilujaksoa, alennusta tai takuuta, ei vain eri sanoja, ja käytä sitä mitä tili oppii.",
  "marketing.features.money.title": "Rahan vartija",
  "marketing.features.money.dayparting.title": "Kohdista budjettia hyödyllisiin hetkiin",
  "marketing.features.money.dayparting.body":
    "Scalephant katsoo, milloin asiakkaat yleensä toimivat, ja voi kohdistaa budjettia näihin hetkiin.",
  "marketing.features.money.negatives.title": "Lopeta maksaminen epäolennaisista hauista",
  "marketing.features.money.negatives.body":
    "Haut, jotka eivät selvästi liity myymääsi, löydetään ja estetään, joten ne eivät kuluta budjettiasi.",
  "marketing.features.money.guardrails.title": "Aseta kulutusrajat, jotka pysyvät päällä",
  "marketing.features.money.guardrails.body":
    "Jos kampanja alkaa maksaa enemmän kuin asettamasi raja, Scalephant voi hidastaa tai pysäyttää sen.",
  "marketing.features.money.anomaly.title": "Huomaa poikkeava kulutus aiemmin",
  "marketing.features.money.anomaly.body":
    "Äkilliset muutokset kulutuksessa tai tuloksissa tarkistetaan heti eikä vasta seuraavassa raportissa.",
  "marketing.features.money.pacing.title": "Jaa budjetti tasaisesti kuukaudelle",
  "marketing.features.money.pacing.body":
    "Kuukausibudjettiasi seurataan päivä kerrallaan, joten se ei todennäköisesti katoa heti kuun alussa.",
  "marketing.features.money.frequency.title": "Vältä saman mainoksen liiallista toistoa",
  "marketing.features.money.frequency.body":
    "Scalephant seuraa, kuinka usein ihmiset näkevät mainoksesi, ja voi vetäytyä, jos sama yleisö näkee liikaa.",
  "marketing.features.money.policy.title": "Havaitse sääntöongelmat ennen julkaisua",
  "marketing.features.money.policy.body":
    "Mainosluonnokset tarkistetaan alustojen sääntöjä vasten, joten ongelmat voi korjata ennen kuin ne haittaavat julkaisua.",
  "marketing.features.money.housekeeping.title": "Hoida tärkeät rutiinit",
  "marketing.features.money.housekeeping.body":
    "Budjettimuutokset, mainosten uudistukset ja kampanjasäädöt jonotetaan ja hoidetaan ilman että sinun tarvitsee asua mainostyökalussa.",
  "marketing.features.picture.title": "Yksi selkeä kuva",
  "marketing.features.picture.attribution.title": "Näe mistä asiakkaat tulivat",
  "marketing.features.picture.attribution.body":
    "Kampanjat ja asiakkaiden toimet yhdistetään, joten näet selvemmin, mitä kukin kanava tuo.",
  "marketing.features.picture.journey.title": "Näe asiakaspolku yhdessä paikassa",
  "marketing.features.picture.journey.body":
    "Suunnittele ja tarkastele mainoksia, jotka esittelevät, selittävät ja myyvät tuotteesi, ilman hyppimistä alustalta toiselle.",
  "marketing.features.picture.types.title": "Käytä Googlen kampanjatyyppejä harkiten",
  "marketing.features.picture.types.body":
    "Kun toista Googlen kampanjatyyppiä kannattaa harkita, Scalephant auttaa punnitsemaan valinnan ennen aloitusta.",
  "marketing.features.picture.lessons.title": "Vie opit seuraavaan kampanjaan",
  "marketing.features.picture.lessons.body":
    "Tilisi oppimat hyödylliset asiat, viesteistä yleisöihin ja formaatteihin, ovat käytettävissä, kun seuraavaa kampanjaa valmistellaan.",
  "marketing.features.picture.oneplace.title": "Yksi paikka Googlelle ja Metalle",
  "marketing.features.picture.oneplace.body":
    "Aloita yhdestä kampanjabriiffistä ja tarkastele molempia alustoja yhdeltä kojelaudalta ilman raporttien yhdistelyä.",
  "marketing.features.picture.value.title": "Muista mitä asiakas on arvoltaan",
  "marketing.features.picture.value.body":
    "Lisää keskiostoksesi tai tilauksen hinta, jotta päätöksissä voidaan katsoa muutakin kuin klikkien määrää.",
  "marketing.cross.badge": "Alustojen yhteinen aivo",
  "marketing.cross.title": "Se mitä Google opettaa, voi auttaa Metaa. Ja toisin päin.",
  "marketing.cross.body1":
    "Viesti, yleisö tai mainosformaatti, joka osoittautuu toimivaksi yhdellä alustalla, ei saisi jäädä sinne. Scalephant vie hyödylliset opit kampanjoiden yli ja Googlen ja Metan välillä, kun seuraavaa suunnitellaan.",
  "marketing.cross.body2": "Saat yhden yhtenäisen kuvan kahden erillisen tilin sijaan.",
  "marketing.final.title": "Anna markkinoinnillesi aina paikalla oleva tiimi.",
  "marketing.final.body":
    "Aloita {days} päivän täyden autopilotin kokeilu ja ota Scalephant töihin Googleen ja Metaan.",
  "marketing.final.cta": "Aloita ilmaiseksi",
  "marketing.final.fineprint":
    "Korttia ei tarvita. Ilmaisiin tileihin sisältyy rajattu käyttö tekoälyn tekemiin mainoksiin ja videoihin.",
  "pricing.title": "Selkeä ja ennustettava hinnoittelu.",
  "pricing.lead":
    "Maksat niistä kampanjoista, joita ajat. Autopilotin optimointi sisältyy hintaan, ja jokainen paketti kertoo mainosmääränsä.",
  "pricing.perMonth": "/ kk",
  "pricing.forever": "ikuisesti",
  "pricing.popular": "Suosituin",
  "pricing.plan.free.description": "Täydellinen tapa nähdä, miten autopilotti toimii.",
  "pricing.plan.starter.description": "Sivuprojekteille ja pienille kokeiluille.",
  "pricing.plan.growth.description": "Vakavasti kasvua hakevalle yksinyrittäjälle.",
  "pricing.plan.scale.description": "Suurta volyymia kunnianhimoisille mainostajille.",
  "pricing.plan.scale_50.description": "Maksimivolyymi nopeasti kasvaville tuotteille.",
  "pricing.plan.concepts": "Enintään {concepts} otsikko- ja viestikulmaa kampanjaa kohden",
  "pricing.plan.media.one": "{images} mainoskuvaa ja yksi video kampanjaa kohden",
  "pricing.plan.media.many": "{images} mainoskuvaa ja {videos} videota kampanjaa kohden",
  "pricing.plan.media.imagesOnly": "{images} mainoskuvaa kampanjaa kohden",
  "pricing.plan.free.noCard": "Ei luottokorttia, ei koskaan",
  "pricing.cta.choose": "Valitse {plan}",
  "pricing.custom.question": "Ajatko yli 50 kampanjaa?",
  "pricing.custom.contact": "Ota yhteyttä",
  "pricing.custom.rest": "niin räätälöimme suuren volyymin paketin tarpeisiisi.",
  "pricing.included.title": "Mitä jokaiseen maksulliseen pakettiin sisältyy",
  "pricing.included.lead": "Ei toimintokohtaista hinnoittelua. Yksi tilaus kattaa kaiken.",
  "pricing.included.planning": "Tuoteanalyysi ja kampanjasuunnittelu",
  "pricing.included.creatives": "Mainostekstit ja mainosten luonti",
  "pricing.included.forecasts": "Ennusteet ja uudelleenmarkkinoinnin analyysi",
  "pricing.included.video": "Videomainosten käsikirjoitukset ja renderöinti",
  "pricing.included.autopilot":
    "Autopilotin ajastetut työt: budjettisiirrot, poikkeamien havaitseminen, ajankohtien painotus ja mainosväsymyksen seuranta",
  "pricing.fairUse":
    "Kohtuukäyttö: käsin käynnistetty tekoälyn käyttö, kuten lisämainokset, ennusteet ja uudelleenmarkkinoinnin analyysi, sisältyy jokaisen maksullisen paketin reiluun kuukausikiintiöön, eikä tavallinen käyttö yllä siihen. Julkaisutyöt ja pyytämäsi työt menevät edelle. Autopilotin optimointi ei kuluta kohtuukäyttöä.",
  "pricing.fairUse.link": "Lue kohtuukäytön ehdot",
  "status.page.title": "Palvelun tila",
  "status.page.lead":
    "Reaaliaikainen tarkistus Scalephantin rajapintaa vasten. Liitä alla olevat tiedot mukaan, jos otat yhteyttä tukeen.",
  "status.title": "Rajapinnan tila",
  "status.ok": "Rajapinta vastaa normaalisti.",
  "status.checking": "Tarkistetaan rajapintaa…",
  "status.unavailable": "Rajapinta ei vastannut: {detail}",
  "status.noCorrelationId": "ei korrelaatiotunnusta",
  "status.service": "Palvelu",
  "status.environment": "Ympäristö",
  "status.version": "Versio",
  "status.versionDev": "kehitysversio",
  "status.serverTime": "Palvelimen aika",
  "settings.tracking.title": "Konversioseuranta",
  "settings.tracking.backToCampaign": "Takaisin kampanjaan",
  "tracking.copied": "Kopioitu",
  "tracking.paths.title": "Valitse miten konversiot ilmoitetaan",
  "tracking.paths.intro":
    "Käytä selaimen kiitossivua tai lähetä vahvistetut ostot ja liidit palvelimeltasi. Molempia ei tarvita.",
  "tracking.paths.browser": "Käytä kiitossivua",
  "tracking.paths.browserInstead": "Käytä sen sijaan kiitossivua",
  "tracking.paths.server": "Käytä palvelinpuolen seurantaa",
  "tracking.paths.detectedServer":
    "Palvelinseuranta on käytössä. Selaimen skripti ja kiitossivusääntö ovat valinnaisia; niitä ei tarvita palvelimelta tulevien konversioiden laskemiseen.",
  "tracking.checklist.serverTitle": "Palvelinpuolen seuranta",
  "tracking.checklist.serverEvent": "Lähetä vahvistettu konversio palvelimeltasi",
  "tracking.checklist.serverEvent.todo":
    "Luo alla pelkkään seurantaan rajattu avain ja lähetä tapahtuma taustajärjestelmästäsi toteutuneen oston tai liidin jälkeen.",
  "tracking.checklist.serverEvent.done":
    "Palvelintapahtuma on saapunut. Tämä polku ei tarvitse selaimen skriptiä tai kiitossivusääntöä.",
  "tracking.checklist.serverEvent.waiting":
    "Avain on valmis. Odotetaan ensimmäistä tapahtumaa palvelimeltasi. Muuta ei tarvita.",
  "tracking.checklist.serverWaiting":
    "Palvelintapahtuma on saapunut, mutta vahvistettua konversiota ei vielä lasketa. Tarkista viimeaikaiset konversiot alta.",
  "tracking.checklist.browserWaiting":
    "Palvelimelta tulevia konversioita lasketaan, mutta tällä kiitossivupolulla ei ole vielä vahvistettu selaimen konversiota.",
  "tracking.checklist.title": "Käyttöönotto kolmessa vaiheessa",
  "tracking.checklist.done": "Valmis",
  "tracking.checklist.pending": "Tekemättä",
  "tracking.checklist.step1": "Asenna snippetti sivustollesi",
  "tracking.checklist.step1.todo":
    "Liitä alla olevat kaksi riviä sivustosi <head>-osaan. Mitään ei ole vielä saapunut.",
  "tracking.checklist.step1.done": "Sivustosi raportoi. Viimeisin sivunäyttö {when}.",
  "tracking.checklist.step2": "Kerro mikä on konversio",
  "tracking.checklist.step2.todo":
    "Nimeä sivu jolle kävijä päätyy konversion jälkeen, valitse havaittu signaali tai raportoi palvelimeltasi.",
  "tracking.checklist.step2.done": "Konversiosääntö on asetettu.",
  "tracking.checklist.step2.viaServer": "Konversiot saapuvat palvelimeltasi — sääntöä ei tarvita.",
  "tracking.checklist.step3": "Näe ensimmäisen konversion saapuvan",
  "tracking.status.nothing": "Yhtään sivunäyttöä ei ole vielä saapunut.",
  "tracking.status.pageviews_only":
    "Sivustosi raportoi sivunäyttöjä, mutta yhtään konversiota ei ole vahvistettu.",
  "tracking.status.signals_only":
    "Toimintaa saapuu, mutta mitään ei ole vielä vahvistettu konversioksi — vahvista sääntö tai raportoi palvelimeltasi.",
  "tracking.status.rule_waiting": "Sääntösi on vahvistettu. Odotetaan ensimmäistä konversiota.",
  "tracking.status.counting": "Konversiot lasketaan. Muuta ei tarvitse tehdä.",
  "tracking.install.title": "Asenna snippetti",
  "tracking.install.intro":
    "Liitä tämä julkinen skripti jokaisen sivun <head>-osaan, myös kiitossivulle. Se kirjaa sivunäyttöjä eikä ole salainen API-avain. Käy sivustollasi ja tarkista alta viimeisin havainto.",
  "tracking.install.copy": "Kopioi snippetti",
  "tracking.install.lastSeen": "Nähty viimeksi {when}",
  "tracking.install.siteHost": "Sivustosi verkkotunnus",
  "tracking.install.siteHost.hint":
    "Automaattinen tunnistus luottaa vain tältä verkkotunnukselta tuleviin signaaleihin.",
  "tracking.originHint.mismatch":
    "Signaaleja saapuu osoitteesta {observedHost}, mutta rekisteröity sivusto on {registeredHost}. Rekisteröi verkkotunnus, jolla snippetti oikeasti on.",
  "tracking.originHint.noSite":
    "Signaaleja saapuu osoitteesta {observedHost}. Rekisteröi se verkkotunnus, jotta niihin voidaan luottaa.",
  "tracking.originHint.missingHeader":
    "Signaaleja saapuu ilman selaimen alkuperää, mikä tarkoittaa yleensä että lähettäjä on jokin muu kuin selain.",
  "tracking.serverAuthority.quiet":
    "Palvelimesi ei ole raportoinut konversiota kahteen päivään. Jos sen olisi pitänyt, tarkista integraatio.",
  "tracking.rules.title": "Mikä lasketaan konversioksi",
  "tracking.rules.intro":
    "Kun julkinen skripti on asennettu, lisää sivu jolle kävijä päätyy vain onnistuneen oston, rekisteröitymisen tai liidin jälkeen, esimerkiksi /kiitos. Valitse konversiotyyppi. Testaa toteutunut konversio ja tarkista viimeaikaiset konversiot alta. Arvattu sääntö on vahvistettava; lomakkeen lähetys voi laskea myös epäonnistuneita yrityksiä.",
  "tracking.rules.empty": "Ei vielä sääntöä.",
  "tracking.rules.confirm": "Vahvista",
  "tracking.rules.delete": "Poista",
  "tracking.rule.urlSentence": "Käynnit sivulla {match} lasketaan: {type}",
  "tracking.rule.formSentence": "Lomakkeen lähetykset sivulla {match} lasketaan: {type}",
  "tracking.provenance.manual": "Oma",
  "tracking.provenance.confirmed": "Vahvistettu",
  "tracking.provenance.auto_promoted": "Arvattu",
  "tracking.thankYou.label": "Sivu jonka kävijä näkee konversion jälkeen",
  "tracking.thankYou.labelAnother": "Lisää toinen sivu, jonka kävijä näkee konversion jälkeen",
  "tracking.thankYou.hint": "Polku kuten /kiitos, tai koko osoite.",
  "tracking.thankYou.add": "Lisää sääntö",
  "tracking.thankYou.error.empty":
    "Anna sivu jonka kävijä näkee konversion jälkeen, esim. /kiitos.",
  "tracking.thankYou.error.invalid_url":
    "Tuo ei näytä kelvolliselta osoitteelta — anna pelkkä polku, esim. /kiitos.",
  "tracking.thankYou.error.homepage":
    "Käytä tiettyä sivua — etusivu laskisi jokaisen kävijän konversioksi.",
  "tracking.eventType.label": "Lasketaan",
  "tracking.eventType.choose": "Valitse…",
  "tracking.eventType.missing": "Valitse, miksi sivu lasketaan.",
  "tracking.eventType.purchase": "Osto",
  "tracking.eventType.lead": "Liidi",
  "tracking.eventType.signup": "Rekisteröityminen",
  "tracking.candidates.title": "Havaittu sivustollasi",
  "tracking.candidates.intro":
    "Kun sääntöä ei ole, snippetti panee merkille saavutetut sivut, lähetetyt lomakkeet ja painikkeiden klikkaukset — vain polut ja painikkeiden tekstit, ei koskaan kirjoitettua sisältöä.",
  "tracking.candidates.empty":
    "Ei vielä havaintoja. Signaalit ilmestyvät tänne, kun kävijät käyttävät sivustoasi.",
  "tracking.candidates.seen": "nähty {count} kertaa",
  "tracking.candidates.suggestedHigh": "Todennäköisesti tämä",
  "tracking.candidates.suggestedMedium": "Mahdollisesti tämä",
  "tracking.candidates.use": "Käytä tätä",
  "tracking.candidates.clickNoRule": "klikkauksista ei voi vielä tehdä sääntöä",
  "tracking.candidate.url": "Kävijät päätyvät sivulle {match}",
  "tracking.candidate.form": "Joku lähettää lomakkeen sivulla {match}",
  "tracking.candidate.click": 'Klikkaukset: "{label}"',
  "tracking.stuck.title": "Automaattinen tunnistus ei etene tällä sivustolla",
  "tracking.stuck.no_success_page":
    "{days} päivän ja {sightings} signaalin ajan etsimme sivua, jolle kävijä päätyy vain konversion jälkeen — kiitos- tai vahvistussivua. Sivustollasi ei näytä olevan sellaista, joten mikään ei kelvannut.",
  "tracking.stuck.success_page_unverified":
    "Vahvistussivulta näyttävä sivu nähtiin, mutta ei tarpeeksi usein rekisteröidyltä verkkotunnukseltasi, jotta se olisi voitu valita kysymättä.",
  "tracking.stuck.why.click": "painikkeiden klikkauksista ei voi tehdä sääntöä",
  "tracking.stuck.why.ordinary_page":
    "näyttää tavalliselta sivulta, ei konversion jälkeen saavutetulta",
  "tracking.stuck.why.unverified": "ei nähty tarpeeksi usein rekisteröidyltä verkkotunnukseltasi",
  "tracking.stuck.why.form_attempts": "lomakkeen lähetys laskee yritykset, ei onnistumisia",
  "tracking.stuck.why.not_a_success_page": "ei näytä vahvistussivulta",
  "tracking.stuck.serverSide":
    "Luotettava tie ulos: raportoi konversiot palvelimeltasi alla olevan osion avaimella.",
  "tracking.stuck.formRule": "Tai laske jokainen lomakkeen lähetys sivulla {match}:",
  "tracking.stuck.formRule.accept": "Laske lomakkeen lähetykset",
  "tracking.stuck.formRule.caveat": "Laskee myös yritykset, jotka epäonnistuvat tai jäävät kesken.",
  "tracking.keys.title": "Raportoi palvelimeltasi",
  "tracking.keys.intro":
    "Taustajärjestelmä tai maksuwebhook voi raportoida vahvistetut konversiot suoraan. Tämä polku ei vaadi selaimen skriptiä tai kiitossivusääntöä. Pelkkä palvelintapahtuma ei todista konversion laskentaa; tarkista viimeaikaiset konversiot alta.",
  "tracking.keys.safety":
    "Seurannan API-avain on salainen. Älä koskaan lisää sitä selaimeen, sivuston HTML-koodiin, skriptiin tai muuhun asiakaspuolen koodiin. Säilytä se vain palvelimen salaisuuksissa.",
  "tracking.keys.scopeOnly":
    "Tämä integraatioavain saa vain tracking:write-oikeuden; sillä ei voi käyttää agenttitoimintoja.",
  "tracking.keys.eventIdCaveat":
    "Käytä todellisen tilauksen tai liidin yksilöllistä, pysyvää eventId-tunnistetta. Käytä samaa tunnistetta saman tapahtuman uusintayrityksissä; eri tunniste samalle konversiolle voi laskea sen kahdesti. Vaihda esimerkin tunniste ja summa ennen lähetystä.",
  "tracking.keys.visitorHint":
    "Lähetä myös visitorId, niin konversio kirjataan mainokselle, josta kävijä tuli. Scalephant lisää sen sivustosi lomakkeisiin piilokenttänä scalephant_visitor_id.",
  "tracking.keys.shownOnce": "Uusi avaimesi. Se näytetään vain tämän kerran — kopioi se nyt.",
  "tracking.keys.copy": "Kopioi avain",
  "tracking.keys.empty": "Ei vielä palvelinpuolen avainta.",
  "tracking.keys.waiting": "Odotetaan ensimmäistä tapahtumaa palvelimeltasi…",
  "tracking.keys.lastReceived": "Viimeisin konversio vastaanotettu {when}",
  "tracking.keys.column.name": "Nimi",
  "tracking.keys.column.prefix": "Avain",
  "tracking.keys.column.lastUsed": "Käytetty viimeksi",
  "tracking.keys.column.actions": "Toiminnot",
  "tracking.keys.revoke": "Mitätöi",
  "tracking.keys.name": "Avaimen nimi",
  "tracking.keys.defaultName": "Palvelinintegraatio",
  "tracking.keys.create": "Luo avain",
  "tracking.keys.roleNeeded": "Vain omistaja tai ylläpitäjä voi luoda tai mitätöidä avaimia.",
  "tracking.keys.sampleIntro": "Sitten aina, kun palvelimesi vahvistaa konversion:",
  "tracking.pixel.title": "Meta-pikseli",
  "tracking.pixel.intro":
    "Snippetti lataa tämän pikselin sivustollesi ja lähettää sille sivunkatselut ja vahvistetut konversiot, jotta Metan optimointi oppii oikeista käynneistä ja tuloksista. Tarkistetaan päivittäin.",
  "tracking.pixel.none":
    "Pikseliä ei ole valittu. Vahvistetut konversiot lasketaan silti täällä; Metan selainpikseli ei laukea.",
  "tracking.pixel.lastEvent": "Lauennut viimeksi {when}",
  "tracking.pixel.clear": "Lopeta käyttö",
  "tracking.pixel.connectAccount": "Yhdistä Meta-tili",
  "tracking.pixel.return": "Palaa pikselin valintaan",
  "tracking.pixel.accountConnected":
    "Meta-tili yhdistetty. Pikselin valinta ja tapahtumien varmistaminen ovat erillisiä vaiheita.",
  "tracking.pixel.accountMissing": "Ei yhdistettyä Meta-tiliä.",
  "tracking.pixel.conflict":
    "Sivustollasi on jo Meta-pikseli; Scalephant lähettää konversiot vain palvelimelta.",
  "tracking.consent.bannerMissing":
    "Sivustollasi ei ole evästebanneria. Euroopasta tulevia kävijöitä ei seurata, eivätkä heidän konversionsa välity mainosalustoille, ennen kuin lisäät bannerin. Scalephant tunnistaa Google Consent Moden, IAB TCF:n, Cookiebotin ja CookieYesin.",
  "tracking.pixel.empty":
    "Yhdistetyiltä Meta-tileiltä ei löytynyt pikseleitä. Tarkista pikselin käyttöoikeudet Metan yritysasetuksista.",
  "tracking.pixel.accounts": "Mainostilit",
  "tracking.pixel.retry": "Yritä pikselilistaa uudelleen",
  "tracking.pixel.choose": "Valitse pikseli",
  "tracking.pixel.select": "Pikseli",
  "tracking.pixel.selectPlaceholder": "Valitse…",
  "tracking.pixel.connectFirst": "Yhdistä ensin Meta-mainostili:",
  "tracking.pixel.status.ok": "Raportoi",
  "tracking.pixel.status.never_fired": "Ei koskaan lauennut",
  "tracking.pixel.status.stale": "Hiljentynyt",
  "tracking.pixel.status.waiting": "Odottaa konversiota",
  "tracking.pixel.status.missing": "Ei löydy",
  "tracking.pixel.status.unavailable": "Ei käytettävissä",
  "tracking.pixel.advice.never_fired":
    "Pikseli ei ole vielä lauennut. Avaa sivustosi kerran, niin pikseli herää.",
  "tracking.pixel.advice.stale":
    "Konversioita on tullut pikselin viimeisen laukeamisen jälkeen, mutta Meta ei nähnyt niitä. Tarkista, että snippetti on yhä asennettuna.",
  "tracking.pixel.advice.waiting":
    "Pikselin viimeisen laukeamisen jälkeen ei ole tullut konversioita, joten sillä ei ole ollut lähetettävää. Jos konversio ei tavoita Metaa, se näkyy tässä.",
  "tracking.pixel.advice.missing":
    "Tätä pikseliä ei enää ole millään yhdistetyllä Meta-tilillä. Valitse toinen.",
  "tracking.pixel.advice.unavailable":
    "Meta ilmoittaa pikselin olevan poissa käytöstä — tarkista Events Manager.",
  "tracking.conversions.title": "Viimeisimmät konversiot",
  "tracking.conversions.summary":
    "{conversions} viimeisen 30 päivän aikana, {confirmed} vahvistettua, {unattributed} ei vielä yhdistetty kampanjaan.",
  "tracking.conversions.empty": "Ei konversioita viimeisen 30 päivän aikana.",
  "tracking.conversions.count": "{count} konversiota",
  "tracking.conversions.column.when": "Milloin",
  "tracking.conversions.column.type": "Tyyppi",
  "tracking.conversions.column.source": "Lähde",
  "tracking.conversions.column.value": "Arvo",
  "tracking.conversions.column.status": "Tila",
  "tracking.conversions.suppressed": "Palvelin raportoi",
  "tracking.conversions.confirmed": "Vahvistettu",
  "tracking.conversions.signal": "Signaali",
  "tracking.source.snippet": "Sivusto",
  "tracking.source.server": "Palvelin",
  "nav.insights": "Näkemykset",
  "nav.audiences": "Yleisöt",
  "dashboard.autopilot.on":
    "Autopilotti on päällä. Se testaa mainostekstejä, siirtää budjettia rajojesi sisällä ja raportoi jokaisen toimen tähän.",
  "dashboard.autopilot.off": "Autopilotti on pois päältä. Kampanjat pyorivät sellaisinaan.",
  "dashboard.autopilot.settings": "Autopilotin asetukset",
  "dashboard.feed.title": "Tämä viikko",
  "dashboard.feed.summary": "Autopilotti teki {actions} toimea, {alerts} asiaa vaatii katseesi.",
  "dashboard.insight.title": "Viikkoraportti",
  "dashboard.insight.none":
    "Ensimmäinen raportti tulee ensimmäisen kokonaisen viikon jälkeen, kun kampanja on käynnissä.",
  "dashboard.insight.more": "Kaikki raportit",
  "nav.main": "Päävalikko",
  "brand.next": "Seuraavaksi",
  "dashboard.band.autopilotOn": "Autopilotti on käynnissä",
  "dashboard.band.autopilotOff": "Autopilotti on pois päältä",
  "dashboard.band.review": "Katso, mikä odottaa sinua",
  "dashboard.autopilot.enabled": "Päällä",
  "dashboard.autopilot.disabled": "Pois",
  "dashboard.autopilot.waiting": "Odottaa käynnissä olevaa kampanjaa.",
  "dashboard.connect.title": "Yhdistä mainostili",
  "dashboard.connect.text":
    "Valitse, missä kampanjasi pyörivät. Rahaa ei kulu ennen kuin käynnistät kampanjan.",
  "dashboard.brandVoice.tone": "Sävy: {tone}",
  "dashboard.retargeting.title": "Uudelleenmarkkinointi",
  "dashboard.retargeting.progress": "{count}/{total} konversiota",
  "dashboard.settings.title": "Työtilan asetukset",
  "activity.title": "Tapahtumat",
  "activity.window": "Viimeiset {days} päivää",
  "activity.alertsOnly": "Vain huomiota vaativat",
  "activity.empty": "Tässä ajassa ei ole tapahtunut mitään.",
  "activity.by.system": "Autopilotti",
  "activity.by.job": "Autopilotti",
  "activity.by.user": "Sinä",
  "activity.by.admin": "Ylläpito",
  "activity.alert": "Katso tämä",
  "activity.kind.created": "Kampanja luotiin",
  "activity.kind.launched": "Julkaistiin",
  "activity.kind.launch_failed": "Julkaisu epäonnistui",
  "activity.kind.paused": "Tauolle",
  "activity.kind.resumed": "Jatkettiin",
  "activity.kind.budget_changed": "Budjettia muutettiin",
  "activity.kind.targeting_changed": "Kohdennusta muutettiin",
  "activity.kind.copy_generated": "Mainostekstit kirjoitettiin",
  "activity.kind.images_generated": "Kuvat luotiin",
  "activity.kind.copy_pushed": "Tekstit julkaistiin alustalle",
  "activity.kind.copy_push_failed": "Tekstejä ei saatu julkaistua alustalle",
  "activity.kind.asset_attached": "Kuva liitettiin",
  "activity.kind.metrics_synced": "Luvut haettiin",
  "activity.kind.guardrail_paused": "Kustannusvahti pysäytti",
  "activity.kind.guardrail_reinstated": "Jatkettiin kustannusvahdin jälkeen",
  "activity.kind.anomaly_detected": "Luvuissa jyrkkä muutos",
  "activity.kind.cap_reached": "Kuukausiraja täyttyi",
  "activity.kind.platform_rejected": "Alusta hylkäsi",
  "activity.kind.archived": "Arkistoitiin",
  "activity.kind.unmanaged": "Ei enää hallinnassa",
  "activity.kind.automation_paused": "Autopilotti pysäytettiin tälle kampanjalle",
  "activity.kind.automation_resumed": "Autopilotti jatkaa tätä kampanjaa",
  "activity.kind.budget_rebalanced": "Budjettia tasattiin alustojen välillä",
  "activity.kind.budget_shifted": "Budjettia siirrettiin paremmalle alustalle",
  "activity.kind.frequency_reduced": "Budjettia laskettiin: mainos näkyi liian usein",
  "activity.kind.anomaly_corrected": "Budjettia korjattiin jyrkän muutoksen jälkeen",
  "activity.kind.spend_change_expected": "Kulutus muuttuu: budjettia siirrettiin",
  "activity.kind.variants_generated": "Uusia mainosversioita kirjoitettiin",
  "activity.kind.ab_round_started": "A/B-testikierros alkoi",
  "activity.kind.ab_winner_selected": "A/B-voittaja valittiin",
  "activity.kind.ab_losers_paused": "Hävinneet versiot tauolle",
  "activity.kind.fatigue_detected": "Mainos alkaa kulua",
  "activity.kind.creative_refreshed": "Uudet mainokset jonossa",
  "activity.kind.claim_audit_flagged": "Väite vaatii tarkistuksen",
  "activity.kind.claim_audit_resolved": "Väitteen tarkistus selviäi",
  "activity.kind.search_terms_negated": "Turhat hakusanat suljettiin pois",
  "activity.kind.keywords_added": "Myyneet haut lisättiin avainsanoiksi",
  "activity.kind.keywords_paused": "Heikot avainsanat tauolle",
  "activity.kind.schedule_applied": "Mainosaikataulu otettiin käyttöön",
  "activity.kind.schedule_reverted": "Mainosaikataulu poistettiin",
  "activity.kind.schedule_skipped": "Mainosaikataulu ohitettiin: liian vähän dataa",
  "activity.kind.goal_lead_promoted": "Tavoite nostettiin liideihin",
  "activity.kind.goal_graduated": "Tavoite nostettiin myyntiin",
  "activity.kind.goal_graduation_volume_low": "Myyntitavoitteeseen on vielä liian vähän myyntiä",
  "activity.kind.goal_graduation_reverted": "Tavoite palautettiin askel taaksepäin",
  "activity.kind.targeting_adjusted": "Kohdennusta säädettiin toisen alustan tulosten perusteella",
  "activity.kind.intel_refreshed": "Kilpailijaraportti päivitettiin",
  "activity.kind.intel_refresh_failed": "Kilpailijaraportti epäonnistui",
  "activity.kind.suggestion_created": "Uusia ehdotuksia",
  "activity.kind.suggestion_applied": "Ehdotus toteutettiin",
  "activity.kind.suggestion_dismissed": "Ehdotus hylättiin",
  "activity.kind.pmax_ready": "Valmis Performance Maxiin",
  "activity.kind.video_script_generated": "Videon käsikirjoitus kirjoitettiin",
  "activity.kind.video_render_started": "Videon renderöinti alkoi",
  "activity.kind.video_rendered": "Video renderöitiin",
  "activity.kind.video_render_failed": "Videon renderöinti epäonnistui",
  "activity.kind.video_scene_repaired": "Videon kohtaus renderöitiin uudelleen",
  "activity.kind.video_attached": "Video liitettiin kampanjaan",
  "activity.kind.video_attach_failed": "Videota ei saatu liitettyä",
  "activity.kind.video_attach_skipped": "Videon liittäminen ohitettiin",
  "activity.kind.story_sequence_generated": "Tarinasarja kirjoitettiin",
  "activity.kind.pmax_assets_attached": "Performance Maxin kuvapaikat täytettiin",
  "activity.kind.pmax_assets_attach_failed": "Performance Maxin kuvapaikkoja ei saatu täytettyä",
  "activity.kind.audience_created": "Uudelleenmarkkinoinnin kohderyhmä luotiin",
  "activity.kind.audience_synced": "Uudelleenmarkkinoinnin kohderyhmä synkronoitiin",
  "activity.kind.audience_sync_failed": "Kohderyhmän synkronointi epäonnistui",
  "activity.kind.retargeting_layer_added": "Uudelleenmarkkinoinnin kerros lisättiin",
  "activity.kind.retargeting_layer_skipped": "Uudelleenmarkkinoinnin kerros ohitettiin",
  "activity.kind.retargeting_layer_failed": "Uudelleenmarkkinoinnin kerrosta ei saatu lisättyä",
  "activity.kind.retargeting_layer_confirmed": "Uudelleenmarkkinoinnin kerros vahvistui alustalla",
  "activity.kind.creative_draft_started": "Ensimmäisten mainosten kirjoitus aloitettiin",
  "activity.kind.creative_draft_finished": "Ensimmäiset mainokset odottavat tarkistusta",
  "activity.kind.creative_draft_failed": "Ensimmäisiä mainoksia ei saatu kirjoitettua",
  "activity.kind.own_creatives_added": "Omia mainoksia tai kuvia lisättiin",
  "activity.kind.creative_source_changed": "Scalephant täydentää nyt jatkossakin automaattisesti",
  "activity.kind.sales_campaign_opened":
    "Autopilotti avasi tavoitteesi kampanjan, ja se korvaa klikkikampanjan",
  "activity.kind.sales_campaign_paused":
    "Tavoitekampanja pysäytettiin, ja klikkikampanja on taas käynnissä.",
  "activity.kind.goal_campaign_kept":
    "Tavoitekampanja tuotti 28 päivässä paremmin kuin klikkikampanja ja jää käyttöön.",
  "activity.kind.bidding_switched":
    "Autopilotti vaihtoi tämän Google-kampanjan hinnoittelemaan tavoitteesi mukaan klikkien sijaan.",
  "activity.kind.bidding_returned":
    "Tämä Google-kampanja hinnoittelee taas klikkien mukaan, koska tavoitteen mukainen hinnoittelu ei tuottanut paremmin.",
  "activity.kind.bidding_kept":
    "Tavoitteen mukainen hinnoittelu tuotti 28 päivässä paremmin kuin klikkien mukainen ja jää käyttöön.",
  "activity.kind.pmax_launched":
    "Google alkoi testata Performance Max -mainoksia osalla tämän kampanjan budjetista. Kokonaisbudjettisi ei muuttunut.",
  "activity.kind.pmax_kept":
    "Performance Max -mainokset toivat konversioita vähintään yhtä edullisesti kuin pelkät hakumainokset ja jäävät käyttöön.",
  "activity.kind.pmax_stopped":
    "Performance Max -mainokset pysäytettiin, ja niiden budjetti palasi tälle kampanjalle.",
  "activity.kind.keywords_expanded":
    "Lisäsimme uusia hakusanoja, koska mainoksesi näkyivät harvoin.",
  "intel.sales.googleWaiting":
    "Kampanja alkaa hinnoitella myyntien mukaan automaattisesti, kun sivustollesi on kertynyt {threshold} myyntiä 30 päivässä (nyt {count}).",
  "intel.sales.bidding":
    "Kampanja hinnoittelee myyntien mukaan. Se palaa klikkeihin, jos tulos ei parane 28 päivässä.",
  "intel.goal.signup.googleWaiting":
    "Kampanja alkaa hinnoitella rekisteröitymisten mukaan automaattisesti, kun sivustollesi on kertynyt {threshold} rekisteröitymistä 30 päivässä (nyt {count}).",
  "intel.goal.signup.bidding":
    "Kampanja hinnoittelee rekisteröitymisten mukaan. Se palaa klikkeihin, jos tulos ei parane 28 päivässä.",
  "intel.goal.lead.googleWaiting":
    "Kampanja alkaa hinnoitella liidien mukaan automaattisesti, kun sivustollesi on kertynyt {threshold} liidiä 30 päivässä (nyt {count}).",
  "intel.goal.lead.bidding":
    "Kampanja hinnoittelee liidien mukaan. Se palaa klikkeihin, jos tulos ei parane 28 päivässä.",
  "settings.autopilot.title": "Autopilotti",
  "settings.autopilot.intro":
    "Mitä autopilotti saa tehdä itsenäisesti. Sen toimet näkyvät tapahtumissa; kulutusrajat ohjaavat budjettipäätöksiä.",
  "settings.autopilot.enabled": "Autopilotti päällä",
  "settings.autopilot.enabledHint":
    "Pois päältä tarkoittaa, että jokainen kampanja pyorii sellaisenaan; mitään ei testata eikä siirretä.",
  "settings.autopilot.caps.title": "Kulutuskatot",
  "settings.autopilot.caps.intro":
    "Budjettipäätösten rajat kampanjaryhmien kuukausitavoitteiden lisäksi. Jätä tyhjäksi, jos rajaa ei ole.",
  "settings.autopilot.currency": "Kattojen valuutta",
  "settings.autopilot.maxMonthly": "Kuukausikatto",
  "settings.autopilot.maxMonthlyHint":
    "Jos mainosalusta ei vahvista pysäytystä, mainostilin kulutus voi ylittää tämän katon.",
  "settings.autopilot.maxDaily": "Päiväkatto",
  "settings.autopilot.requireApproval": "Budjetin korotukset vaativat hyväksyntäni",
  "settings.autopilot.requireApprovalHint":
    "Automaattiset korotukset muuttuvat ehdotuksiksi, jotka hyväksyt; laskut tapahtuvat edelleen itsestään.",
  "settings.autopilot.systems.title": "Järjestelmät",
  "settings.autopilot.systems.intro":
    "Jokaisen voi kytkeä pois erikseen. Yllä oleva pääkytkin ohittaa ne kaikki.",
  "settings.autopilot.system.rebalancing": "Tasaa budjettia alustojen välillä",
  "settings.autopilot.system.arbitrage": "Siirrä budjettia edullisemmin konvertoivalle alustalle",
  "settings.autopilot.system.frequency": "Laske budjettia, kun mainos näkyy liian usein",
  "settings.autopilot.system.abTesting": "Testaa mainostekstiversioita (A/B)",
  "settings.autopilot.system.fatigueRefresh": "Uudista kuluvat mainokset",
  "settings.autopilot.system.dayparting": "Ajoita mainokset konvertoiviin tunteihin",
  "settings.autopilot.system.searchTerms": "Sulje pois turhat hakusanat (Google)",
  "settings.autopilot.system.goalLadder": "Nosta tavoitetta tulosten karttuessa",
  "settings.autopilot.system.competitiveIntel": "Kilpailijaraportit",
  "settings.autopilot.system.weeklyInsights": "Viikkoraportit",
  "settings.autopilot.system.retargeting": "Uudelleenmarkkinointi",
  "settings.autopilot.save": "Tallenna autopilotin asetukset",
  "settings.autopilot.roleNeeded": "Vain omistaja tai ylläpitäjä voi muuttaa näitä.",
  "settings.autopilot.error.currencyRequired": "Valitse katoille valuutta.",
  "settings.autopilot.error.invalidAmount":
    "Syötä nollaa suurempi kokonaissumma tai jätä tyhjäksi.",
  "campaigns.tab.overview": "Yleiskuva",
  "campaigns.tab.creatives": "Mainokset",
  "campaigns.tab.intel": "Tiedustelu",
  "campaigns.tab.forecast": "Ennuste",
  "campaigns.tab.policy": "Autopilotti",
  "campaigns.tab.activity": "Tapahtumat",
  "campaigns.tab.video": "Video",
  "campaigns.tab.story": "Tarina",

  "videos.title": "Videomainokset",
  "videos.intro":
    "Käsikirjoitus kirjoitetaan heti. Renderöinti tapahtuu media-palvelussa ja kestää minuutteja; rivi kertoo monesko yritys on menossa.",
  "videos.none": "Ei vielä videokäsikirjoituksia.",
  "videos.generate": "Kirjoita videokäsikirjoitus",
  "videos.render": "Renderöi",
  "videos.queued":
    "Jonossa. Renderöinti kestää muutaman minuutin; sivu päivittyy kun se valmistuu.",
  "videos.format": "{aspect}, noin {seconds} s, {scenes} kohtausta",
  "videos.marked": "Merkitty tekoälyn tekemäksi itse tiedostoon",
  "videos.attemptsLeft": "{left} renderöintiyritystä jäljellä",
  "videos.status.pending": "Ei renderöity",
  "videos.status.rendering": "Renderöidään",
  "videos.status.done": "Renderöity",
  "videos.status.failed": "Renderöinti epäonnistui",
  "videos.status.superseded": "Korvattu",
  "videos.hosted.link": "Liitä YouTube-video",
  "videos.hosted.field": "YouTube-videon tunnus",
  "videos.hosted.linked": "Liitetty YouTube-videoon {id}",
  "videos.hosted.invalid":
    "Tämä ei ole YouTube-videon tunnus. Liitä yksitoista merkkiä v=-osoitteen jälkeen, ei koko osoitetta.",

  "stories.title": "Tarinasekvenssit",
  "stories.intro":
    "Kolme mainosta, jotka kertovat yhden tarinan: ongelma kylmälle yleisölle, ratkaisu katsoneille, päätös sivustolla käyneille.",
  "stories.none": "Ei vielä tarinasekvenssejä.",
  "stories.generate": "Kirjoita tarinasekvenssi",
  "stories.act": "Näytös {number}",
  "stories.stage.cold": "Kylmä yleisö",
  "stories.stage.video_viewers": "Katsoi näytöksen 1",
  "stories.stage.site_visitors": "Kävi sivustolla",
  "stories.targetingNotes": "Miten kohdennus toimii",
  "stories.setupSteps": "Miten tämä yleisö rakennetaan",

  "audiences.title": "Uudelleenmarkkinoinnin yleisöt",
  "audiences.intro":
    "Scalephant lisää uudelleenmarkkinoinnin jo käynnissä olevaan kampanjaan; se ei koskaan luo erillistä.",
  "audiences.none":
    "Ei vielä yleisöjä. Yleisö luodaan kun konversioita on tarpeeksi uudelleenmarkkinointia varten.",
  "audiences.source.website_visitors": "Sivustolla kävijät",
  "audiences.source.customer_list": "Asiakaslista",
  "audiences.source.lookalike": "Kaltaisyleisö",
  "audiences.retention": "Katsoo {days} päivää taaksepäin",
  "audiences.size": "Noin {size} ihmistä",
  "audiences.sizeUnknown": "Kokoa ei ole vielä ilmoitettu",
  "audiences.state.pending": "Odottaa",
  "audiences.state.syncing": "Synkronoidaan",
  "audiences.state.synced": "Synkronoitu",
  "audiences.state.failed": "Epäonnistui",
  "audiences.ready": "Valmis uudelleenmarkkinointiin",
  "audiences.notReady.not_enough_conversions":
    "Konversioita ei ole vielä tarpeeksi. Uudelleenmarkkinointi alkaa kun niitä on kymmenen.",
  "audiences.notReady.pixel_stale":
    "Metan pikselin ei ole havaittu laukeavan viime aikoina. Tarkista sivustosi snippetti.",
  "audiences.conversions": "{count} konversiota kirjattu",
  "audiences.layers": "Kampanjat joissa on uudelleenmarkkinointikerros",
  "audiences.layer.pending": "Vahvistetaan alustalla",
  "audiences.layer.confirmed": "Käynnissä",

  "apiKeys.scopes": "Mitä tämä avain saa tehdä",
  "apiKeys.scope.tracking:write": "Lähettää konversioita",
  "apiKeys.scope.agent:read": "Lukea kampanjoita ja lukuja",
  "apiKeys.scope.agent:write":
    "Kirjoittaa luonnoksia ja tekstejä (lasketaan manuaaliseen kohtuukäyttöön)",
  "policy.title": "Mitä autopilotti saa tehdä tälle kampanjalle",
  "policy.active":
    "Autopilotti saa optimoida tätä kampanjaa: testata tekstejä, siirtää budjettia alla olevissa rajoissa ja säätää kohdennusta näytön perusteella.",
  "policy.paused":
    "Autopilotti jättää tämän kampanjan rauhaan. Se pyorii edelleen; mitään ei testata eikä siirretä.",
  "policy.pausedSince": "Tauolla alkaen {when}.",
  "policy.pause": "Pysäytä autopilotti tälle kampanjalle",
  "policy.resume": "Anna autopilotin optimoida taas",
  "policy.unmanaged":
    "Tämä kampanja pyorii tilillä, jonka yhteys katkaistiin. Mikään täällä ei voi vaikuttaa siihen.",
  "policy.group.title": "Tämä kampanjaryhmä",
  "policy.group.target": "Kuukausitavoite: {amount}",
  "policy.group.noTarget": "Ryhmälle ei ole asetettu kuukausitavoitetta.",
  "policy.org.title": "Organisaatiosi rajat",
  "policy.org.disabled": "Autopilotti on pois päältä koko organisaatiolta.",
  "policy.org.monthlyCap": "Kuukausikatto: {amount}",
  "policy.org.dailyCap": "Päiväkatto: {amount}",
  "policy.org.noCaps": "Kattoja ei ole asetettu; jokaisen ryhmän oma tavoite on raja.",
  "policy.org.approval": "Budjetin korotukset odottavat hyväksyntääsi.",
  "policy.org.settings": "Muuta organisaation autopilotin asetuksia",
  "policy.roleNeeded": "Vain omistaja tai ylläpitäjä voi muuttaa tätä.",
  "intel.report.title": "Kilpailijaraportti",
  "intel.report.none":
    "Ei vielä raporttia. Se tehdään julkaisun jälkeen, sitten kuukausittain tai kun tulokset notkahtavat.",
  "intel.report.generated": "Luotu {when} ({reason}), toimiala: {industry}",
  "intel.report.reason.no_report": "ensimmäinen raportti",
  "intel.report.reason.monthly": "kuukausittainen",
  "intel.report.reason.post_launch": "julkaisun jälkeen",
  "intel.report.reason.performance_dip": "tulokset notkahtivat",
  "intel.report.reason.manual": "pyynnöstä",
  "intel.report.competitors": "Kilpailijat",
  "intel.report.themes": "Viestinnän teemat",
  "intel.report.formats": "Mainosmuodot",
  "intel.report.opportunities": "Mahdollisuudet",
  "intel.report.basedOn": "Perustuu: {sources}",
  "intel.source.webResearch": "verkkotutkimus",
  "intel.source.platformSignals": "alustan signaalit",
  "intel.source.performanceData": "tämän kampanjan tulokset",
  "intel.source.none": "ei vielä dataa",
  "intel.refresh": "Päivitä raportti nyt",
  "intel.refreshQueued":
    "Jonossa. Uusi raportti ilmestyy tähän muutamassa minuutissa. Pyydetty päivitys lasketaan manuaaliseen kohtuukäyttöön, ajastettu optimointi ei.",
  "intel.rules.title": "Ohjeet ja neuvot",
  "intel.rules.none": "Raportista ei vielä ole ohjeita.",
  "intel.rules.dismiss": "Hylkää",
  "intel.rule.type.creative_direction": "Luova suunta",
  "intel.rule.type.messaging_angle": "Viestin kärki",
  "intel.rule.type.add_keywords": "Lisättävät avainsanat",
  "intel.rule.type.add_negative_keywords": "Poissuljettavat avainsanat",
  "intel.rule.type.budget_shift": "Budjetin siirto",
  "intel.rule.directive": "Ohjaa seuraavia tekstejä",
  "intel.rule.advisory": "Vain neuvo",
  "intel.rule.tainted": "Rakennettu asiakkaiden hakulauseista; ei käytetä automaattisesti",
  "intel.adjustments.title": "Kohdennuksen säädöt",
  "intel.adjustments.none": "Ei vielä säätöjä toisen alustan tulosten perusteella.",
  "intel.adjustment.kind.google_demographic_focus":
    "Google: keskity segmenttiin, jonka Meta konvertoi",
  "intel.adjustment.kind.meta_geo_focus": "Meta: keskity alueeseen, jonka Google konvertoi",
  "intel.adjustment.kind.pmax_graduation": "Performance Max kannattaa kokeilla",
  "intel.adjustment.status.proposed": "Ehdotettu",
  "intel.adjustment.status.applying": "Toteutetaan",
  "intel.adjustment.status.applied": "Toteutettu",
  "intel.adjustment.status.failed": "Epäonnistui",
  "intel.adjustment.status.dismissed": "Hylätty",
  "intel.adjustment.status.expired": "Vanhentunut",
  "intel.adjustment.dismiss": "Hylkää",
  "intel.adjustment.pmaxNote":
    "Tämä ei ole julkaisu. Automaattinen siirtymä ei ole käytössä ennen kuin varmennettu julkaisu- ja seurantapolku on yhdistetty. Pidä hakukampanja käynnissä; tarkista vaatimukset ennen PMax-kokeilua Google Adsissa.",
  "intel.adjustment.pmaxRecorded": "Päätös kirjattu",
  "intel.adjustment.pmaxRecordedNote":
    "Julkaisua ei ole vahvistettu. Kirjattu päätös ei todista, että PMax-kampanja on Google Adsissa.",
  "intel.adjustment.pmaxConversions":
    "{count} / 30 vahvistettua konversiota viimeisen 30 päivän aikana (tuntematon ilman varmennusta).",
  "intel.adjustment.pmaxDays": "{count} / 25 täyttä mittauspäivää viimeisen 30 päivän aikana.",
  "intel.adjustment.pmaxTracking":
    "Konversioseurantaa ei ole vahvistettu tässä; varmista laadukkaat konversiot ennen siirtymää.",
  "intel.adjustment.pmaxAssets":
    "PMax-kuvien valmiutta ei ole vahvistettu; tarkista hyväksytyt kuvat ja laskeutumissivu.",
  "intel.adjustment.pmaxBudgetUnknown":
    "Päiväbudjettia ei ole vahvistettu. Säilytä haun vähimmäisbudjetti ja noudata ryhmän kuukausikattoa.",
  "intel.adjustment.pmaxBudget":
    "Arvioitu pilottibudjetti vähintään {amount} päivässä; säilytä haun vähimmäisbudjetti ja ryhmän kuukausikatto.",
  "intel.suggestions.title": "Ehdotukset",
  "intel.suggestions.intro":
    "Mikään täällä ei tapahdu ennen kuin hyväksyt sen. Hyväksyminen tekee saman tarkistetun muutoksen, jonka voisit tehdä itse.",
  "intel.suggestions.none": "Ei avoimia ehdotuksia.",
  "intel.suggestion.kind.budget_increase": "Nosta päiväbudjettia",
  "intel.suggestion.kind.budget_decrease": "Laske päiväbudjettia",
  "intel.suggestion.kind.creative_refresh": "Uudista mainokset",
  "intel.suggestion.kind.targeting_change": "Muuta kohdennusta",
  "intel.suggestion.kind.bid_adjustment": "Säädä hintatarjouksia",
  "intel.suggestion.status.new": "Avoin",
  "intel.suggestion.status.accepted": "Hyväksytty",
  "intel.suggestion.status.applied": "Toteutettu",
  "intel.suggestion.status.dismissed": "Hylätty",
  "intel.suggestion.status.expired": "Vanhentunut",
  "intel.suggestion.value": "Ehdotettu päiväbudjetti: {amount}",
  "intel.suggestion.accept": "Hyväksy ja toteuta",
  "intel.suggestion.dismiss": "Hylkää",
  "intel.suggestion.adviceOnly": "Neuvo: tätä ei voi toteuttaa automaattisesti.",
  "intel.suggestion.applied": "Toteutettu. Muutos näkyy tapahtumissa.",
  "intel.suggestion.refused": "Muutosta ei sallittu: {reason}",
  "intel.suggestion.expires": "Avoinna {when} asti",
  "intel.ladder.title": "Tavoiteportaat",
  "intel.ladder.rung.clicks": "Klikit",
  "intel.ladder.rung.leads": "Liidit",
  "intel.ladder.rung.sales": "Myynti",
  "intel.ladder.current": "Optimoidaan: {rung}",
  "intel.ladder.explicit": "Valitsit tämän tavoitteen; portaat eivät koske siihen.",
  "intel.ladder.auto": "Autopilotti nostaa tavoitetta, kun oikeita liidejä ja myyntiä kertyy.",
  "intel.ladder.promoted": "Nostettiin liideihin {when}",
  "intel.ladder.graduated": "Nostettiin myyntiin {when}",
  "intel.ladder.reverted": "Palautettiin {when}",
  "intel.ladder.volumeLow": "Myyntimäärä merkittiin vähäiseksi {when}",
  "intel.sales.waiting":
    "Myyntikampanja käynnistyy automaattisesti, kun sivustollesi on kertynyt {threshold} myyntiä 30 päivässä (nyt {count}).",
  "intel.sales.budgetTooSmall":
    "Päiväbudjetti on pienempi kuin kampanja tarvitsee oppiakseen, joten myyntikampanjaa ei vielä avata.",
  "intel.sales.running":
    "Myyntikampanja on käynnissä tämän tilalla. Tämä palaa, jos se ei tuota paremmin 28 päivässä.",
  "intel.sales.exhausted":
    "Kaksi myyntikampanjaa ei tuonut myyntejä, joten autopilotti ei enää avaa uutta.",
  "intel.sales.isSales":
    "Tämä myyntikampanja optimoi ostoille. Autopilotti avasi sen, kun myyntejä oli kertynyt tarpeeksi.",
  "intel.goal.signup.waiting":
    "Rekisteröitymiskampanja käynnistyy automaattisesti, kun sivustollesi on kertynyt {threshold} rekisteröitymistä 30 päivässä (nyt {count}).",
  "intel.goal.signup.budgetTooSmall":
    "Päiväbudjetti on pienempi kuin kampanja tarvitsee oppiakseen, joten rekisteröitymiskampanjaa ei vielä avata.",
  "intel.goal.signup.running":
    "Rekisteröitymiskampanja on käynnissä tämän tilalla. Tämä palaa, jos se ei tuota paremmin 28 päivässä.",
  "intel.goal.signup.exhausted":
    "Kaksi rekisteröitymiskampanjaa ei tuonut rekisteröitymisiä, joten autopilotti ei enää avaa uutta.",
  "intel.goal.signup.isSales":
    "Tämä kampanja optimoi rekisteröitymisille. Autopilotti avasi sen, kun rekisteröitymisiä oli kertynyt tarpeeksi.",
  "intel.goal.lead.waiting":
    "Liidikampanja käynnistyy automaattisesti, kun sivustollesi on kertynyt {threshold} liidiä 30 päivässä (nyt {count}).",
  "intel.goal.lead.budgetTooSmall":
    "Päiväbudjetti on pienempi kuin kampanja tarvitsee oppiakseen, joten liidikampanjaa ei vielä avata.",
  "intel.goal.lead.running":
    "Liidikampanja on käynnissä tämän tilalla. Tämä palaa, jos se ei tuota paremmin 28 päivässä.",
  "intel.goal.lead.exhausted":
    "Kaksi liidikampanjaa ei tuonut liidejä, joten autopilotti ei enää avaa uutta.",
  "intel.goal.lead.isSales":
    "Tämä kampanja optimoi liideille. Autopilotti avasi sen, kun liidejä oli kertynyt tarpeeksi.",
  "intel.goalSwitch.platformWaiting.meta":
    "Sivustollasi on tarpeeksi konversioita. Vaihto odottaa, että Meta on saanut niistä vähintään puolet (nyt {count}/{needed}).",
  "intel.goalSwitch.platformWaiting.google":
    "Sivustollasi on tarpeeksi konversioita. Vaihto odottaa, että Google-mainoksistasi on tullut niistä {needed} (nyt {count}).",
  "intel.goal.purchase.unmeasured": "Tavoitteesi on myynti, mutta mikään sivu ei vielä laske sitä.",
  "intel.goal.signup.unmeasured":
    "Tavoitteesi on rekisteröitymiset, mutta mikään sivu ei vielä laske niitä.",
  "intel.goal.lead.unmeasured": "Tavoitteesi on liidit, mutta mikään sivu ei vielä laske niitä.",
  "intel.insights.title": "Mitä ryhmän toinen alusta oppi",
  "intel.insights.none": "Ei vielä jaettavaa alustojen välillä.",
  "intel.insights.kind.winning_angle": "Voittava kärki",
  "intel.insights.kind.customer_language": "Asiakkaiden omat sanat",
  "intel.insights.kind.avoid_language": "Vältettävät sanat",
  "intel.insights.kind.audience_segment": "Konvertoiva segmentti",
  "intel.insights.kind.comparative_note": "Alustavertailu",
  "intel.insights.kind.fatigue_theme": "Kulunut teema",
  "forecast.title": "Jos muutan budjettia, mitä voin odottaa?",
  "forecast.intro":
    "Perustuu ensin tämän kampanjan omiin tuloksiin, sitten alustan arvioihin ja vasta sitten toimialan vertailuarvoihin. Kaikki mittaamaton on haarukka, ei fakta.",
  "forecast.budgets": "Ennustettavat kuukausibudjetit pilkuilla eroteltuina",
  "forecast.budgetsHint": "Valuutassa {currency}, kokonaissummina. Enintään kuusi.",
  "forecast.submit": "Ennusta",
  "forecast.invalid": "Syötä yhdestä kuuteen nollaa suurempaa kokonaissummaa.",
  "forecast.noCredits":
    "Manuaalisen käytön raja täyttyi. Kokeile kauden vaihduttua tai ota yhteyttä tukeen.",
  "forecast.unavailable": "Ennustaminen ei ole käytössä tällä palvelimella.",
  "forecast.latest": "Ennuste ajalta {when}",
  "forecast.none": "Ei vielä ennustetta.",
  "forecast.grounding.measured": "Perustuu tämän kampanjan mitattuihin tuloksiin",
  "forecast.grounding.platform": "Käyttää alustan yleisö- ja avainsana-arvioita",
  "forecast.grounding.benchmarks":
    "Vain suuntaa antava: toimialan vertailuarvot, ei vielä omaa dataa",
  "forecast.column.budget": "Kuukausibudjetti",
  "forecast.column.impressions": "Näytöt",
  "forecast.column.clicks": "Klikit",
  "forecast.column.conversions": "Konversiot",
  "forecast.column.cpa": "Hinta per konversio",
  "forecast.column.roas": "ROAS",
  "forecast.column.ctr": "CTR",
  "forecast.column.confidence": "Luottamus",
  "forecast.confidence.high": "Korkea",
  "forecast.confidence.medium": "Keskitaso",
  "forecast.confidence.low": "Matala",
  "forecast.methodology": "Miten se tehtiin",
  "forecast.caveats": "Mikä voi muuttaa sitä",
  "forecast.assumptions": "Oletukset",
  "forecast.plan.title": "Kohti positiivista tuottoa",
  "forecast.plan.minBudget": "Positiivinen ROAS vaatii vähintään {amount} kuukaudessa.",
  "forecast.plan.constraints": "Rajoitteet",
  "forecast.plan.actions": "Mitä Scalephant tekee",
  "forecast.roleNeeded": "Vain omistaja tai ylläpitäjä voi pyytää ennusteen.",
  "insights.title": "Näkemykset",
  "insights.intro":
    "Joka viikko autopilotti kirjaa, mitä tapahtui, mitä se tekee seuraavaksi ja mitä kannattaa seurata.",
  "insights.weekly.title": "Viikkoraportit",
  "insights.weekly.none":
    "Ei vielä raportteja. Ensimmäinen kattaa ensimmäisen kokonaisen viikon, jolloin kampanja oli käynnissä.",
  "insights.weekly.week": "Viikko {start} - {end}",
  "insights.weekly.quality.real_metrics": "Alustan luvuista",
  "insights.weekly.quality.tracked_only": "Vain seuratuista konversioista",
  "insights.weekly.quality.no_data": "Ei lukuja tällä viikolla",
  "insights.weekly.highlights": "Kohokohdat",
  "insights.weekly.warnings": "Huomioi",
  "insights.weekly.planned": "Seuraavaksi",
  "insights.weekly.top": "Paras kampanja: {name}",
  "insights.brandVoice.title": "Brändin ääni",
  "insights.brandVoice.intro":
    "Poimittu omista mainoksistasi, voittajat ensin, ja käytetään uusia tekstejä kirjoitettaessa.",
  "insights.brandVoice.none": "Ei vielä analysoitu.",
  "insights.brandVoice.analyze": "Analysoi nyt",
  "insights.brandVoice.analyzed": "Analysoitu {when} {count} kampanjasta.",
  "insights.brandVoice.tone": "Sävy: {tone}, {formality}, {humor}",
  "insights.brandVoice.valueProps": "Arvolupaukset",
  "insights.brandVoice.ctas": "Toimintakehotukset",
  "insights.brandVoice.avoid": "Vältä",
  "insights.brandVoice.examples": "Esimerkkiotsikot",
  "insights.brandVoice.error.no_copy": "Analysoitavia mainostekstejä ei vielä ole.",
  "insights.brandVoice.error.autopilot_off": "Autopilotti on pois päältä.",
  "insights.brandVoice.error.no_credits":
    "Manuaalisen käytön raja täyttyi. Kokeile kauden vaihduttua tai ota yhteyttä tukeen.",
  "insights.brandVoice.roleNeeded": "Vain omistaja tai ylläpitäjä voi ajaa analyysin.",
  "admin.tab.organizations": "Organisaatiot",
  "admin.tab.learning": "Oppimisaivot",
  "admin.tab.aiUsage": "Tekoälyn käyttö",
  "admin.tab.cost": "Kustannusvalvonta",
  "admin.tab.models": "Mallit",
  "admin.models.intro":
    "Kunkin tason tekstimalli, ehdokkaiden arvioinnit sitä vastaan ja jokaisen paketin arvioitu kate. Ehdokas otetaan käyttöön vain täältä, ja vain jos minkään paketin kate ei putoa alle rajan.",
  "admin.models.tiers": "Tasot",
  "admin.models.tier.light": "Kevyt",
  "admin.models.tier.content": "Sisältö",
  "admin.models.tier.core": "Keskusaivot (optimointi)",
  "admin.models.source.builtin": "Oletus",
  "admin.models.source.assigned": "Hyväksytty",
  "admin.models.previous": "edellinen: {model}",
  "admin.models.restore": "Palauta edellinen",
  "admin.models.restored":
    "Taso palasi edelliseen malliin. Kaikki palvelut ottavat sen käyttöön minuutin sisällä.",
  "admin.models.margins": "Kate paketeittain",
  "admin.models.margins.note":
    "Arvio täydellä käytöllä listahinnoin sekä kiinteillä infra- ja korttimaksuilla; ei kirjanpito. Raja {floor}.",
  "admin.models.belowFloor": "alle rajan",
  "admin.models.evaluations": "Arvioinnit",
  "admin.models.evaluations.note":
    "Jokainen ajo vertaa ehdokasta tason nykyiseen malliin kiinteällä koeaineistolla; yksi ajo maksaa enintään {cap}.",
  "admin.models.evaluations.none": "Ei vielä arviointeja.",
  "admin.models.automatic":
    "Ehdokkaat ehdotetaan automaattisesti. Joka maanantai järjestelmä lukee OpenAI:n mallilistan ja lisää arvioinnin jonoon, kun tasolle sopii halvempi tai uudempi malli. Kun malli läpäisee arvioinnin, saat sähköpostin ja täällä vain hyväksyt tai hylkäät sen.",
  "admin.models.against": "vastaan {model} · {input} / {output} miljoonalta tokenilta ({source})",
  "admin.models.summary.quality":
    "Laatu: tuomari arvioi sen yhtä hyväksi tai paremmaksi kuin nykyisen mallin {good} esimerkissä {judged}:stä ({need} vaaditaan).",
  "admin.models.summary.noAnswers":
    "Laatu: se ei antanut yhtään käyttökelpoista vastausta, joten vertailtavaa ei ollut.",
  "admin.models.summary.rejections":
    "Käyttökelvottomat vastaukset: {candidate} (nykyinen malli {baseline}).",
  "admin.models.summary.cheaper": "Kulu: {share} halvempi tehtävää kohden.",
  "admin.models.summary.dearer": "Kulu: {share} kalliimpi tehtävää kohden.",
  "admin.models.runCost": "Ajon kulu {cost}",
  "admin.models.marginWith": "Kate tällä mallilla: {margins}",
  "admin.models.samples": "Esimerkit rinnakkain",
  "admin.models.approve": "Ota käyttöön",
  "admin.models.approved": "Otettu käyttöön. Kaikki palvelut vaihtavat minuutin sisällä.",
  "admin.models.reject": "Hylkää",
  "admin.models.dismiss": "Poista listalta",
  "admin.models.rejected": "Hylätty.",
  "admin.models.blocked": "Estetty: paketin {plans} kate putoaisi alle 50 %:n.",
  "admin.models.status.queued": "Jonossa",
  "admin.models.status.running": "Käynnissä",
  "admin.models.status.passed": "Läpäisi",
  "admin.models.status.failed": "Ei läpäissyt",
  "admin.models.status.error": "Virhe",
  "admin.models.status.approved": "Käytössä",
  "admin.models.status.rejected": "Hylätty",
  "admin.models.status.superseded": "Ohitettu",
  "admin.models.column.tier": "Taso",
  "admin.models.column.model": "Malli",
  "admin.models.column.price": "Syöte / tuotos miljoonalta tokenilta",
  "admin.models.column.since": "Käytössä alkaen",
  "admin.models.column.plan": "Paketti",
  "admin.models.column.priceMonth": "Hinta kuukaudessa",
  "admin.models.column.cost": "Arvioitu kulu",
  "admin.models.column.margin": "Kate",
  "admin.models.column.candidate": "Ehdokas",
  "admin.models.column.status": "Tila",
  "admin.models.column.result": "Tulos",
  "admin.models.candidate.title": "Lisää malli käsin",
  "admin.models.candidate.intro":
    "Tarvitaan vain mallille, jota viikoittainen tarkistus ei ole ehdottanut. Hinnat OpenAI:n hinnoittelusivulta. Ajo alkaa kymmenen minuutin sisällä.",
  "admin.models.candidate.model": "Mallin tunnus",
  "admin.models.candidate.input":
    "Malliin lähetetyn tekstin hinta (input), dollaria miljoonalta tokenilta",
  "admin.models.candidate.output":
    "Mallin kirjoittaman tekstin hinta (output), dollaria miljoonalta tokenilta",
  "admin.models.candidate.context": "Kontekstin koko, tokenia",
  "admin.models.candidate.maxOutput": "Tuotoksen enimmäispituus, tokenia",
  "admin.models.candidate.priceSource": "Hinnan lähde",
  "admin.models.candidate.effort": "Tukee päättelyn tasoa (effort)",
  "admin.models.candidate.submit": "Lisää arviointijonoon",
  "admin.models.candidate.queued": "Jonossa. Tulos näkyy täällä, kun ajo on valmis.",
  "admin.models.candidate.open": "Tätä mallia arvioidaan jo tälle tasolle.",
  "admin.tab.watchdog": "Vahtikoira",
  "admin.tab.launchFailures": "Epäonnistuneet julkaisut",
  "admin.tab.queues": "Jonot",
  "admin.stepUpRequired": "Tämä sivu vaatii tuoreen tunnistautumisen.",
  "admin.learning.intro":
    "Mitä autopilotti on oppinut kaikista organisaatioista: vain lukumäärät. Yhdenkään opin teksti ei ylitä asiakasrajaa.",
  "admin.learning.total": "{count} oppia",
  "admin.learning.highConfidence": "{count} korkealla varmuudella",
  "admin.learning.organizations": "{count} organisaatiosta",
  "admin.learning.byType": "Tyypeittäin",
  "admin.learning.byIndustry": "Toimialoittain",
  "admin.learning.byPlatform": "Alustoittain",
  "admin.learning.none": "Ei vielä opittua.",
  "admin.learning.type.creative_winner": "Voittava mainos",
  "admin.learning.type.fatigue_pattern": "Kulumiskaava",
  "admin.learning.type.anomaly_correction": "Poikkeaman korjaus",
  "admin.learning.type.budget_optimization": "Budjetin optimointi",
  "admin.aiUsage.intro":
    "Kirjatut krediitit organisaatioittain ja ominaisuuksittain. Mallikulut dollareissa ovat /metrics-mittari, eikä niitä tallenneta tänne.",
  "admin.window": "Aikaikkuna",
  "admin.window.days": "Viimeiset {days} päivää",
  "admin.aiUsage.none": "Ei kulutusta tässä ajassa.",
  "admin.aiUsage.column.organization": "Organisaatio",
  "admin.aiUsage.column.plan": "Paketti",
  "admin.aiUsage.column.credits": "Krediitit",
  "admin.aiUsage.column.calls": "Kutsut",
  "admin.aiUsage.column.features": "Ominaisuuksittain",
  "admin.aiUsage.feature": "{feature}: {credits} krediittiä {calls} kutsussa",
  "admin.cost.intro":
    "Kulutus suhteessa paketin krediittimäärään ja listahintaan. Tunnistettu liikevaihto laskee aktiiviset tilaukset listahintaan.",
  "admin.cost.recognised": "Tunnistettu kuukausiliikevaihto: {amount}",
  "admin.cost.consumed": "Kulutetut krediitit: {count}",
  "admin.cost.column.organization": "Organisaatio",
  "admin.cost.column.plan": "Paketti",
  "admin.cost.column.price": "Listahinta",
  "admin.cost.column.allowance": "Krediittimäärä",
  "admin.cost.column.consumed": "Kulutettu",
  "admin.cost.column.utilisation": "Käyttöaste",
  "admin.cost.none": "Ei organisaatioita.",
  "admin.watchdog.intro":
    "Julkinen mallikatalogi verrattuna käytössä oleviin malleihin, ja synteettinen koetus jokaiselle mallille viiden minuutin välein. Löydökset ovat löydöksiä; mikään ei vaihda mallia itsestään.",
  "admin.watchdog.findings": "Löydökset",
  "admin.watchdog.findings.none": "Ei löydöksiä.",
  "admin.watchdog.finding.deprecated_in_use": "Käytössä oleva malli on vanhentunut",
  "admin.watchdog.finding.newer_available": "Uudempi malli on saatavilla",
  "admin.watchdog.finding.new_model": "Uusi malli ilmestyi",
  "admin.watchdog.finding.api_version_sunset": "Alustan API-versio on poistumassa",
  "admin.watchdog.status.active": "Aktiivinen",
  "admin.watchdog.status.resolved": "Ratkaistu",
  "admin.watchdog.column.finding": "Löydös",
  "admin.watchdog.column.model": "Malli",
  "admin.watchdog.column.seen": "Nähty ensi kerran / viimeksi",
  "admin.watchdog.column.alerted": "Hälytetty",
  "admin.watchdog.inUse": "käytössä: {model}",
  "admin.watchdog.health": "Mallien terveys",
  "admin.watchdog.health.none": "Yhtään mallia ei koeteta.",
  "admin.watchdog.state.healthy": "Terve",
  "admin.watchdog.state.degraded": "Heikentynyt",
  "admin.watchdog.state.unavailable": "Ei saatavilla",
  "admin.watchdog.column.capability": "Kyvykkyys",
  "admin.watchdog.column.state": "Tila",
  "admin.watchdog.column.failures": "Peräkkäiset virheet",
  "admin.watchdog.column.lastProbe": "Viimeisin koetus / onnistuminen",
  "admin.watchdog.run": "Aja vahtikoira nyt",
  "admin.watchdog.probe": "Koeta kaikki mallit nyt",
  "admin.watchdog.queued": "Jonossa: {queue}. Tulokset ilmestyvät tähän, kun työstin on valmis.",
  "admin.launchFailures.intro":
    "Kampanjat, joiden julkaisu epäonnistui tai jumittui, kaikista organisaatioista, uusin ensin.",
  "admin.launchFailures.none": "Ei epäonnistuneita julkaisuja.",
  "admin.launchFailures.column.campaign": "Kampanja",
  "admin.launchFailures.column.organization": "Organisaatio",
  "admin.launchFailures.column.status": "Tila",
  "admin.launchFailures.column.attempts": "Yritykset",
  "admin.launchFailures.column.error": "Virhe",
  "admin.launchFailures.column.when": "Viimeisin yritys",
  "admin.launchFailures.binding.pending": "sidonta kesken",
  "admin.launchFailures.binding.live": "sidonta voimassa",
  "admin.launchFailures.binding.failed": "sidonta epäonnistui",
  "admin.launchFailures.binding.none": "ei sidontaa",
  "admin.queues.intro":
    "Jokainen jono käytäntöineen, aikatauluineen ja syvyyksineen työvarastosta. Tämä korvaa suunnitelman nimeämän hallintapaneelin, joka ei olisi mahtunut tämän portin taakse.",
  "admin.queues.none": "Ei jonoja.",
  "admin.queues.noStore":
    "Rajapinnalla ei ole työvarastoa kysyttävänä; lukumäärät eivät ole saatavilla.",
  "admin.queues.column.queue": "Jono",
  "admin.queues.column.schedule": "Aikataulu",
  "admin.queues.column.ready": "Valmiina",
  "admin.queues.column.active": "Työn alla",
  "admin.queues.column.failed": "Epäonnistuneet",
  "admin.queues.column.deadLetter": "Hylkäykset",
  "admin.queues.column.oldest": "Vanhin odottava",
  "admin.queues.column.lastFailure": "Viimeisin virhe",
  "admin.queues.onDemand": "tarvittaessa",
  "admin.queues.retries": "{count} uudelleenyritystä",
  // SP-022 — mistä mainokset tulevat (docs/creative-source/implementation_plan.md).
  "source.title": "Mistä mainoksesi tulevat?",
  "source.intro":
    "Valitse erikseen tekstit ja kuvat. Valinta ratkaisee, mitä Scalephant tekee, mitä tuot itse ja mitä autopilotti tekee myöhemmin, kun mainokset väsyvät.",
  "source.copy": "Tekstit",
  "source.images": "Kuvat",
  "source.option.ai": "Scalephant tekee ne",
  "source.option.upload": "Tuon omani",
  "source.option.mixed": "Molemmat",
  "source.option.ai.copy":
    "Scalephant kirjoittaa otsikot, tekstit ja toimintakehotteet tuotetietojesi pohjalta ja uudet, kun vanhat väsyvät. Tarkistat ne ennen kuin mitään julkaistaan.",
  "source.option.ai.images":
    "Scalephant tekee mainoskuvat tuotetietojesi pohjalta ja uudet, kun vanhat väsyvät. Tarkistat ne ennen kuin mitään julkaistaan.",
  "source.option.upload.copy":
    "Kirjoitat omat valmiit mainoksesi: otsikon, tekstin ja toimintakehotteen. Scalephant ei kirjoita mitään itse, vaan kertoo sinulle, kun mainoksesi väsyvät.",
  "source.option.upload.images":
    "Lataat omat kuvasi ja logosi. Scalephant ei tee kuvia itse, vaan kertoo sinulle, kun kuvasi väsyvät.",
  "source.option.mixed.copy":
    "Lisäät omia mainoksiasi, ja Scalephant kirjoittaa loput käyttäen omiasi sävysi mallina.",
  "source.option.mixed.images": "Lataat omat kuvasi ja logosi, ja Scalephant tekee loput.",
  "source.locked": "Valintaa ei voi muuttaa myöhemmin.",
  "source.required": "Valitse, mistä sekä tekstit että kuvat tulevat.",
  "source.value.ai": "Scalephant",
  "source.value.upload": "Omat",
  "source.value.mixed": "Omat ja Scalephant",
  "source.summary": "Tekstit: {copy} · Kuvat: {images}",
  "source.chosenAtSetup": "Valittu tuotetta perustettaessa; valintaa ei voi muuttaa.",
  "onboarding.step.content": "Mainosten sisältö",
  "onboarding.content.title": "Mitä mainoksiisi tulee?",
  "onboarding.content.intro":
    "Päätä, tekeekö Scalephant mainostesi tekstit ja kuvat, tuotko omasi vai molemmat.",
  "onboarding.content.saveChoice": "Tallenna valinta",
  "onboarding.content.continue": "Jatka",
  "onboarding.content.logoRequired":
    "Lataa logosi ennen kuin jatkat: omia kuvia käyttävät mainokset tarvitsevat sen.",
  "onboarding.content.ownAdsHint":
    "Voit lisätä omat mainoksesi nyt tai myöhemmin kunkin kampanjan mainossivulla.",
  "onboarding.content.ownImagesHint":
    "Voit ladata omat kuvasi nyt tai myöhemmin kunkin kampanjan mainossivulla.",
  "logo.title": "Logo",
  "logo.hint":
    "Neliön muotoinen kuva, vähintään 128 × 128 pikseliä. Google näyttää sen Performance Max -mainostesi yhteydessä, ja sen värit ohjaavat luotuja kuvia.",
  "logo.found.text": "Löysimme tämän logon verkkosivuiltasi. Käytetäänkö sitä mainoksissa?",
  "logo.found.used": "Logo tallennettu. Sen värit ohjaavat nyt luotuja kuvia.",
  "logo.choose": "Valitse logotiedosto",
  "logo.use": "Käytä tätä logoa",
  "logo.change": "Vaihda logo",
  "logo.current": "Logosi",
  "logo.preview": "Logon esikatselu",
  "logo.uploading": "Ladataan…",
  "upload.choose": "Valitse kuvat",
  "upload.hint":
    "JPEG, PNG, WebP tai GIF, enintään 25 Mt ja lyhyemmältä sivultaan vähintään 300 pikseliä.",
  "upload.uploading": "Ladataan {name}…",
  "upload.done": "{name} lisätty.",
  "upload.error.type": "{name}: vain JPEG-, PNG-, WebP- ja GIF-kuvia voi lisätä.",
  "upload.error.size": "{name}: tiedosto on yli 25 Mt.",
  "upload.error.file_signature_mismatch":
    "{name}: tiedosto ei ole sitä kuvaa, joksi sen nimi sen kertoo.",
  "upload.error.unreadable_image": "{name}: kuvaa ei voitu lukea.",
  "upload.error.image_too_small": "{name}: kuva on liian pieni mainokseen.",
  "upload.error.logo_not_square": "{name}: logon täytyy olla neliön muotoinen.",
  "upload.error.own_creative_limit": "{name}: tänne ei mahdu enempää omia kuvia.",
  "upload.error.not_an_image": "{name}: tänne voi lisätä vain kuvia.",
  "upload.error.generic": "{name}: lataus epäonnistui. Yritä uudelleen.",
  "upload.error.unavailable":
    "Tiedostojen lataus ei ole juuri nyt käytettävissä tällä palvelimella.",
  "ownAds.title": "Omat mainoksesi",
  "ownAds.empty": "Ei vielä omia mainoksia.",
  "ownAds.add": "Lisää mainos",
  "ownAds.headline": "Otsikko",
  "ownAds.body": "Teksti",
  "ownAds.cta": "Toimintakehote",
  "ownAds.counter": "{count}/{max}",
  "ownAds.counter.both": "{count} merkkiä · Google sallii {google}, Meta {meta}",
  "ownAds.tooLongFor":
    "Liian pitkä alustalle {platform}; lyhennä ennen kuin sitä voi käyttää siellä.",
  "ownAds.remove": "Poista",
  "ownAds.added": "Mainos lisätty.",
  "library.title": "Omat kuvasi",
  "library.empty": "Ei vielä omia kuvia.",
  "library.remove": "Poista",
  "library.crop": "Säädä rajausta",
  "library.cropped": "Rajattu",
  "library.preview": "Oma kuvasi",
  "crop.title": "Rajaa kuva",
  "crop.intro":
    "Valitse, mitä kukin mainosmuoto näyttää. Vedä kehystä tai käytä painikkeita ja kokoliukusäädintä.",
  "crop.ratio.1.91:1": "Vaaka 1,91:1",
  "crop.ratio.1:1": "Neliö 1:1",
  "crop.ratio.4:5": "Pysty 4:5",
  "crop.size": "Kehyksen koko",
  "crop.left": "Siirrä vasemmalle",
  "crop.right": "Siirrä oikealle",
  "crop.up": "Siirrä ylös",
  "crop.down": "Siirrä alas",
  "crop.auto": "Käytä automaattista rajausta",
  "crop.save": "Tallenna rajaus",
  "crop.saved": "Rajaus tallennettu. Tarkista kuva uudelleen ennen julkaisua.",
  "crop.loading": "Ladataan kuvaa…",
  "imageConsent.title": "Omat kuvasi ja AI",
  "imageConsent.disclosure":
    "Scalephant voi käyttää omia kuviasi mallina, kun se tekee uusia mainoskuvia. Tarkistat uudet kuvat ennen kuin mitään julkaistaan.",
  "imageConsent.authorize": "Salli omien kuvieni lähettäminen kuvamallille",
  "imageConsent.ownerOnly": "Vain omistaja tai ylläpitäjä voi muuttaa tätä.",
  "imageConsent.saved": "Tallennettu.",
  "creatives.origin.customer": "Oma",
  "creatives.origin.fromOwn": "AI, omien pohjalta",
  "creatives.own.add": "Lisää oma mainos",
  "creatives.own.cancel": "Peruuta",
  "creatives.own.added": "Lisätty. Oma mainoksesi on merkitty tarkistetuksi.",
  "creatives.own.queued": "Lisätty. Se viedään alustalle hetken kuluttua.",
  "creatives.images.upload": "Lataa omia kuvia",
  "creatives.images.crop": "Säädä rajausta",
  "creatives.google.needs":
    "Google tarvitsee vähintään 3 eri otsikkoa ja 2 eri tekstiä. Vielä puuttuu: {headlines} otsikkoa ja {bodies} tekstiä.",
  "creatives.google.ok": "Otsikoita ja tekstejä on Googlelle riittävästi.",
  "creatives.fill.copy": "Täydennä tekstit AI:lla",
  "creatives.fill.images": "Täydennä kuvat AI:lla",
  "creatives.fill.title": "Täydennä AI:lla",
  "creatives.fill.intro.copy":
    "Scalephant kirjoittaa puuttuvat tekstit käyttäen omia mainoksiasi sävysi mallina. Tarkistat ne ennen kuin mitään julkaistaan. Tämä käyttää yhden tekstierän.",
  "creatives.fill.intro.images":
    "Scalephant tekee puuttuvat kuvat. Luvallasi se käyttää omia kuviasi mallina. Tarkistat ne ennen kuin mitään julkaistaan. Kuvat lasketaan pakettisi kuvamäärään.",
  "creatives.fill.question": "Haluatko, että Scalephant täydentää jatkossakin automaattisesti?",
  "creatives.fill.yes": "Kyllä, täydennä jatkossakin automaattisesti",
  "creatives.fill.yesHint":
    "Autopilotti tekee jatkossa uusia mainoksia itse, kun vanhat väsyvät. Omista mainoksistasi ilmoitamme edelleen.",
  "creatives.fill.no": "Ei, vain tämän kerran",
  "creatives.fill.noHint": "Kun mainokset väsyvät, saat ilmoituksen ja päätät itse.",
  "creatives.fill.confirm": "Täydennä nyt",
  "creatives.fill.cancel": "Peruuta",
  "creatives.fill.chooseFirst": "Valitse ensin jompikumpi vaihtoehto.",
  "creatives.fill.done.copy": "{count} uutta tekstiä odottaa tarkistustasi.",
  "creatives.fill.done.images": "{count} uutta kuvaa on tekeillä ja ne ilmestyvät tänne pian.",
  "creatives.fill.none": "Mitään ei puuttunut, joten uutta ei tehty.",
  "creatives.fill.changed": "Scalephant täydentää nyt jatkossakin automaattisesti.",
  "creatives.draft.running":
    "Scalephant kirjoittaa ensimmäisiä mainoksiasi. Ne ilmestyvät tänne hetken kuluttua.",
  "creatives.draft.failed":
    "Scalephant ei saanut kirjoitettua ensimmäisiä mainoksia. Voit tehdä ne alla olevilla painikkeilla.",
  "notifications.title": "Vaatii huomiotasi",
  "notifications.fatigue.copy": "Omat tekstisi kampanjassa {campaign} alkavat väsyä.",
  "notifications.fatigue.images": "Omat kuvasi kampanjassa {campaign} alkavat väsyä.",
  "notifications.fatigue.both": "Omat kuvasi ja tekstisi kampanjassa {campaign} alkavat väsyä.",
  "notifications.fatigue.ask":
    "Lisää uusia kuvia, tekstejä tai toimintakehotteita, tai anna Scalephantin kirjoittaa lisää alkuperäisten materiaaliesi pohjalta. Mitään uutta ei julkaista ennen kuin olet tarkistanut sen.",
  "notifications.open": "Avaa mainokset",
  "notifications.generate": "Kirjoita lisää alkuperäisteni pohjalta",
  "notifications.dismiss": "Sulje",
  "products.section.source": "Mistä mainokset tulevat",
};

export const MESSAGES = { en, fi } as const;
